/* eslint-disable react/prop-types */
"use client";
import { usesHostPaymentRequest } from "./HostMoreActionsPanel";
import React, { useEffect, useRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../api/axios";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import useEventAttendanceWindow, {
  EVENT_ATTENDANCE_OPENS_BEFORE_MS,
} from "../../hooks/useEventAttendanceWindow";
import { createPortal } from "react-dom";
import { useBodyLock } from "../../hooks/useDisableBodyScroll";
import useInstagramIAB from "../../hooks/useInstagramIAB";
import ConfirmAttendanceModal from "./ConfirmAttendanceModal";
import ConfirmModal from "./ConfirmModal";
import ConfirmIntentModal from "./ConfirmIntentModal";
import FirstJoinRulesCarouselModal from "./FirstJoinRulesCarouselModal";
import { keepLastResolvedEvent } from "./eventDetailsState";
import PhotoRequestModal from "./PhotoRequestModal";
import EventGenderConfirmationModal from "./EventGenderConfirmationModal";
import useEventAudienceConfirmation from "./useEventAudienceConfirmation";
import WaitlistModal from "./WaitlistModal";
import HostRoleRequestModal from "./HostRoleRequestModal";
import { getEventHostTransferPolicyPreview } from "./hostTransferPolicy";
import HostIntentionRequestModal from "./HostIntentionRequestModal";
import ParticipantRequestsReviewModal from "./ParticipantRequestsReviewModal";
import ConceptDatePollModal from "./ConceptDatePollModal";
import RemoveParticipantModal from "./RemoveParticipantModal";
import BoostEventModal from "./BoostEventModal";
import EventDetailsDesktopContent from "./EventDetailsDesktopContent";
import EventDetailsMobileContent from "./EventDetailsMobileContent";
import EventDetailsLoadingSkeleton from "./EventDetailsLoadingSkeleton";
import HostAnalyticsModal from "../analytics/HostAnalyticsModal.jsx";
import ReportForm from "../search/ReportForm";
import { useProfileCardModal } from "../../context/ProfileCardContext";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import { useWaitlistUnread } from "../../context/WaitlistUnreadContext";
import { useParticipantRequestUnread } from "../../context/ParticipantRequestUnreadContext";
import { RequiredActionsContext } from "../../context/RequiredActionsContext";
import { buildEventPath } from "../../utils/eventUrl";
import { buildEventShareUrl } from "../../utils/eventShareUrl";
import { canAnswerSharedEventIntent } from "../../utils/eventIntentDeepLink";
import { buildClubPath } from "../../utils/clubUrl";
import { AUTH_REQUIRED_REASON } from "../../utils/authRedirect";
import { buildLeaveConfirmMessage } from "../../utils/eventLeaveFlow";
import { getSearchKeyIcon } from "../../constants/searchKeys";
import { buildEventMapState } from "../../utils/eventMapFocus";
import { trackNewcomerAction } from "../../utils/registrationOnboardingAnalytics";
import {
  trackGAEvent,
  trackEventDetailsView,
  trackEventJoinClick,
  trackEventJoinSuccess,
} from "../../utils/gaEventTracking";
import {
  isNativeApp,
  isNativeIOS,
  openExternalUrl,
  openUrlPreferInstalledApp,
  shareContent,
} from "../../native/runtime";
import { presentNativeAppleCalendarEvent } from "../../native/calendar";
import { copyTextToClipboard } from "../../utils/clipboard";

// control desktop popup width
const DETAILS_MODAL_WIDTH_FACTOR = 0.5;  // fraction of available container width
const DETAILS_MODAL_MIN_WIDTH     = 700;  // minimum width in pixels
const ATTENDANCE_SESSION_AUTO_OPEN_DELAY_MS = 1350;
const WAITLIST_CAPACITY_BLOCK_CODES = new Set([
  "gender_segment_full",
  "seat_hold_no_capacity",
]);

// ──────────────────────────────────────────────────────────
// New Joiners reserve helpers (front-only logic)
// ──────────────────────────────────────────────────────────
const fmtUnlock = (d) =>
  d.toLocaleString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  });

const formatCountdown = (ms) => {
  if (ms == null) return null;
  const total = Math.max(0, Math.floor(ms / 1000)); // seconds
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
};

const getParticipantCount = (eventObj) => {
  const explicitCount = Number(eventObj?.participants_count);
  if (Number.isFinite(explicitCount) && explicitCount >= 0) {
    return explicitCount;
  }
  return Array.isArray(eventObj?.participants) ? eventObj.participants.length : 0;
};

const computeReserveUI = (ev) => {
  const total = ev?.max_participants || 0;

  // Clamp new_joiners_places to total capacity
  const rawReserved = typeof ev?.new_joiners_places === "number" ? ev.new_joiners_places : 0;
  const reserved = Math.max(0, Math.min(rawReserved, total));

  const taken = getParticipantCount(ev);
  const unlockMs = (ev?.unlock_time_hours || 0) * 3600 * 1000;

  const dt = ev?.date_time ? new Date(ev.date_time) : null;
  const unlockAt = dt ? new Date(dt.getTime() - unlockMs) : null;
  const lockActive = reserved > 0 && unlockAt && Date.now() < unlockAt.getTime();

  const generalCap = Math.max(0, total - reserved);

  // How many reserved seats are still locked for newbies
  const reservedLeft = lockActive
    ? (typeof ev?.new_joiners_left === 'number'
        ? Math.max(0, Math.min(reserved, ev.new_joiners_left))
        : Math.max(0, reserved - Math.max(0, taken - generalCap)))
    : 0;

  // Newbies currently occupying reserved seats (they can use reserved even when general exists)
  const newbiesInReserved = lockActive
    ? Math.max(0, Math.min(reserved, reserved - reservedLeft))
    : 0;

  const generalUsed = Math.max(0, taken - newbiesInReserved);
  const availableGeneral = Math.max(0, generalCap - generalUsed);

  // What we display as grey "NEW" placeholders
  const capacityLeft = Math.max(0, total - taken);
  const lockedRemaining = lockActive
    ? Math.max(0, Math.min(reservedLeft, capacityLeft))
    : 0;

  const userOldOrBlocked = (!ev?.is_newbie) || !!ev?.early_access_blocked;

  let canJoinNow = true;
  if (total > 0) {
    if (!lockActive) {
      canJoinNow = taken < total;
    } else if (ev?.is_newbie) {
      canJoinNow = taken < total;
    } else {
      canJoinNow = availableGeneral > 0;
    }
  }

  return { lockActive, reserved, lockedRemaining, availableGeneral, unlockAt, userOldOrBlocked, canJoinNow };
};

const padCalendarPart = (n) => String(n).padStart(2, "0");

const toUtcCalendarStamp = (d) => {
  const year = d.getUTCFullYear();
  const month = padCalendarPart(d.getUTCMonth() + 1);
  const day = padCalendarPart(d.getUTCDate());
  const hours = padCalendarPart(d.getUTCHours());
  const minutes = padCalendarPart(d.getUTCMinutes());
  const seconds = padCalendarPart(d.getUTCSeconds());
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

const toZonelessCalendarStamp = (d, timeZone) => {
  if (!timeZone || typeof Intl === "undefined" || typeof Intl.DateTimeFormat !== "function") {
    return null;
  }

  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    const parts = formatter.formatToParts(d);
    const partMap = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    if (
      !partMap.year ||
      !partMap.month ||
      !partMap.day ||
      !partMap.hour ||
      !partMap.minute ||
      !partMap.second
    ) {
      return null;
    }
    return `${partMap.year}${partMap.month}${partMap.day}T${partMap.hour}${partMap.minute}${partMap.second}`;
  } catch (_error) {
    return null;
  }
};

const escapeIcsText = (value = "") =>
  String(value)
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const sanitizeIcsFileName = (value = "event") => {
  const trimmed = String(value).trim();
  const safe = Array.from(trimmed)
    .map((ch) => {
      const code = ch.charCodeAt(0);
      if (code < 32 || '<>:"/\\|?*'.includes(ch)) return "_";
      return ch;
    })
    .join("");
  return safe || "event";
};

const getCalendarEventData = (ev, t) => {
  if (!ev?.date_time) return null;

  const start = new Date(ev.date_time);
  // Use approximate duration if provided, otherwise default to 120 minutes
  const durationMin = ev.approximate_duration || 120;
  const end = new Date(start.getTime() + durationMin * 60 * 1000);

  const title = ev.name || t("events.details.calendar_default_title");
  const details = ev.description || "";
  const location =
    ev.event_format === 0
      ? (ev.event_link || t("events.details.online_label"))
      : (() => {
          const streetNum = [ev.street, ev.building_number].filter(Boolean).join(" ");
          return [streetNum, ev.city, ev.country].filter(Boolean).join(", ");
        })();

  return {
    start,
    end,
    title,
    details,
    location,
  };
};

const buildGoogleCalendarUrl = (ev, t) => {
  const eventData = getCalendarEventData(ev, t);
  if (!eventData) return null;

  const eventTimeZone = typeof ev?.tz_name === "string" ? ev.tz_name.trim() : "";
  const zonedStartStr = toZonelessCalendarStamp(eventData.start, eventTimeZone);
  const zonedEndStr = toZonelessCalendarStamp(eventData.end, eventTimeZone);
  const startStr = zonedStartStr || toUtcCalendarStamp(eventData.start);
  const endStr = zonedEndStr || toUtcCalendarStamp(eventData.end);

  const base = "https://calendar.google.com/calendar/render?action=TEMPLATE";
  const params = [
    `text=${encodeURIComponent(eventData.title)}`,
    `dates=${startStr}/${endStr}`,
    `details=${encodeURIComponent(eventData.details)}`,
    `location=${encodeURIComponent(eventData.location)}`,
    ...(eventTimeZone ? [`ctz=${encodeURIComponent(eventTimeZone)}`] : []),
    "sf=true",
    "output=xml",
  ].join("&");

  return `${base}&${params}`;
};

const buildAppleCalendarIcs = (ev, t) => {
  const eventData = getCalendarEventData(ev, t);
  if (!eventData) return null;

  const uid = `vaultgrid-${ev?.id || "event"}-${eventData.start.getTime()}@vaultgrid.net`;
  const eventTimeZone = typeof ev?.tz_name === "string" ? ev.tz_name.trim() : "";
  const zonedStartStr = toZonelessCalendarStamp(eventData.start, eventTimeZone);
  const zonedEndStr = toZonelessCalendarStamp(eventData.end, eventTimeZone);
  const dtStamp = toUtcCalendarStamp(new Date());
  const dtStart = zonedStartStr || toUtcCalendarStamp(eventData.start);
  const dtEnd = zonedEndStr || toUtcCalendarStamp(eventData.end);
  const dtStartLine =
    zonedStartStr && eventTimeZone
      ? `DTSTART;TZID=${eventTimeZone}:${dtStart}`
      : `DTSTART:${dtStart}`;
  const dtEndLine =
    zonedEndStr && eventTimeZone
      ? `DTEND;TZID=${eventTimeZone}:${dtEnd}`
      : `DTEND:${dtEnd}`;

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "PRODID:-//VaultGrid//Events//EN",
    ...(eventTimeZone ? [`X-WR-TIMEZONE:${eventTimeZone}`] : []),
    "BEGIN:VEVENT",
    `UID:${escapeIcsText(uid)}`,
    `DTSTAMP:${dtStamp}`,
    dtStartLine,
    dtEndLine,
    `SUMMARY:${escapeIcsText(eventData.title)}`,
    `DESCRIPTION:${escapeIcsText(eventData.details)}`,
    `LOCATION:${escapeIcsText(eventData.location)}`,
    "END:VEVENT",
    "END:VCALENDAR",
    "",
  ].join("\r\n");
};

const downloadAppleCalendarIcs = (ev, t) => {
  if (typeof document === "undefined") return;
  const icsContent = buildAppleCalendarIcs(ev, t);
  if (!icsContent) return;

  const eventData = getCalendarEventData(ev, t);
  const fileNameBase = sanitizeIcsFileName(eventData?.title || "event");
  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = `${fileNameBase}.ics`;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
};

const normalizeRewardAmount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

const formatRewardAmount = (value) => {
  const normalized = normalizeRewardAmount(value);
  if (normalized >= 1e9) {
    const short = (normalized / 1e9).toFixed(1).replace(/\.0$/, "");
    return `${short}B`;
  }
  if (normalized >= 1e6) {
    const short = (normalized / 1e6).toFixed(1).replace(/\.0$/, "");
    return `${short}M`;
  }
  if (normalized >= 1e3) {
    const short = (normalized / 1e3).toFixed(1).replace(/\.0$/, "");
    return `${short}K`;
  }
  return `${normalized}`;
};

const getHostRewardItems = (reward, t) => {
  if (!reward) return [];
  return [
    {
      key: "coins",
      kind: "coins",
      amount: normalizeRewardAmount(reward.coins),
      amountLabel: formatRewardAmount(reward.coins),
      icon: "/assets/vault/coin_icon.svg",
      label: t("participants_section.host_reward_items.coins"),
      iconClassName: "h-7 w-7",
    },
    {
      key: "diamonds",
      kind: "resource",
      amount: normalizeRewardAmount(reward.diamonds),
      amountLabel: formatRewardAmount(reward.diamonds),
      icon: "/assets/vault/diamand_icon.svg",
      label: t("participants_section.host_reward_items.diamonds"),
      iconClassName: "h-7 w-7",
    },
    {
      key: "key1",
      kind: "resource",
      amount: normalizeRewardAmount(reward.keys_level_1),
      amountLabel: formatRewardAmount(reward.keys_level_1),
      icon: getSearchKeyIcon(1),
      label: t("participants_section.host_reward_items.key_level_1"),
      iconClassName: "h-12 w-12 rotate-[45deg]",
    },
    {
      key: "key2",
      kind: "resource",
      amount: normalizeRewardAmount(reward.keys_level_2),
      amountLabel: formatRewardAmount(reward.keys_level_2),
      icon: getSearchKeyIcon(2),
      label: t("participants_section.host_reward_items.key_level_2"),
      iconClassName: "h-12 w-12 rotate-[45deg]",
    },
    {
      key: "key3",
      kind: "resource",
      amount: normalizeRewardAmount(reward.keys_level_3),
      amountLabel: formatRewardAmount(reward.keys_level_3),
      icon: getSearchKeyIcon(3),
      label: t("participants_section.host_reward_items.key_level_3"),
      iconClassName: "h-12 w-12 rotate-[45deg]",
    },
    {
      key: "key4",
      kind: "resource",
      amount: normalizeRewardAmount(reward.keys_level_4),
      amountLabel: formatRewardAmount(reward.keys_level_4),
      icon: getSearchKeyIcon(4),
      label: t("participants_section.host_reward_items.key_level_4"),
      iconClassName: "h-12 w-12 rotate-[45deg]",
    },
    {
      key: "key5",
      kind: "resource",
      amount: normalizeRewardAmount(reward.keys_level_5),
      amountLabel: formatRewardAmount(reward.keys_level_5),
      icon: getSearchKeyIcon(5),
      label: t("participants_section.host_reward_items.key_level_5"),
      iconClassName: "h-12 w-12 rotate-[45deg]",
    },
  ].filter((item) => item.amount > 0);
};

const PARTICIPANT_JOIN_LIMIT_COPY = {
  en: {
    blockedTitle: "Participant rating is 0%",
    cooldownTitle: "Event joins are paused",
    slotsTitle: "Parallel event limit reached",
    openRating: "Open rating",
    openPremium: "Get Premium",
    resetRating: "Reset rating",
    resettingRating: "Resetting...",
    blockedLead:
      "Your participant rating is now 0%, so you cannot join new events right now.",
    cooldownLead:
      "Your rating has increased, but new joins are paused after it reached 0%.",
    blockedSupport:
      "Open your rating to review what changed. If you think this is a mistake, please contact support.",
    blockedPremium:
      "VaultGrid Premium gives you more flexibility and a recovery path when your rating reaches zero.",
    blockedPremiumReady:
      "Your Premium monthly rating reset is available now.",
    blockedPremiumUsed:
      "Your Premium reset was already used this month. If this looks wrong, contact support.",
    slotsLead(used, total) {
      return `Right now you are already joined to ${used} out of ${total} events or concepts at the same time.`;
    },
    slotsImprove:
      "Improve your participant rating and you will be able to join more events and concepts in parallel.",
    slotsMaxed:
      "At 100% participant rating, you already have the highest standard limit for parallel events and concepts.",
    slotsPremium:
      "Unlimited events and concepts in parallel are available with VaultGrid Premium.",
    slotLine: "Joined now",
    scoreLine: "Current rating",
  },
  ru: {
    blockedTitle: "Рейтинг участника 0%",
    cooldownTitle: "Запись на ивенты приостановлена",
    slotsTitle: "Достигнут лимит параллельных событий",
    openRating: "Открыть рейтинг",
    openPremium: "Открыть Premium",
    resetRating: "Сбросить рейтинг",
    resettingRating: "Сбрасываем...",
    blockedLead:
      "Ваш рейтинг участника сейчас 0%, поэтому вы пока не можете присоединяться к новым ивентам.",
    cooldownLead:
      "Рейтинг уже вырос, но новые записи приостановлены после того, как он достиг 0%.",
    blockedSupport:
      "Откройте рейтинг, чтобы посмотреть, что на него повлияло. Если вы считаете, что это ошибка, пожалуйста, свяжитесь с поддержкой.",
    blockedPremium:
      "VaultGrid Premium даёт больше гибкости и путь к восстановлению доступа, если рейтинг падает до нуля.",
    blockedPremiumReady:
      "Ваш ежемесячный Premium-сброс рейтинга сейчас доступен.",
    blockedPremiumUsed:
      "В этом месяце Premium-сброс уже был использован. Если это ошибка, свяжитесь с поддержкой.",
    slotsLead(used, total) {
      return `Сейчас вы уже подключены к ${used} из ${total} ивентов или концептов одновременно.`;
    },
    slotsImprove:
      "Повысьте рейтинг участника, и вы сможете подключаться к большему количеству ивентов и концептов одновременно.",
    slotsMaxed:
      "При рейтинге участника 100% у вас уже открыт максимальный стандартный лимит на параллельные ивенты и концепты.",
    slotsPremium:
      "Неограниченное количество параллельных ивентов и концептов доступно в VaultGrid Premium.",
    slotLine: "Подключено сейчас",
    scoreLine: "Текущий рейтинг",
  },
  sk: {
    blockedTitle: "Participant rating je 0 %",
    cooldownTitle: "Pridávanie k udalostiam je pozastavené",
    slotsTitle: "Limit paralelných udalostí je plný",
    openRating: "Otvoriť rating",
    openPremium: "Získať Premium",
    resetRating: "Resetnúť rating",
    resettingRating: "Resetujem...",
    blockedLead:
      "Tvoj participant rating je teraz 0 %, takže sa teraz nemôžeš pridávať k novým udalostiam.",
    cooldownLead:
      "Tvoj rating už stúpol, ale nové pripojenia sú pozastavené po tom, čo dosiahol 0 %.",
    blockedSupport:
      "Otvor svoj rating a pozri sa, čo ho zmenilo. Ak si myslíš, že je to chyba, kontaktuj support.",
    blockedPremium:
      "VaultGrid Premium ti dáva viac flexibility a cestu k obnoveniu prístupu, keď rating klesne na nulu.",
    blockedPremiumReady:
      "Tvoj mesačný Premium reset ratingu je teraz dostupný.",
    blockedPremiumUsed:
      "Premium reset si už tento mesiac použil. Ak je to chyba, kontaktuj support.",
    slotsLead(used, total) {
      return `Momentálne máš pripojených ${used} z ${total} udalostí alebo konceptov naraz.`;
    },
    slotsImprove:
      "Zlepši participant rating a budeš sa môcť pridávať k väčšiemu počtu udalostí a konceptov naraz.",
    slotsMaxed:
      "Pri 100 % participant ratingu už máš odomknutý najvyšší štandardný limit pre paralelné udalosti a koncepty.",
    slotsPremium:
      "Neobmedzený počet udalostí a konceptov naraz je dostupný vo VaultGrid Premium.",
    slotLine: "Pripojené teraz",
    scoreLine: "Aktuálny rating",
  },
};

const EventDetailsFullscreen = (
  {
    event,
    onClose,
    onConfirmClose = () => {},
    onEventUpdate   = () => {},
    isFavourite = false,
    onToggleFavourite = () => {},
    shiftRight = false,
    forceOpenConfirmModal = false,
    forceOpenJoinFlow = false,
    onForceJoinConsumed = () => {},
    openParticipantsOnMount = false,
    isGuest = false,
    onRequireAuth = null,
    analyticsContext = null,
    asPage = false,
    newcomerInterestAction = null,
  }
) => {
  const { t, i18n } = useTranslation('events');
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const isDesktopPageMode = isDesktop && asPage;
  useBodyLock(isDesktop && !asPage);
  const waitlistUnreadCtx = useWaitlistUnread();
  const waitlistUnreadRefetch = waitlistUnreadCtx?.refetch;
  const participantRequestUnreadCtx = useParticipantRequestUnread();
  const participantRequestUnreadRefetch = participantRequestUnreadCtx?.refetch;
  const {
    refresh: refreshRequiredActions,
  } = React.useContext(RequiredActionsContext);

  const { isInstagramIAB, isTelegramEnv, isInApp } = useInstagramIAB();

  const isInAppBrowser = React.useMemo(() => {
    const baseFlag = isInApp || isInstagramIAB || isTelegramEnv;
    if (typeof window === "undefined") return baseFlag;
    const host = window.location.hostname || "";
    const isLocal = host === "localhost" || host === "127.0.0.1";
    if (isLocal) return false; // force "browser" behavior on local dev
    return baseFlag;
  }, [isInApp, isInstagramIAB, isTelegramEnv]);

  const [browserBottomOverlayPx, setBrowserBottomOverlayPx] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    const update = () => {
      try {
        if (isInAppBrowser) {
          setBrowserBottomOverlayPx(0);
          return;
        }

        const vv = window.visualViewport;
        if (!vv) {
          setBrowserBottomOverlayPx(0);
          return;
        }

        const overlay = window.innerHeight - vv.height - (vv.offsetTop || 0);
        const px = clamp(Math.round(Math.max(0, overlay)), 0, 180);
        setBrowserBottomOverlayPx(px);
      } catch {
        setBrowserBottomOverlayPx(0);
      }
    };

    update();

    const vv = window.visualViewport;
    window.addEventListener("resize", update);
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);

    return () => {
      window.removeEventListener("resize", update);
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
    };
  }, [isInAppBrowser]);

  const EXTRA_BOTTOM_GAP_INAPP_PX = 80;
  const EXTRA_BOTTOM_GAP_BROWSER_PX = 64;
  const INAPP_MIN_BOTTOM_SAFE_PX = 220;

  const extraBottomGapPx = isInAppBrowser
    ? EXTRA_BOTTOM_GAP_INAPP_PX
    : EXTRA_BOTTOM_GAP_BROWSER_PX;

  const dynamicBrowserOverlayPx = isInAppBrowser ? 0 : browserBottomOverlayPx;

  const bottomSafeSpaceCss = isInAppBrowser
    ? `max(calc(var(--bottom-nav-clearance, 112px) + ${extraBottomGapPx}px), ${INAPP_MIN_BOTTOM_SAFE_PX}px)`
    : `calc(var(--bottom-nav-clearance, 112px) + ${extraBottomGapPx}px + ${dynamicBrowserOverlayPx}px)`;

  const loadingCode = null;
  const { openProfileCard } = useProfileCardModal();
  const forceJoinConsumedRef = useRef(null);
  const attendanceSessionRedirectTimerRef = useRef(null);
  const attendanceSessionRedirectEventRef = useRef(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [attendanceOpenRequest, setAttendanceOpenRequest] = useState(0);
  const attendanceOpenHandledRef = useRef(0);
  const [attendanceCashQrMode, setAttendanceCashQrMode] = useState(false);
  const [isIntentOpen, setIsIntentOpen] = useState(false);
  const [checkoutCapacityReservedOpen, setCheckoutCapacityReservedOpen] = useState(false);
  const [isTicketSuccessOpen, setIsTicketSuccessOpen] = useState(false);
  const [paymentSuccessKind, setPaymentSuccessKind] = useState("ticket");
  const [isHostConfirmOpen, setIsHostConfirmOpen] = useState(false);
  const [isHostCancelConfirmOpen, setIsHostCancelConfirmOpen] = useState(false);
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [waitlistMode, setWaitlistMode] = useState("participant");
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistLoadError, setWaitlistLoadError] = useState("");
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState(null);
  const loadWaitlistStatusRef = useRef(null);
  const [waitlistCapacityMessage, setWaitlistCapacityMessage] = useState("");
  const [waitlistPromotingUserId, setWaitlistPromotingUserId] = useState(null);
  const [waitlistOpenedFromAvailabilityAlert, setWaitlistOpenedFromAvailabilityAlert] = useState(false);
  const waitlistDeepLinkConsumedRef = useRef(null);
  const [isCoHostModalOpen, setIsCoHostModalOpen] = useState(false);
  const [isHostTransferModalOpen, setIsHostTransferModalOpen] = useState(false);
  const [hostRoleCandidates, setHostRoleCandidates] = useState([]);
  const [isHostIntentionModalOpen, setIsHostIntentionModalOpen] = useState(false);
  const [isReviewRequestsModalOpen, setIsReviewRequestsModalOpen] = useState(false);
  const [isConceptDatePollModalOpen, setIsConceptDatePollModalOpen] = useState(false);
  const [isRemoveParticipantModalOpen, setIsRemoveParticipantModalOpen] = useState(false);
  const [isHostAnalyticsModalOpen, setIsHostAnalyticsModalOpen] = useState(false);
  const [eventToolsOpenRequest, setEventToolsOpenRequest] = useState(0);
  const [eventToolsCloseRequest, setEventToolsCloseRequest] = useState(0);
  const [hostAnalyticsLoading, setHostAnalyticsLoading] = useState(false);
  const [hostAnalyticsError, setHostAnalyticsError] = useState("");
  const [hostAnalyticsSummary, setHostAnalyticsSummary] = useState(null);
  const [hostAnalyticsRows, setHostAnalyticsRows] = useState([]);
  const [conceptPollDeepLinkConsumed, setConceptPollDeepLinkConsumed] = useState(false);
  const [coHostSubmitting, setCoHostSubmitting] = useState(false);
  const [pendingCoHostCancelIds, setPendingCoHostCancelIds] = useState([]);
  const [intentStatus, setIntentStatus] = useState('unknown'); // 'unknown' | 'confirmed'
  const [intentVariant, setIntentVariant] = useState("intent"); // "intent" | "payment" | "activation_date" | "slot"
  const [serverPrepayRequired, setServerPrepayRequired] = useState(false);
  const [forceImmediatePayment, setForceImmediatePayment] = useState(false);
  const [serverPayUrl, setServerPayUrl] = useState(null);
  const [serverTicketPaymentState, setServerTicketPaymentState] = useState(null);
  const [preJoinPaymentFlag, setPreJoinPaymentFlag] = useState(false);
  const [ticketMode, setTicketMode] = useState(false);
  const [ticketCancellationQuote, setTicketCancellationQuote] = useState(null);
  const [ticketCancellationLoading, setTicketCancellationLoading] = useState(false);
  const [ticketCancellationSubmitting, setTicketCancellationSubmitting] = useState(false);
  const [ticketCancellationOutcome, setTicketCancellationOutcome] = useState(null);
  const [ticketDisputeSubmitting, setTicketDisputeSubmitting] = useState(false);
  const [ticketPanelOpenRequest, setTicketPanelOpenRequest] = useState(0);
  const [ticketPanelOpenView, setTicketPanelOpenView] = useState("summary");
  const ticketCancellationLoadSeqRef = useRef(0);
  const ticketCancellationResolvedRef = useRef(false);

  const [primaryAction, setPrimaryAction] = useState(null); // 'intent' | 'slot' | 'activation_date' | 'attendance' | null
  const [primaryActionVersion, setPrimaryActionVersion] = useState(0);

  // Photo modal state
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [hasCustomAvatar, setHasCustomAvatar] = useState(null); // null = unknown, true/false = checked
  const [photoGateTarget, setPhotoGateTarget] = useState(null); // "join" | "intent" | null
  const [genderPrompt, setGenderPrompt] = useState(null);
  const [genderSubmitting, setGenderSubmitting] = useState(false);
  const [genderError, setGenderError] = useState("");
  const [joinRulesIntroRequired, setJoinRulesIntroRequired] = useState(null);
  // Keep the latest server answer available synchronously inside the same
  // click flow. React state is intentionally asynchronous; without this ref a
  // first-time join can fetch the profile twice before opening the rules
  // modal, which is especially fragile in mobile Safari.
  const joinRulesIntroRequiredRef = useRef(null);
  const [isJoinRulesIntroOpen, setIsJoinRulesIntroOpen] = useState(false);
  const [joinRulesInitialIndex, setJoinRulesInitialIndex] = useState(0);
  // Card navigation must not re-render the entire event details tree on mobile.
  const [hostCreateRulesIntroRequired, setHostCreateRulesIntroRequired] = useState(null);
  const hostClaimRulesResumeRef = useRef(null);
  const deepLinkIntentOpenerRef = useRef(null);

  useEffect(() => {
    setConceptPollDeepLinkConsumed(false);
  }, [event?.id]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;

      const DEEP_KEYS = [
        "confirm",
        "intent",
        "activation_date",
        "host_confirm",
        "force_payment",
        "pay",
        "manual_pay",
        "manual_payment",
        "confirm_intent",
        "confirm_attend",
        "ticket_success",
        "deposit_success",
        "remaining_success",
      ];

      const search = location?.search || window.location.search || "";
      const pathname = location?.pathname || window.location.pathname || "";
      const sp = new URLSearchParams(search);

      const hadDeepFlagsInUrl = DEEP_KEYS.some((k) => sp.has(k));

      const locKey = location?.key || "nokey";
      const eventIdForToken = event?.id || "noev";
      const token = `${eventIdForToken}|${pathname}|${locKey}|${search}`;

      window.__VG_DEEPLINK_CONSUMED__ ||= {};

      let openConfirm = sp.get("confirm") === "1";
      let openIntent = sp.get("intent") === "1";
      let openActivationDate = sp.get("activation_date") === "1";
      let openHostConfirm = sp.get("host_confirm") === "1";
      let openTicketSuccess = sp.get("ticket_success") === "1";
      let openDepositSuccess = sp.get("deposit_success") === "1";
      let openRemainingSuccess = sp.get("remaining_success") === "1";
      let forcePaymentLink =
        sp.get("force_payment") === "1" || sp.get("pay") === "1";
      let manualPayParam =
        sp.get("manual_pay") === "1" || sp.get("manual_payment") === "1";

      if (hadDeepFlagsInUrl) {
        try {
          window.sessionStorage?.removeItem("vg_event_deeplink");
        } catch {}
      }

      if (
        !openConfirm &&
        !openIntent &&
        !openActivationDate &&
        !openHostConfirm &&
        !openTicketSuccess &&
        !openDepositSuccess &&
        !openRemainingSuccess &&
        !forcePaymentLink &&
        !manualPayParam
      ) {
        try {
          const raw = window.sessionStorage?.getItem("vg_event_deeplink");
          if (raw) {
            const payload = JSON.parse(raw);

            if (payload && String(payload.event) === String(event?.id)) {
              openConfirm = payload.confirm === "1";
              openIntent = payload.intent === "1";
              openActivationDate = payload.activation_date === "1";
              openHostConfirm = payload.host_confirm === "1";
              openTicketSuccess = payload.ticket_success === "1";
              openDepositSuccess = payload.deposit_success === "1";
              openRemainingSuccess = payload.remaining_success === "1";
              forcePaymentLink =
                payload.force_payment === "1" || payload.pay === "1";
              manualPayParam =
                payload.manual_pay === "1" || payload.manual_payment === "1";

              window.sessionStorage.removeItem("vg_event_deeplink");
            }
          }
        } catch (e) {
          console.warn("[EventDetailsFullscreen] deeplink payload error", e);
        }
      }

      const shouldOpenConfirm = !!openConfirm && event?.type !== "concept";
      const shouldOpenIntent = !!(
        openIntent ||
        openActivationDate ||
        forcePaymentLink ||
        manualPayParam
      );
      const shouldOpenTicketSuccess = !!(
        openTicketSuccess ||
        openDepositSuccess ||
        openRemainingSuccess
      );
      const shouldOpenHostConfirm = !!openHostConfirm && !!event?.is_host;
      const shouldOpenAnything =
        shouldOpenConfirm ||
        shouldOpenIntent ||
        shouldOpenTicketSuccess ||
        shouldOpenHostConfirm;

      if (!shouldOpenAnything) return;
      if (window.__VG_DEEPLINK_CONSUMED__[token]) return;
      window.__VG_DEEPLINK_CONSUMED__[token] = "pending";

      let cancelled = false;

      const finishDeepLink = () => {
        if (cancelled) return;
        window.__VG_DEEPLINK_CONSUMED__[token] = true;

        if (!hadDeepFlagsInUrl || typeof navigate !== "function") return;

        try {
          const currentPathname = window.location.pathname || pathname;
          const currentParams = new URLSearchParams(window.location.search || search);
          let changed = false;
          for (const k of DEEP_KEYS) {
            if (currentParams.has(k)) {
              currentParams.delete(k);
              changed = true;
            }
          }
          if (changed) {
            const nextSearch = currentParams.toString();
            navigate(
              `${currentPathname}${nextSearch ? `?${nextSearch}` : ""}`,
              { replace: true }
            );
          }
        } catch (e) {
          console.warn("[EventDetailsFullscreen] deeplink cleanup failed", e);
        }
      };

      if (shouldOpenConfirm) {
        setAttendanceOpenRequest((value) => value + 1);
      }

      if (shouldOpenHostConfirm) {
        setIsHostConfirmOpen(true);
      }

      if (shouldOpenTicketSuccess) {
        setServerPayUrl(null);
        setServerTicketPaymentState(null);
        setServerPrepayRequired(false);
        setForceImmediatePayment(false);
        setPreJoinPaymentFlag(false);
        setTicketMode(false);
        setPaymentSuccessKind(
          openDepositSuccess
            ? "deposit"
            : openRemainingSuccess
              ? "remaining"
              : "ticket"
        );
        setIsTicketSuccessOpen(true);
      }

      if (shouldOpenIntent) {
        (async () => {
          const startMs = event?.date_time ? new Date(event.date_time).getTime() : null;
          const nowMs = Date.now();
          const requiresPrepayGate = Boolean(
            event?.is_payable_in_advance ||
              event?.pay_in_advance ||
              event?.requires_prepayment
          );

          const inT3d = !!(
            requiresPrepayGate &&
            startMs &&
            nowMs >= startMs - 3 * 24 * 60 * 60 * 1000 &&
            nowMs < startMs
          );

          const alreadyPaid = Boolean(event?.ticket_paid);
          const explicitPaymentLink = Boolean(
            !openActivationDate && (forcePaymentLink || manualPayParam)
          );
          const shouldOpenPaymentFlow =
            !openActivationDate &&
            !alreadyPaid &&
            Boolean(explicitPaymentLink || inT3d);

          let currentRequiredAction = null;
          let participantActionData = null;
          let participantActionLoaded = false;
          if (!explicitPaymentLink) {
            try {
              const { data } = await api.get(`/events/${event.id}/participant-action/`, {
                authOptional: true,
                suppressUnauthorizedRedirect: true,
              });
              participantActionData = data;
              currentRequiredAction = data?.primary_action || data?.action || null;
              participantActionLoaded = true;
            } catch {
              // The backend confirmation endpoint remains the final safety net.
            }
          }

          const hasPendingConfirmationAction = [
            "slot",
            "activation_date",
            "intent",
          ].includes(currentRequiredAction);
          const canAnswerSharedIntent = Boolean(
            openIntent &&
            canAnswerSharedEventIntent(event, participantActionData)
          );
          if (
            !explicitPaymentLink
            && participantActionLoaded
            && !hasPendingConfirmationAction
            && !canAnswerSharedIntent
          ) {
            if (openIntent && participantActionData?.intent_status === "confirmed") {
              window.dispatchEvent(new CustomEvent("global-info", {
                detail: { message: t("confirm_intent_modal.toasts.intention_confirmed") },
              }));
            }
            finishDeepLink();
            return;
          }

          let variant = "intent";
          if (shouldOpenPaymentFlow) {
            variant = "payment";
          } else if (currentRequiredAction === "slot") {
            variant = "slot";
          } else if (
            currentRequiredAction === "activation_date"
            || (!participantActionLoaded && openActivationDate)
          ) {
            variant = "activation_date";
          }

          setIntentVariant(variant);
          const isPreJoinPaymentLink = Boolean(
            shouldOpenPaymentFlow && forcePaymentLink && !event?.is_participant
          );
          setPreJoinPaymentFlag(isPreJoinPaymentLink);
          const shouldForcePayment = shouldOpenPaymentFlow;
          setForceImmediatePayment(shouldForcePayment);

          if (openActivationDate) {
            setTicketMode(false);
          } else if (isPreJoinPaymentLink) {
            setTicketMode(false);
          } else if (shouldOpenPaymentFlow && (manualPayParam || forcePaymentLink || !inT3d)) {
            setTicketMode(true);
          } else {
            setTicketMode(false);
          }

          const openIntentModal = deepLinkIntentOpenerRef.current;
          if (typeof openIntentModal !== "function") {
            delete window.__VG_DEEPLINK_CONSUMED__[token];
            return;
          }

          await openIntentModal();
          finishDeepLink();
        })();
      } else {
        finishDeepLink();
      }

      return () => {
        cancelled = true;
        if (window.__VG_DEEPLINK_CONSUMED__[token] === "pending") {
          delete window.__VG_DEEPLINK_CONSUMED__[token];
        }
      };
    } catch (err) {
      console.error("[EventDetailsFullscreen] deep-link error", err);
    }
  }, [
    location?.search,
    location?.pathname,
    location?.key,
    navigate,
    event?.id,
    event?.type,
    event?.is_host,
    event?.intent_confirmation_sent,
    event?.status,
    event?.ticket_paid,
    event?.is_payable_in_advance,
    event?.pay_in_advance,
    event?.requires_prepayment,
    event?.show_pay_button,
    event?.date_time,
    t,
  ]);

  useEffect(() => {
    if (!event?.id || conceptPollDeepLinkConsumed) return;
    const eventIsConcept = event?.type === "concept";
    if (!eventIsConcept || !event?.is_host) return;

    try {
      const search = (location && location.search) || window.location.search || "";
      const sp = new URLSearchParams(search);
      if (sp.get("concept_poll") !== "1") return;
      setConceptPollDeepLinkConsumed(true);
      navigate(`/menu/chats/event/${event.id}?concept_poll=1`, { replace: true });
    } catch {
      // ignore malformed query params
    }
  }, [
    conceptPollDeepLinkConsumed,
    event?.id,
    event?.is_host,
    event?.type,
    location,
    navigate,
  ]);

  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
  const [leavePreviewLoading, setLeavePreviewLoading] = useState(false);
  const [leavePreview, setLeavePreview] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isJoinConfirmOpen, setIsJoinConfirmOpen] = useState(false);
  const [joinLimitModal, setJoinLimitModal] = useState(null);
  const [joinLimitResetSubmitting, setJoinLimitResetSubmitting] = useState(false);
  const [isCalendarPickerOpen, setIsCalendarPickerOpen] = useState(false);
  const [isHostClaimConfirmOpen, setIsHostClaimConfirmOpen] = useState(false);
  const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);

  const [linkCopied, setLinkCopied] = useState(false);
  const participantsSectionRef = useRef(null);
  const [participantsAnchorConsumed, setParticipantsAnchorConsumed] = useState(false);
  const referralCodeRef = useRef(null);

  const readRefCodeFromLocation = React.useCallback(() => {
    try {
      const rawSearch =
        String(location?.search || "").trim() ||
        String(window?.location?.search || "").trim();
      const params = new URLSearchParams(rawSearch);
      const code = String(params.get("ref") || "").trim();
      return code || null;
    } catch {
      return null;
    }
  }, [location?.search]);

  const getOrCreateReferralCode = React.useCallback(async () => {
    if (referralCodeRef.current) return referralCodeRef.current;
    if (isGuest) return null;

    try {
      const { data } = await api.post("/miniapp/referral-links/");
      const directCode = String(data?.code || "").trim();
      if (directCode) {
        referralCodeRef.current = directCode;
        return directCode;
      }

      const url = String(data?.url || "").trim();
      if (url) {
        const parsed = new URL(url, window.location.origin);
        const fromUrl = String(parsed.searchParams.get("ref") || "").trim();
        if (fromUrl) {
          referralCodeRef.current = fromUrl;
          return fromUrl;
        }
      }
    } catch (err) {
      console.warn("[EventDetailsFullscreen] failed to load referral code for share", err);
    }

    return null;
  }, [isGuest]);

  const buildAuthRedirectUrl = React.useCallback(() => {
    const params = new URLSearchParams();
    params.set("reason", AUTH_REQUIRED_REASON);

    const refCode = readRefCodeFromLocation();
    if (refCode) params.set("ref", refCode);

    const fullPath = `${location?.pathname || "/"}${location?.search || ""}${location?.hash || ""}`;
    if (fullPath.startsWith("/")) {
      params.set("next", fullPath);
    }

    const query = params.toString();
    return `/auth${query ? `?${query}` : ""}`;
  }, [location?.hash, location?.pathname, location?.search, readRefCodeFromLocation]);

  const redirectGuestToAuth = React.useCallback(() => {
    const authUrl = buildAuthRedirectUrl();
    if (typeof onRequireAuth === "function") {
      onRequireAuth(authUrl);
      return;
    }
    navigate(authUrl, { replace: true });
  }, [buildAuthRedirectUrl, navigate, onRequireAuth]);

  const shareEvent = async (e) => {
    e.stopPropagation();
    if (!ev?.id) return;
    let url = buildEventShareUrl(ev);
    const refCode = readRefCodeFromLocation() || (await getOrCreateReferralCode());
    if (refCode) {
      const parsed = new URL(url);
      parsed.searchParams.set("ref", refCode);
      url = parsed.toString();
    }
    try {
      if (await shareContent({ url })) {
        return;
      }
      throw new Error("Web Share not supported");
    } catch (err) {
      // Native share sheets report a genuine user cancellation. On the web,
      // Windows also uses AbortError when its share UI cannot complete, so it
      // must still continue to the clipboard fallback.
      const nativeShareCanceled =
        isNativeApp() && (err?.name === "AbortError" || err?.message === "Share canceled");
      if (nativeShareCanceled) return;
      try {
        const copied = await copyTextToClipboard(url);
        if (!copied) throw new Error("Clipboard write failed");
        setLinkCopied(true);
        window.dispatchEvent(new CustomEvent("global-info", {
          detail: { message: t('events.details.link_copied') }
        }));
        setTimeout(() => setLinkCopied(false), 2500);
      } catch {
        window.dispatchEvent(new CustomEvent("global-error", {
          detail: { message: t('events.details.unable_copy_link') }
        }));
      }
    }
  };

  const [localEvent, setLocalEvent] = useState(event ?? null);
  useEffect(() => {
    setLocalEvent((previousEvent) => keepLastResolvedEvent(previousEvent, event));
  }, [event]);
  useEffect(() => {
    attendanceSessionRedirectEventRef.current = null;
    if (attendanceSessionRedirectTimerRef.current) {
      window.clearTimeout(attendanceSessionRedirectTimerRef.current);
      attendanceSessionRedirectTimerRef.current = null;
    }
    return () => {
      if (attendanceSessionRedirectTimerRef.current) {
        window.clearTimeout(attendanceSessionRedirectTimerRef.current);
        attendanceSessionRedirectTimerRef.current = null;
      }
    };
  }, [event?.id]);
  useEffect(() => {
    ticketCancellationResolvedRef.current = false;
    ticketCancellationLoadSeqRef.current = 0;
  }, [event?.id]);
  const ev = localEvent;
  const { confirmAudience, audienceModal } = useEventAudienceConfirmation(ev);
  const isConcept = ev?.type === "concept";
  const participantTicketing = serverTicketPaymentState || ev?.participant_ticketing || null;
  const ticketPurchasePreview = ev?.ticket_purchase_preview || null;
  const effectiveTicketingPreview = participantTicketing || ticketPurchasePreview;
  const participantPaidTicketAmount = Number.parseFloat(
    participantTicketing?.total_paid_amount || "0"
  );
  const leaveRequiresTicketCancellation = Boolean(
    participantTicketing &&
    Number.isFinite(participantPaidTicketAmount) &&
    participantPaidTicketAmount > 0
  );
  const openParticipantTicketPanel = useCallback((view = "summary") => {
    setTicketPanelOpenView(
      view === "cancellation" || view === "dispute" ? view : "summary"
    );
    setTicketPanelOpenRequest((prev) => prev + 1);
  }, []);
  const handleTicketPanelOpenHandled = useCallback(() => {
    setTicketPanelOpenRequest(0);
  }, []);
  const lang = (i18n?.resolvedLanguage || "en").split("-")[0];
  const joinLimitCopy = PARTICIPANT_JOIN_LIMIT_COPY[lang] || PARTICIPANT_JOIN_LIMIT_COPY.en;
  const userStorageCode = ev?.storage_code || null;
  const detailsViewTrackedRef = useRef(null);

  useEffect(() => {
    if (!ev?.id) return;
    const trackingKey = [
      ev.id,
      analyticsContext?.surface || "",
      analyticsContext?.list_type || "",
      analyticsContext?.open_mode || "",
    ].join(":");
    if (detailsViewTrackedRef.current === trackingKey) return;
    detailsViewTrackedRef.current = trackingKey;
    trackEventDetailsView(ev, {
      surface: analyticsContext?.surface || "event_details",
      list_type: analyticsContext?.list_type || "",
      open_mode: analyticsContext?.open_mode || "details",
    });
  }, [analyticsContext, ev]);

  useEffect(() => {
    setParticipantsAnchorConsumed(false);
  }, [ev?.id, openParticipantsOnMount]);

  useEffect(() => {
    if (!openParticipantsOnMount || participantsAnchorConsumed) return;
    if (!participantsSectionRef.current) return;

    let rafId = null;
    let timeoutId = null;

    const scrollToParticipants = () => {
      if (!participantsSectionRef.current) return;
      participantsSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: isDesktop ? "start" : "nearest",
      });
      setParticipantsAnchorConsumed(true);
    };

    rafId = window.requestAnimationFrame(scrollToParticipants);
    timeoutId = window.setTimeout(scrollToParticipants, 140);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [isDesktop, openParticipantsOnMount, participantsAnchorConsumed]);

  useEffect(() => {
    setWaitlistMode("participant");
    setIsCoHostModalOpen(false);
    setIsHostTransferModalOpen(false);
    setHostRoleCandidates([]);
    setCoHostSubmitting(false);
    setIsCalendarPickerOpen(false);
    try {
      const search = (location && location.search) || window.location.search || "";
      const sp = new URLSearchParams(search);
      const openingConceptPoll = sp.get("concept_poll") === "1";
      if (!openingConceptPoll) {
        setIsConceptDatePollModalOpen(false);
      }
    } catch {
      setIsConceptDatePollModalOpen(false);
    }
  }, [ev?.id]);

  useEffect(() => {
    if (!isLeaveConfirmOpen) {
      setLeavePreview(null);
      setLeavePreviewLoading(false);
    }
  }, [isLeaveConfirmOpen]);

  const [joiningChat, setJoiningChat] = useState(false);

  const getTelegramChatLink = useCallback((eventObj) => {
    if (!eventObj) return null;

    const link =
      eventObj?.chat ||
      eventObj?.telegram_chat_link ||
      eventObj?.telegram_link ||
      null;

    if (!link) return null;
    const s = String(link).trim();
    return s.length ? s : null;
  }, []);

  const openInternalEventChat = useCallback(
    async (e, options = {}) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      if (!ev?.id) return;

      if (isGuest) {
        redirectGuestToAuth();
        return;
      }

      try {
        setJoiningChat(true);

        const res = await api.post(`/chats/${ev.id}/internal-chat/join/`);
        const chatId = res?.data?.chat_id ?? null;

        const params = new URLSearchParams();
        if (chatId) params.set("chat_id", String(chatId));
        if (options?.openParticipants) params.set("participants", "1");

        const qs = params.toString();
        const url = `/menu/chats/event/${ev.id}${qs ? `?${qs}` : ""}`;

        const shouldOpenInNewTab =
          isDesktop &&
          !isInAppBrowser &&
          typeof window !== "undefined" &&
          !options?.forceSameTab;

        if (shouldOpenInNewTab) {
          const fullUrl = `${window.location.origin}${url}`;
          window.open(fullUrl, "_blank", "noopener,noreferrer");
        } else {
          navigate(url);
        }
      } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("events.details.join_chat_failed");

        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: { message: msg, status },
          })
        );
      } finally {
        setJoiningChat(false);
      }
    },
    [ev?.id, isGuest, navigate, onRequireAuth, isDesktop, isInAppBrowser, redirectGuestToAuth]
  );

  const openEventChat = useCallback(
    async (e, options = {}) => {
      e?.stopPropagation?.();

      const tg = getTelegramChatLink(ev);
      if (tg && !options?.forceInternal) {
        await openExternalUrl(tg);
        return;
      }

      await openInternalEventChat(e, options);
    },
    [ev, getTelegramChatLink, openInternalEventChat]
  );

  const openMainHostDirectChat = useCallback(
    (e) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();

      if (isGuest) {
        redirectGuestToAuth();
        return;
      }

      const hostCode = String(ev?.host?.code || "").trim().toUpperCase();
      if (!hostCode || !ev?.id) return;

      navigate(`/menu/chats/direct/code/${hostCode}`, {
        state: {
          source: "event_details",
          peer: ev?.host || null,
          eventContext: {
            id: ev.id,
            name: ev.name || "",
            type: ev.type || "event",
            status: ev.status || "active",
            date_time: ev.date_time || null,
            country: ev.country || "",
            city: ev.city || "",
            event_format: ev.event_format,
            host_format: ev.host_format,
            badge_label: ev.badge_label || null,
            icon_image_url: ev.icon_image_url || null,
            pricing_model: ev.pricing_model || "free",
            join_by_request: Boolean(ev.join_by_request),
            is_verified: Boolean(ev.is_verified),
            is_validated: ev.is_validated !== false,
            public_path: ev.public_path || buildEventPath(ev.id, ev.name || ""),
          },
        },
      });
    },
    [ev, isGuest, navigate, redirectGuestToAuth]
  );

  useEffect(() => {
    if (isConcept && isConfirmOpen) setIsConfirmOpen(false);
  }, [isConcept, isConfirmOpen]);

  const [favouriteState, setFavouriteState] = useState(Boolean(isFavourite));

  useEffect(() => {
    setFavouriteState(Boolean(isFavourite));
  }, [ev?.id, isFavourite]);

  const toggleFav = useCallback(async (e) => {
    e.stopPropagation();
    if (!ev?.id) return;
    const previous = favouriteState;
    setFavouriteState(!previous);
    const succeeded = await onToggleFavourite(ev.id);
    if (succeeded === false) setFavouriteState(previous);
  }, [favouriteState, onToggleFavourite, ev?.id]);

  useEffect(() => {
    if (asPage) return undefined;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [asPage]);

  const buildEditPayload = useCallback(async (payloadEvent) => {
    let eventData = payloadEvent || null;
    let hostRating = payloadEvent?.host_rating || null;

    if (!eventData?.id) {
      return { eventData, hostRating };
    }

    try {
      const [detailsRes, hostRatingRes] = await Promise.all([
        api.get(`/events/details/${eventData.id}/`).catch(() => null),
        hostRating
          ? Promise.resolve({ data: hostRating })
          : api.get("/events/get-host-rating/").catch(() => null),
      ]);

      if (detailsRes?.data) {
        eventData = { ...eventData, ...detailsRes.data };
      }
      if (hostRatingRes?.data) {
        hostRating = hostRatingRes.data;
      }
    } catch (err) {
      console.warn("[EventDetailsFullscreen] edit payload enrichment failed", err);
    }

    return { eventData, hostRating };
  }, []);

  const handleEditEvent = useCallback(async (requestedSection = null) => {
    const payloadEvent = ev || event;
    if (!payloadEvent) return;

    const { eventData, hostRating } = await buildEditPayload(payloadEvent);
    const initialSection = requestedSection === "promo_gallery" ? requestedSection : null;
    navigate("/events/edit", {
      state: {
        mode: "edit",
        eventData: eventData || payloadEvent,
        hostRating: hostRating || null,
        ...(initialSection ? { initialSection } : {}),
      },
    });
  }, [buildEditPayload, ev, event, navigate]);

  const handleActivateConcept = useCallback(async () => {
    const payloadEvent = ev || event;
    if (!payloadEvent || payloadEvent.type !== "concept") return;

    const [{ eventData, hostRating }, pollRes] = await Promise.all([
      buildEditPayload(payloadEvent),
      api.get(`/events/${payloadEvent.id}/concept-date-poll/`).catch(() => ({ data: null })),
    ]);
    navigate(`/events/edit?activate_concept=${payloadEvent.id}`, {
      state: {
        mode: "activate_concept",
        eventData: eventData || payloadEvent,
        conceptDatePoll: pollRes?.data?.poll || null,
        activationMode: true,
        hostRating: hostRating || null,
      },
    });
  }, [buildEditPayload, ev, event, navigate]);

  const handleRepeatEvent = async () => {
    const payloadEvent = ev || event;
    if (!payloadEvent) return;

    const { eventData, hostRating } = await buildEditPayload(payloadEvent);

    navigate("/events/edit", {
      state: {
        mode: "clone",
        startAtTypeSelection: true,
        eventData: eventData || payloadEvent,
        hostRating: hostRating || null,
      },
    });
  };

  const refreshDetails = useCallback(async () => {
    if (!ev?.id) return null;
    try {
      const { data } = await api.get(`/events/details/${ev.id}/`);
      setLocalEvent(data);
      return data || null;
    } catch (err) {
      console.error("Refresh failed", err);
      return null;
    }
  }, [ev?.id]);

  const refreshDetailsAndNotify = useCallback(async () => {
    const updatedEvent = await refreshDetails();
    if (updatedEvent?.id) {
      onEventUpdate(updatedEvent);
    }
    return updatedEvent;
  }, [onEventUpdate, refreshDetails]);

  const hostPaidDetailsHydratedForRef = useRef(null);

  useEffect(() => {
    const payloadEvent = ev || event;
    const eventId = payloadEvent?.id;
    if (!eventId) return;

    const isHostPaidCardEvent =
      payloadEvent?.ticketing_flow_kind === "host_stripe" ||
      (payloadEvent?.ticketing_flow_kind !== "vaultgrid_revolut" &&
        String(payloadEvent?.pricing_model || "").toLowerCase() === "host_paid" &&
        String(payloadEvent?.host_payment_collection_method || "").toLowerCase() === "card");
    if (!isHostPaidCardEvent) {
      hostPaidDetailsHydratedForRef.current = null;
      return;
    }

    const missingOrganizer = !payloadEvent?.host_paid_organizer;
    const missingPurchasePreview = !payloadEvent?.ticket_purchase_preview;
    if (!missingOrganizer && !missingPurchasePreview) {
      hostPaidDetailsHydratedForRef.current = null;
      return;
    }

    if (hostPaidDetailsHydratedForRef.current === eventId) {
      return;
    }
    hostPaidDetailsHydratedForRef.current = eventId;
    void refreshDetails();
  }, [
    ev,
    event,
    refreshDetails,
  ]);

  const removeViewerFromLocalEventAfterCancellation = useCallback(() => {
    setLocalEvent((prev) => {
      if (!prev) return prev;
      const currentParticipants = Array.isArray(prev.participants) ? prev.participants : [];
      const filteredParticipants = userStorageCode
        ? currentParticipants.filter((participant) => participant?.code !== userStorageCode)
        : currentParticipants;
      const participantWasPresent = filteredParticipants.length !== currentParticipants.length;
      const nextCountRaw =
        typeof prev.participants_count === "number"
          ? prev.participants_count - (participantWasPresent ? 1 : 0)
          : filteredParticipants.length;
      return {
        ...prev,
        is_participant: false,
        ticket_paid: false,
        participant_ticketing: null,
        participants: filteredParticipants,
        participants_count: Math.max(0, nextCountRaw || 0),
      };
    });
  }, [userStorageCode]);

  const requestEventToolsOpen = React.useCallback(() => {
    setEventToolsOpenRequest((value) => value + 1);
  }, []);

  const requestEventToolsClose = React.useCallback(() => {
    setEventToolsCloseRequest((value) => value + 1);
  }, []);

  const removeParticipantsFromLocalEvent = useCallback((removedUserIds = [], removedParticipants = []) => {
    const normalizeRemovalCode = (value) => String(value || "").trim().toUpperCase();
    const removedIdSet = new Set(
      (Array.isArray(removedUserIds) ? removedUserIds : [])
        .map((value) => Number(value || 0))
        .filter(Boolean)
    );
    const removedCodeSet = new Set(
      (Array.isArray(removedParticipants) ? removedParticipants : [])
        .map((participant) => normalizeRemovalCode(participant?.code))
        .filter(Boolean)
    );

    if (!removedIdSet.size && !removedCodeSet.size) return;

    setLocalEvent((prev) => {
      if (!prev) return prev;
      const currentParticipants = Array.isArray(prev.participants) ? prev.participants : [];
      const shouldRemove = (participant) => {
        const userId = Number(participant?.user_id || participant?.user?.id || participant?.id || 0);
        const code = normalizeRemovalCode(participant?.code || participant?.user?.code);
        return (userId && removedIdSet.has(userId)) || (code && removedCodeSet.has(code));
      };
      const filteredParticipants = currentParticipants.filter((participant) => !shouldRemove(participant));
      const removedCount = currentParticipants.length - filteredParticipants.length;
      const currentCoHosts = Array.isArray(prev.co_hosts) ? prev.co_hosts : [];
      const filteredCoHosts = currentCoHosts.filter((participant) => !shouldRemove(participant));
      const nextCountRaw =
        typeof prev.participants_count === "number"
          ? prev.participants_count - removedCount
          : filteredParticipants.length;

      return {
        ...prev,
        participants: filteredParticipants,
        participants_count: Math.max(0, nextCountRaw || 0),
        co_hosts: filteredCoHosts,
      };
    });
  }, []);

  // 🔹 коллбек, который будет использоваться мобилкой для join-by-request
  const handleAfterJoinByRequestSuccess = () => {
    void refreshDetailsAndNotify();
  };

  const checkProfilePhoto = React.useCallback(async (nextAction = null) => {
    // Strict rule: joining events is allowed ONLY with a custom avatar.
    const openModal = () => {
      setPhotoGateTarget(nextAction);
      setIsPhotoModalOpen(true);
    };

    // If we already know the user has a custom avatar, allow join immediately
    if (hasCustomAvatar === true) {
      setPhotoGateTarget(null);
      return true;
    }

    // If we know there is no avatar, open the modal
    if (hasCustomAvatar === false) {
      openModal();
      return false;
    }

    // First-time check: fetch profile info
    try {
      const { data } = await api.get("/accounts/profile/");
      const nextJoinRulesRequired =
        typeof data?.event_join_rules_intro_required === "boolean"
          ? data.event_join_rules_intro_required
          : null;
      joinRulesIntroRequiredRef.current = nextJoinRulesRequired;
      setJoinRulesIntroRequired(nextJoinRulesRequired);

      const url = data?.avatar_custom_url || null;
      if (url) {
        setHasCustomAvatar(true);
        setPhotoGateTarget(null);
        return true;
      }

      // No custom avatar → show modal and block join
      setHasCustomAvatar(false);
      openModal();
      return false;
    } catch (err) {
      console.error("[EventDetailsFullscreen] profile check failed", err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("events.details.generic_error");
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: { message: msg },
        })
      );
      // Strict: on error we do NOT allow join (otherwise it becomes a bypass)
      return false;
    }
  }, [hasCustomAvatar, t]);

  const checkJoinRulesIntro = React.useCallback(async () => {
    const cachedRequired = joinRulesIntroRequiredRef.current;
    if (cachedRequired === true || joinRulesIntroRequired === true) {
      return true;
    }

    if (cachedRequired === false || joinRulesIntroRequired === false) {
      return false;
    }

    try {
      const { data } = await api.get("/accounts/profile/");
      const nextValue =
        typeof data?.event_join_rules_intro_required === "boolean"
          ? data.event_join_rules_intro_required
          : false;
      joinRulesIntroRequiredRef.current = nextValue;
      setJoinRulesIntroRequired(nextValue);
      return nextValue;
    } catch (err) {
      console.error("[EventDetailsFullscreen] join rules intro check failed", err);
      return false;
    }
  }, [joinRulesIntroRequired]);

  const acknowledgeJoinRulesIntro = React.useCallback(async () => {
    const { data } = await api.post("/events/join-rules/ack/", {});
    const nextRequired =
      typeof data?.event_join_rules_intro_required === "boolean"
        ? data.event_join_rules_intro_required
        : false;
    joinRulesIntroRequiredRef.current = nextRequired;
    setJoinRulesIntroRequired(nextRequired);
  }, []);

  const checkHostCreateRulesIntro = React.useCallback(async () => {
    if (hostCreateRulesIntroRequired === true) {
      return true;
    }

    if (hostCreateRulesIntroRequired === false) {
      return false;
    }

    try {
      const { data } = await api.get("/accounts/profile/");
      const nextValue =
        typeof data?.host_create_rules_intro_required === "boolean"
          ? data.host_create_rules_intro_required
          : false;
      setHostCreateRulesIntroRequired(nextValue);
      return nextValue;
    } catch (err) {
      console.error("[EventDetailsFullscreen] host create rules intro check failed", err);
      return false;
    }
  }, [hostCreateRulesIntroRequired]);

  const openIntentModal = React.useCallback(async () => {
    const ok = await checkProfilePhoto("intent");
    if (!ok) return false;
    setPhotoGateTarget(null);
    setIsIntentOpen(true);
    return true;
  }, [checkProfilePhoto]);
  deepLinkIntentOpenerRef.current = openIntentModal;

  const setIsIntentOpenGuarded = React.useCallback(
    (nextOpen) => {
      if (!nextOpen) {
        setIsIntentOpen(false);
        return;
      }
      void openIntentModal();
    },
    [openIntentModal]
  );

  const openParticipantRatingFromJoinModal = React.useCallback(() => {
    setJoinLimitModal(null);
    navigate("/events/participant-rating");
  }, [navigate]);

  const openParticipantRatingFromRulesModal = React.useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/events/participant-rating");
  }, [navigate]);

  const openPremiumFromJoinModal = React.useCallback(() => {
    setJoinLimitModal(null);
    navigate("/menu/subscription");
  }, [navigate]);

  const resetParticipantRatingFromJoinModal = React.useCallback(async () => {
    try {
      setJoinLimitResetSubmitting(true);
      await api.post("/events/participant-rating/reset/");
      setJoinLimitModal(null);
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message:
              joinLimitCopy.blockedPremiumReady,
          },
        })
      );
      void refreshDetailsAndNotify();
    } catch (err) {
      const msg = err?.response?.data?.error || t("events.details.join_failed");
      window.dispatchEvent(new CustomEvent("global-error", { detail: { message: msg, status: 400 } }));
    } finally {
      setJoinLimitResetSubmitting(false);
    }
  }, [joinLimitCopy.blockedPremiumReady, refreshDetailsAndNotify, t]);

  const buildJoinLimitModalNode = React.useCallback((payload, mode) => {
    const score = Number(payload?.score_percent) || 0;
    const slotsUsed = Number(payload?.slots_used) || 0;
    const slotsTotal = Number(payload?.slots_total) || 0;
    const canRaise = Boolean(payload?.can_raise_slots_with_rating);

    if (mode === "blocked") {
      return (
        <div className="text-center text-white">
          <p className="text-[1.02rem] font-medium leading-relaxed">
            {score > 0 ? joinLimitCopy.cooldownLead : joinLimitCopy.blockedLead}
          </p>
          <div className="mt-4 rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3">
            <div className="text-xs uppercase tracking-[0.18em] text-rose-100/60">
              {joinLimitCopy.scoreLine}
            </div>
            <div className="mt-1 text-3xl font-semibold text-rose-300">{score}%</div>
          </div>
          <p className="mt-4 text-[1.02rem] font-medium leading-relaxed">
            {joinLimitCopy.blockedSupport}
          </p>
        </div>
      );
    }

    return (
        <div className="text-center text-white">
          <p className="text-[1.02rem] font-medium leading-relaxed">
            {joinLimitCopy.slotsLead(slotsUsed, slotsTotal)}
          </p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <div className="text-xs uppercase tracking-[0.18em] text-white/45">
              {joinLimitCopy.scoreLine}
            </div>
            <div className="mt-1 text-3xl font-semibold text-white">{score}%</div>
          </div>
          <div className="rounded-2xl border border-sky-400/20 bg-sky-500/10 px-4 py-3">
            <div className="text-xs uppercase tracking-[0.18em] text-sky-100/65">
              {joinLimitCopy.slotLine}
            </div>
            <div className="mt-1 text-3xl font-semibold text-sky-300">
              {slotsUsed}/{slotsTotal}
            </div>
          </div>
        </div>
        <p className="mt-4 text-[1.02rem] font-medium leading-relaxed">
          {canRaise ? joinLimitCopy.slotsImprove : joinLimitCopy.slotsMaxed}
        </p>
        <p className="mt-4 text-[1.02rem] font-medium leading-relaxed text-sky-100/85">
          {joinLimitCopy.slotsPremium}
        </p>
      </div>
    );
  }, [joinLimitCopy]);

  const openJoinLimitModalFromPayload = React.useCallback(
    (code, participantRating) => {
      const normalizedCode = String(code || "").trim();
      if (normalizedCode === "participant_rating_blocked" && participantRating) {
        setJoinLimitModal({
          mode: "blocked",
          title: Number(participantRating.score_percent) > 0 ? joinLimitCopy.cooldownTitle : joinLimitCopy.blockedTitle,
          messageNode: buildJoinLimitModalNode(participantRating, "blocked"),
          primaryAction: "rating",
          primaryText: joinLimitCopy.openRating,
        });
        return true;
      }
      if (normalizedCode === "participant_slots_full" && participantRating) {
        setJoinLimitModal({
          mode: "slots_full",
          title: joinLimitCopy.slotsTitle,
          messageNode: buildJoinLimitModalNode(participantRating, "slots_full"),
          primaryAction: "premium",
          primaryText: joinLimitCopy.openPremium,
          secondaryAction: "rating",
          secondaryText: joinLimitCopy.openRating,
        });
        return true;
      }
      return false;
    },
    [
      buildJoinLimitModalNode,
      joinLimitCopy.blockedTitle,
      joinLimitCopy.cooldownTitle,
      joinLimitCopy.openPremium,
      joinLimitCopy.openRating,
      joinLimitCopy.slotsTitle,
    ]
  );

  const getJoinLimitActionHandler = React.useCallback((action) => {
    if (action === "premium") return openPremiumFromJoinModal;
    if (action === "reset") return resetParticipantRatingFromJoinModal;
    if (action === "rating") return openParticipantRatingFromJoinModal;
    return undefined;
  }, [
    openParticipantRatingFromJoinModal,
    openPremiumFromJoinModal,
    resetParticipantRatingFromJoinModal,
  ]);

  const handleJoinPaymentRequired = React.useCallback(async (data) => {
    setServerPrepayRequired(true);
    setForceImmediatePayment(!!data?.need_immediate_payment);
    setServerPayUrl(data?.pay_url || null);
    setServerTicketPaymentState(data?.ticket_payment_state || null);
    setIntentVariant("payment");
    setPreJoinPaymentFlag(true);
    setTicketMode(false);
    await openIntentModal();
  }, [openIntentModal]);

  const handleCheckoutCapacityReserved = React.useCallback((payload = null) => {
    const code = String(payload?.code || "").trim();
    setServerPayUrl(null);
    setServerTicketPaymentState(null);
    setServerPrepayRequired(false);
    setForceImmediatePayment(false);
    setIsIntentOpen(false);

    if (WAITLIST_CAPACITY_BLOCK_CODES.has(code)) {
      setCheckoutCapacityReservedOpen(false);
      setWaitlistCapacityMessage(
        payload?.error || t("events.details.join_failed")
      );
      setWaitlistMode("participant");
      setWaitlistOpenedFromAvailabilityAlert(false);
      setIsWaitlistOpen(true);
      void loadWaitlistStatusRef.current?.({ silent: true });
      return;
    }

    setCheckoutCapacityReservedOpen(true);
  }, [t]);

  const doJoinEvent = React.useCallback(async (
    targetEvent = ev,
    { confirmedGender = null, confirmGenderMismatch = false } = {}
  ) => {
    if (!targetEvent?.id) return;
    const targetIsConcept = targetEvent?.type === "concept";

    try {
      const payload = confirmedGender
        ? {
            confirmed_gender: confirmedGender,
            ...(confirmGenderMismatch ? { confirm_gender_mismatch: true } : {}),
          }
        : {};
      const { data } = await api.post(`/events/join-event/${targetEvent.id}/`, payload);
      setGenderPrompt(null);
      setGenderError("");

      if (data?.payment_required) {
        await handleJoinPaymentRequired(data);
        return true;
      }

      if (data?.success && (data.joined === undefined || data.joined === true)) {
        trackEventJoinSuccess(targetEvent, {
          surface: analyticsContext?.surface || "event_details",
          list_type: analyticsContext?.list_type || "",
          open_mode: analyticsContext?.open_mode || "details",
        });
        setJoinRulesIntroRequired(false);
        const claimedHost = Boolean(data?.claimed_host);
        const rewardCoins = Number(data?.reward?.coins || 0);
        const rewardDiamonds = Number(data?.reward?.diamonds || 0);
        const rewardKey1 = Number(data?.reward?.keys_level_1 || 0);
        const rewardKey2 = Number(data?.reward?.keys_level_2 || 0);
        const rewardKey3 = Number(data?.reward?.keys_level_3 || 0);
        const rewardKey4 = Number(data?.reward?.keys_level_4 || 0);
        const rewardKey5 = Number(data?.reward?.keys_level_5 || 0);

        setLocalEvent((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            ...(claimedHost
              ? {
                  is_hostless_concept: false,
                  concept_host_state: "none",
                }
              : {}),
            participant_roster_visible: true,
            participants: [
              ...(Array.isArray(prev?.participants) ? prev.participants : []),
              { code: userStorageCode, avatar: "" },
            ],
            participants_count: getParticipantCount(prev) + 1,
          };
        });
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: claimedHost
                ? t("events.details.host_claim_success", {
                    coins: rewardCoins,
                    diamonds: rewardDiamonds,
                    key1: rewardKey1,
                    key2: rewardKey2,
                    key3: rewardKey3,
                    key4: rewardKey4,
                    key5: rewardKey5,
                  })
                : t(
                    targetIsConcept
                      ? "events.details.joined_concept"
                      : "events.details.joined_event"
                  ),
            },
          })
        );
        void refreshDetailsAndNotify();
        window.dispatchEvent(
          new CustomEvent("vg-participant-rating-unread-refresh")
        );
        if (claimedHost) {
          setIsHostClaimConfirmOpen(false);
          return true;
        }
        setIsJoinConfirmOpen(true);
        return true;
      }

      throw new Error(data?.error || t('events.details.join_failed'));
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.message;
      const code = String(err?.response?.data?.code || "").trim();
      const participantRating = err?.response?.data?.participant_rating || null;
      if (code === "checkout_capacity_reserved") {
        handleCheckoutCapacityReserved(err?.response?.data || null);
        return false;
      }
      if (WAITLIST_CAPACITY_BLOCK_CODES.has(code)) {
        setWaitlistCapacityMessage(msg || t("events.details.join_failed"));
        setWaitlistMode("participant");
        setWaitlistOpenedFromAvailabilityAlert(false);
        setIsWaitlistOpen(true);
        trackGAEvent("waitlist_modal_open", {
          event_id: String(targetEvent.id),
          source: "join_capacity_block",
          capacity_block_code: code,
        });
        void loadWaitlistStatusRef.current?.({ silent: true });
        return false;
      }
      if (openJoinLimitModalFromPayload(code, participantRating)) {
        return false;
      }
      if (code === "gender_confirmation_required") {
        setGenderError("");
        setGenderPrompt({ targetEvent });
        return true;
      }
      if (code === "gender_mismatch_confirmation_required" && confirmedGender) {
        return await doJoinEvent(targetEvent, {
          confirmedGender,
          confirmGenderMismatch: true,
        });
      }
      if (confirmedGender) {
        setGenderError(msg || t("events.details.join_failed"));
      }
      window.dispatchEvent(new CustomEvent("global-error", { detail: { message: msg, status } }));
      return false;
    }
  }, [
    ev,
    t,
    userStorageCode,
    refreshDetailsAndNotify,
    handleJoinPaymentRequired,
    handleCheckoutCapacityReserved,
    analyticsContext,
    openJoinLimitModalFromPayload,
    navigate,
    onClose,
  ]);

  useEffect(() => {
    if (!location.state?.resumeHostClaimAfterRules || !ev?.id) return;
    if (Number(location.state.hostClaimRulesEventId) !== Number(ev.id)) return;
    if (hostClaimRulesResumeRef.current === location.key) return;
    hostClaimRulesResumeRef.current = location.key;
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      {
        replace: true,
        state: {
          ...(location.state || {}),
          resumeHostClaimAfterRules: false,
          hostClaimRulesEventId: undefined,
        },
      }
    );
    void doJoinEvent();
  }, [doJoinEvent, ev?.id, location, navigate]);

  const performJoin = React.useCallback(async () => {
    trackEventJoinClick(ev, {
      surface: analyticsContext?.surface || "event_details",
      list_type: analyticsContext?.list_type || "",
      open_mode: analyticsContext?.open_mode || "details",
    });
    return await doJoinEvent();
  }, [analyticsContext, doJoinEvent, ev]);

  const continueJoinFlow = React.useCallback(async () => {
    if (!(await confirmAudience())) return false;
    const needsJoinRulesIntro = await checkJoinRulesIntro();
    if (needsJoinRulesIntro) {
      setJoinRulesInitialIndex(0);
      setIsJoinRulesIntroOpen(true);
      return false;
    }

    return await performJoin();
  }, [checkJoinRulesIntro, confirmAudience, ev, performJoin]);

  useEffect(() => {
    const shouldResumeJoinRules = Boolean(location?.state?.resumeJoinRulesModal);
    if (shouldResumeJoinRules) {
    const nextJoinCardRaw = Number(location?.state?.resumeJoinRulesCard);
    const nextJoinCard = Number.isFinite(nextJoinCardRaw)
      ? Math.max(0, Math.min(2, nextJoinCardRaw))
      : 0;

    setJoinRulesInitialIndex(nextJoinCard);
    setIsJoinRulesIntroOpen(true);

      navigate(
        {
          pathname: location.pathname,
          search: location.search,
        },
        {
          replace: true,
          state: {
            ...(location.state || {}),
            resumeJoinRulesModal: false,
            resumeJoinRulesCard: undefined,
          },
        }
      );
      return;
    }

  }, [location, navigate]);

  const loadWaitlistStatus = React.useCallback(
    async ({ silent = false, markHostSeen = false } = {}) => {
      if (!ev?.id || isGuest) {
        setWaitlistStatus(null);
        setWaitlistLoadError("");
        return null;
      }
      setWaitlistLoading(true);
      setWaitlistLoadError("");
      try {
        const params = markHostSeen ? { mark_host_seen: 1 } : undefined;
        const { data } = await api.get(`/events/waitlist/${ev.id}/`, { params });
        setWaitlistStatus(data || null);
        if (markHostSeen && typeof waitlistUnreadRefetch === "function") {
          void waitlistUnreadRefetch();
        }
        return data || null;
      } catch (err) {
        const status = err?.response?.status;
        const loadErrorMessage = t("events.details.waitlist.load_failed");
        setWaitlistLoadError(loadErrorMessage);
        trackGAEvent("waitlist_status_load_failed", {
          event_id: String(ev.id),
          http_status: status || 0,
        });
        if (!silent) {
          const msg =
            err?.response?.data?.error ||
            err?.message ||
            loadErrorMessage;
          window.dispatchEvent(
            new CustomEvent("global-error", { detail: { message: msg, status } })
          );
        }
        return null;
      } finally {
        setWaitlistLoading(false);
      }
    },
    [ev?.id, isGuest, t, waitlistUnreadRefetch]
  );
  loadWaitlistStatusRef.current = loadWaitlistStatus;

  const doJoinWaitlist = React.useCallback(async ({ openModal = true } = {}) => {
    if (!ev?.id) return false;
    setWaitlistSubmitting(true);
    try {
      const { data } = await api.post(`/events/add-to-waitlist/${ev.id}/`);
      const msg =
        data?.message || t("events.details.waitlist.added_success");
      trackGAEvent("waitlist_join_success", {
        event_id: String(ev.id),
        prepay_required: Boolean(ev?.is_payable_in_advance),
      });
      window.dispatchEvent(
        new CustomEvent("global-info", { detail: { message: msg } })
      );

      const responsePosition = Number(data?.position || 0) || null;
      setWaitlistStatus((previousStatus) => {
        const wasAlreadyInWaitlist = Boolean(previousStatus?.is_in_waitlist);
        const previousCount = Math.max(0, Number(previousStatus?.waitlist_count || 0));
        return {
          ...(previousStatus || {}),
          event_id: ev.id,
          is_in_waitlist: true,
          my_position: responsePosition || previousStatus?.my_position || null,
          waitlist_count: Math.max(
            previousCount + (wasAlreadyInWaitlist ? 0 : 1),
            responsePosition || 1
          ),
          can_join_waitlist: false,
          join_error: null,
          join_block_code: null,
        };
      });
      setWaitlistMode("participant");
      setWaitlistCapacityMessage("");
      if (openModal) {
        setIsWaitlistOpen(true);
      }

      await loadWaitlistStatus({ silent: true });
      await refreshDetailsAndNotify();
      return true;
    } catch (err) {
      const status = err?.response?.status;
      const code = String(err?.response?.data?.code || "").trim();
      const participantRating = err?.response?.data?.participant_rating || null;
      if (openJoinLimitModalFromPayload(code, participantRating)) {
        setIsWaitlistOpen(false);
        await loadWaitlistStatus({ silent: true });
        return false;
      }
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("events.details.generic_error");
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg, status } })
      );
      await loadWaitlistStatus({ silent: true });
      return false;
    } finally {
      setWaitlistSubmitting(false);
    }
  }, [ev?.id, ev?.is_payable_in_advance, loadWaitlistStatus, openJoinLimitModalFromPayload, refreshDetailsAndNotify, t]);

  useEffect(() => {
    if (!checkoutCapacityReservedOpen || !ev?.id || isGuest) return undefined;

    let cancelled = false;
    const syncCapacity = async () => {
      const statusData = await loadWaitlistStatus({ silent: true });
      await refreshDetailsAndNotify();
      if (cancelled || !statusData) return;

      if (statusData?.is_in_waitlist) {
        setCheckoutCapacityReservedOpen(false);
        return;
      }

      const checkoutStillFull = Boolean(statusData?.checkout_capacity_full);
      const confirmedFull = Boolean(statusData?.is_full);
      if (!checkoutStillFull && !confirmedFull) {
        setCheckoutCapacityReservedOpen(false);
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t("events.details.waitlist.checkout_reserved_available"),
            },
          })
        );
      }
    };

    const intervalId = window.setInterval(() => {
      void syncCapacity();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    checkoutCapacityReservedOpen,
    ev?.id,
    isGuest,
    loadWaitlistStatus,
    refreshDetailsAndNotify,
    t,
  ]);

  useEffect(() => {
    if (!ev?.id || typeof window === "undefined") return undefined;

    const refreshCurrentEventState = () => {
      void refreshDetailsAndNotify();
      if (!isGuest) {
        void loadWaitlistStatus({ silent: true });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshCurrentEventState();
      }
    };

    window.addEventListener("focus", refreshCurrentEventState);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", refreshCurrentEventState);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [ev?.id, isGuest, loadWaitlistStatus, refreshDetailsAndNotify]);

  const doLeaveWaitlist = React.useCallback(async () => {
    if (!ev?.id) return;
    setWaitlistSubmitting(true);
    try {
      const { data } = await api.post(`/events/remove-from-waitlist/${ev.id}/`);
      const msg =
        data?.message || t("events.details.waitlist.removed_success");
      trackGAEvent("waitlist_leave_success", {
        event_id: String(ev.id),
        prepay_required: Boolean(waitlistStatus?.prepay_required),
      });
      window.dispatchEvent(
        new CustomEvent("global-info", { detail: { message: msg } })
      );
      await loadWaitlistStatus({ silent: true });
      await refreshDetailsAndNotify();
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("events.details.generic_error");
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg, status } })
      );
      await loadWaitlistStatus({ silent: true });
    } finally {
      setWaitlistSubmitting(false);
    }
  }, [ev?.id, loadWaitlistStatus, refreshDetailsAndNotify, t, waitlistStatus?.prepay_required]);

  const handleHostPromoteWaitlistUser = React.useCallback(
    async (targetUserId) => {
      if (!ev?.id || !targetUserId) return;
      setWaitlistPromotingUserId(targetUserId);
      try {
        const { data } = await api.post(`/events/waitlist/promote/${ev.id}/`, {
          user_id: targetUserId,
        });
        const msg =
          data?.message || t("events.details.waitlist.promote_success");
        window.dispatchEvent(
          new CustomEvent("global-info", { detail: { message: msg } })
        );
        await loadWaitlistStatus({ silent: true, markHostSeen: true });
        await refreshDetailsAndNotify();
        if (typeof waitlistUnreadRefetch === "function") {
          void waitlistUnreadRefetch();
        }
      } catch (err) {
        const status = err?.response?.status;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          t("events.details.generic_error");
        window.dispatchEvent(
          new CustomEvent("global-error", { detail: { message: msg, status } })
        );
      } finally {
        setWaitlistPromotingUserId(null);
      }
    },
    [ev?.id, loadWaitlistStatus, refreshDetailsAndNotify, t, waitlistUnreadRefetch]
  );

  const handleOpenWaitlist = React.useCallback(
    async (e) => {
      e?.stopPropagation?.();
      if (!ev?.id) return;

      if (isGuest) {
        redirectGuestToAuth();
        return;
      }

      setWaitlistMode("participant");
      setWaitlistOpenedFromAvailabilityAlert(false);
      setWaitlistCapacityMessage("");
      setIsWaitlistOpen(true);
      trackGAEvent("waitlist_modal_open", {
        event_id: String(ev.id),
        source: "event_details",
      });

      const statusData = await loadWaitlistStatus({ silent: true });
      if (!statusData) return;

      const alreadyInWaitlist = Boolean(statusData?.is_in_waitlist);
      if (!alreadyInWaitlist) {
        const blockCode = String(statusData?.join_block_code || "").trim();
        const participantRating = statusData?.participant_rating || null;
        if (openJoinLimitModalFromPayload(blockCode, participantRating)) {
          setIsWaitlistOpen(false);
          return;
        }
        const ok = await checkProfilePhoto("waitlist_join");
        if (!ok) {
          setIsWaitlistOpen(false);
          return;
        }
      }
    },
    [
      checkProfilePhoto,
      ev?.id,
      isGuest,
      loadWaitlistStatus,
      navigate,
      onRequireAuth,
      redirectGuestToAuth,
      openJoinLimitModalFromPayload,
    ]
  );

  useEffect(() => {
    if (!ev?.id || isGuest || typeof window === "undefined") return;

    const params = new URLSearchParams(location?.search || "");
    if (params.get("waitlist") !== "1") return;

    const requestedEventId = Number(params.get("event") || ev.id);
    if (Number.isFinite(requestedEventId) && requestedEventId !== Number(ev.id)) return;

    const deepLinkKey = `${ev.id}:${location?.search || ""}`;
    if (waitlistDeepLinkConsumedRef.current === deepLinkKey) return;
    waitlistDeepLinkConsumedRef.current = deepLinkKey;

    setWaitlistMode("participant");
    setWaitlistOpenedFromAvailabilityAlert(true);
    setWaitlistCapacityMessage("");
    setIsWaitlistOpen(true);
    trackGAEvent("waitlist_modal_open", {
      event_id: String(ev.id),
      source: "availability_notification",
    });

    void (async () => {
      const statusData = await loadWaitlistStatus({ silent: true });
      if (!statusData) return;
      trackGAEvent("waitlist_notification_open_result", {
        event_id: String(ev.id),
        result: statusData?.manual_join_available
          ? "spot_available"
          : statusData?.is_in_waitlist
          ? "spot_already_taken"
          : "not_waiting",
        prepay_required: Boolean(statusData?.prepay_required),
      });
    })();

    params.delete("waitlist");
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      {
        replace: true,
        state: location.state,
      }
    );
  }, [ev?.id, isGuest, loadWaitlistStatus, location.pathname, location.search, location.state, navigate]);

  const handleJoinWaitlist = React.useCallback(async () => {
    if (!ev?.id) return;
    if (isGuest) {
      redirectGuestToAuth();
      return;
    }

    if (!(await confirmAudience())) return;
    const ok = await checkProfilePhoto("waitlist_join");
    if (!ok) return;
    await doJoinWaitlist();
  }, [checkProfilePhoto, confirmAudience, doJoinWaitlist, ev?.id, isGuest, navigate, onRequireAuth, redirectGuestToAuth]);

  const handleOpenLeaveConfirm = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (leaveRequiresTicketCancellation) {
      setIsLeaveConfirmOpen(false);
      openParticipantTicketPanel("cancellation");
      return;
    }
    setIsLeaveConfirmOpen(true);

    if (isConcept || !ev?.id) return;

    setLeavePreviewLoading(true);
    try {
      const { data } = await api.get(`/events/leave-event-preview/${ev.id}/`);
      if (data?.success) {
        setLeavePreview(data);
      } else {
        setLeavePreview(null);
      }
    } catch {
      // Keep modal usable with static fallback text if preview endpoint fails.
      setLeavePreview(null);
    } finally {
      setLeavePreviewLoading(false);
    }
  }, [ev?.id, isConcept, leaveRequiresTicketCancellation, openParticipantTicketPanel]);

  const handleLeaveEvent = async (e) => {
    e.stopPropagation();
    if (!ev?.id) return;
    try {
      const res = await api.post(`/events/leave-event/${ev.id}/`);
      if (res.data?.success) {
        const leftCode = String(
          res?.data?.storage_code || userStorageCode || ""
        )
          .trim()
          .toUpperCase();
        setLocalEvent(prev => ({
          ...prev,
          is_participant: false,
          participant_action: null,
          participants_count: Math.max(0, getParticipantCount(prev) - 1),
          participants: Array.isArray(prev?.participants)
            ? prev.participants.filter((p) => {
                const code = String(p?.code || "").trim().toUpperCase();
                return !leftCode || code !== leftCode;
              })
            : [],
          co_hosts: Array.isArray(prev?.co_hosts)
            ? prev.co_hosts.filter((p) => {
                const code = String(p?.code || "").trim().toUpperCase();
                return !leftCode || code !== leftCode;
              })
            : [],
        }));
        setIsLeaveConfirmOpen(false);
        window.dispatchEvent(new CustomEvent("global-info", {
          detail: { message: t('events.details.left_event') }
        }));
        void refreshDetailsAndNotify();
        if (res.data?.penalty_applied) {
          window.dispatchEvent(new CustomEvent("vg-participant-rating-refresh"));
          if (typeof onClose === "function") {
            onClose();
          }
          if (location?.pathname !== "/events/participant-rating") {
            navigate("/events/participant-rating");
          }
        }
      } else {
        throw new Error(res.data.error || t('events.details.leave_failed'));
      }
    } catch (err) {
      const status = err.response?.status;
      const errorCode = String(err?.response?.data?.code || "").trim();
      if (errorCode === "ticket_cancellation_required") {
        if (err?.response?.data?.participant_ticketing) {
          setLocalEvent((prev) =>
            prev
              ? {
                  ...prev,
                  participant_ticketing: err.response.data.participant_ticketing,
                }
              : prev
          );
        }
        setIsLeaveConfirmOpen(false);
        openParticipantTicketPanel("cancellation");
        return;
      }
      const msg = err.response?.data?.error || err.message;
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: { message: msg, status }
      }));
    }
  };

  const handleJoinEvent = async (e) => {
    e.stopPropagation();
    if (!ev?.id) return;
    if (!isGuest) trackNewcomerAction(ev.id, "join_click");

    if (isGuest) {
      redirectGuestToAuth();
      return;
    }

    if (ev?.is_hostless_concept) {
      setIsHostClaimConfirmOpen(true);
      return;
    }

    if (!(await confirmAudience())) return;

    // Keep the visible CTA as Join event while the public counter still has
    // room.  On click, check the viewer's actual capacity segment and show the
    // waitlist choice instead of rules or payment when that segment is full.
    const statusData = await loadWaitlistStatus({ silent: true });
    const capacityBlockCode = String(statusData?.capacity_block_code || "").trim();
    if (
      statusData?.can_join_waitlist &&
      (WAITLIST_CAPACITY_BLOCK_CODES.has(capacityBlockCode) ||
        capacityBlockCode === "checkout_capacity_reserved")
    ) {
      setWaitlistCapacityMessage(
        statusData?.capacity_block_error || t("events.details.join_failed")
      );
      setWaitlistMode("participant");
      setWaitlistOpenedFromAvailabilityAlert(false);
      setIsWaitlistOpen(true);
      trackGAEvent("waitlist_modal_open", {
        event_id: String(ev.id),
        source: "join_capacity_precheck",
        capacity_block_code: capacityBlockCode,
      });
      return;
    }

    // Check if user has a proper profile photo before joining
    const ok = await checkProfilePhoto("join");
    if (!ok) {
      // PhotoRequestModal will be opened by checkProfilePhoto; join will be continued from the modal callbacks
      return;
    }

    await continueJoinFlow();
  };

  useEffect(() => {
    if (!forceOpenJoinFlow || !ev?.id) {
      forceJoinConsumedRef.current = null;
      return;
    }

    const key = `${ev.id}:join`;
    if (forceJoinConsumedRef.current === key) return;
    forceJoinConsumedRef.current = key;
    onForceJoinConsumed();

    void handleJoinEvent({
      stopPropagation() {},
      preventDefault() {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ev?.id, forceOpenJoinFlow, onForceJoinConsumed]);

  const handleManualJoinFromWaitlist = React.useCallback(async () => {
    if (!ev?.id || isGuest) return;

    if (ev?.is_hostless_concept) {
      setIsHostClaimConfirmOpen(true);
      return;
    }

    const ok = await checkProfilePhoto("join");
    if (!ok) return;

    trackGAEvent("waitlist_spot_claim_click", {
      event_id: String(ev.id),
      prepay_required: Boolean(waitlistStatus?.prepay_required),
      source: waitlistOpenedFromAvailabilityAlert
        ? "availability_notification"
        : "event_details",
    });
    setWaitlistSubmitting(true);
    try {
      const joined = await continueJoinFlow();
      if (!joined) {
        await loadWaitlistStatus({ silent: true });
        return;
      }

      setIsWaitlistOpen(false);
      setWaitlistMode("participant");
      setWaitlistPromotingUserId(null);
      setWaitlistOpenedFromAvailabilityAlert(false);
      await loadWaitlistStatus({ silent: true });
    } finally {
      setWaitlistSubmitting(false);
    }
  }, [
    checkProfilePhoto,
    continueJoinFlow,
    ev?.id,
    ev?.is_hostless_concept,
    isGuest,
    loadWaitlistStatus,
    waitlistOpenedFromAvailabilityAlert,
    waitlistStatus?.prepay_required,
  ]);

  const handleDeleteEvent = async (e) => {
    e?.stopPropagation?.();
    if (!ev?.id) return;
    try {
      const res = await api.post(`/events/delete-event/${ev.id}/`);
      if (res.data?.success) {
        window.dispatchEvent(new CustomEvent("global-info", {
          detail: { message: t('events.details.deleted_success') }
        }));
        setLocalEvent(prev => ({
          ...prev,
          status: "deleted"
        }));
      } else {
        throw new Error(res.data.error || t('events.details.deleted_failed'));
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.message;
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: { message: msg, status }
      }));
    }
  };

  const handleConfirmHosting = async (e) => {
    e?.stopPropagation?.();
    if (!ev?.id) return;
    try {
      const { data } = await api.post(`/events/host/confirm/${ev.id}/`);
      setLocalEvent((prev) => ({
        ...prev,
        host_confirmation_status: data?.host_confirmation_status || "confirmed",
        host_confirmed_at: data?.host_confirmed_at || new Date().toISOString(),
      }));
      window.dispatchEvent(new CustomEvent("global-info", {
        detail: { message: t("events.details.host_confirmed_success") }
      }));
      setIsHostConfirmOpen(false);
      setIsHostCancelConfirmOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error || t("events.details.host_confirm_failed");
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: { message: msg, status }
      }));
    }
  };

  const handleOpenHostConfirmModal = (e) => {
    e?.stopPropagation?.();
    setIsHostConfirmOpen(true);
  };

  const handleBoosted = React.useCallback(
    (payload = {}) => {
      setLocalEvent((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          promoted: payload.promoted ?? true,
          is_promoted_active: payload.is_promoted_active ?? true,
          promoted_rank: payload.promoted_rank ?? prev.promoted_rank,
          promoted_until: payload.promoted_until ?? prev.promoted_until,
          boost_diamond_cost: payload.diamond_cost ?? prev.boost_diamond_cost,
          boost_duration_days: payload.duration_days ?? prev.boost_duration_days,
          boost_duration_options:
            payload.duration_options ?? prev.boost_duration_options,
          diamonds_balance: payload.diamonds_balance ?? prev.diamonds_balance,
          boost_premium_free_available:
            payload.premium_free_available ?? prev.boost_premium_free_available,
          boost_premium_free_used:
            payload.premium_free_used ?? prev.boost_premium_free_used,
          boost_premium_free_duration_days:
            payload.premium_free_duration_days ?? prev.boost_premium_free_duration_days,
        };
      });
      void refreshDetailsAndNotify();
    },
    [refreshDetailsAndNotify]
  );

  const handleReportEvent = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!ev?.id) return;
    setIsReportOpen(true);
  };

  const handleReportSubmit = async (message) => {
    if (!ev?.id) return;
    try {
      await api.post(
        "/events/report-event/",
        {
          event_id: ev.id,
          description: message,
        },
        {
          headers: {
            "X-CSRFToken": window.getCookie?.("csrftoken"),
          },
        }
      );

      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: t("events.details.report_sent", {
              defaultValue: "Report sent successfully",
            }),
          },
        })
      );
      setIsReportOpen(false);
    } catch (error) {
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: {
            status: error?.response?.status,
            message:
              error?.response?.data?.error ||
              t("events.details.report_failed", {
                defaultValue: "Failed to send report",
              }),
          },
        })
      );
      throw error;
    }
  };

  const normalizeCode = React.useCallback(
    (value) => String(value || "").trim().toUpperCase(),
    []
  );

  const pendingCoHostRequests = React.useMemo(
    () => (Array.isArray(ev?.pending_cohost_requests) ? ev.pending_cohost_requests : []),
    [ev?.pending_cohost_requests]
  );
  const pendingHostTransferRequests = React.useMemo(
    () => (
      Array.isArray(ev?.pending_host_transfer_requests)
        ? ev.pending_host_transfer_requests
        : []
    ),
    [ev?.pending_host_transfer_requests]
  );

  const maxCoHostSlots = Number(ev?.max_co_hosts || 10) || 10;
  const usedCoHostSlots = Number(ev?.cohost_slots_used || 0) || 0;

  const buildHostRoleCandidates = React.useCallback(
    (waitlistEntries = [], options = {}) => {
      const includeWaitlist = options?.includeWaitlist !== false;
      const hostCode = normalizeCode(ev?.host?.code);
      const map = new Map();
      const pendingByUserId = new Map(
        pendingCoHostRequests
          .map((item) => [Number(item?.user_id || 0), item])
          .filter(([userId]) => userId > 0)
      );

      for (const participant of Array.isArray(ev?.participants) ? ev.participants : []) {
        const code = normalizeCode(participant?.code);
        const userId = Number(
          participant?.user_id || participant?.id || participant?.user?.id || 0
        );
        if (!code || !userId || code === hostCode) continue;
        if (participant?.event_organizer_blocked) continue;
        map.set(userId, {
          user_id: userId,
          code,
          avatar: participant?.avatar || "",
          avatar_custom_url: participant?.avatar_custom_url || null,
          source: "participant",
          is_co_host: Boolean(participant?.is_co_host),
          has_plus_access: Boolean(participant?.has_plus_access),
          has_premium_subscription: Boolean(participant?.has_premium_subscription),
          subscription_tier: String(participant?.subscription_tier || "free").toLowerCase(),
          pending_request_notification_id: Number(
            pendingByUserId.get(userId)?.notification_id || 0
          ) || null,
        });
      }

      if (includeWaitlist) {
        for (const entry of Array.isArray(waitlistEntries) ? waitlistEntries : []) {
          const code = normalizeCode(entry?.code);
          const userId = Number(entry?.user_id || 0);
          if (!code || !userId || code === hostCode) continue;
          if (entry?.event_organizer_blocked) continue;
          const current = map.get(userId);
          if (current) {
            map.set(userId, {
              ...current,
              source: "participant_waitlist",
              pending_request_notification_id:
                current.pending_request_notification_id ||
                Number(pendingByUserId.get(userId)?.notification_id || 0) ||
                null,
            });
            continue;
          }
          map.set(userId, {
            user_id: userId,
            code,
            avatar: entry?.avatar || "",
            avatar_custom_url: entry?.avatar_custom_url || null,
            source: "waitlist",
            is_co_host: false,
            has_plus_access: Boolean(entry?.has_plus_access),
            has_premium_subscription: Boolean(entry?.has_premium_subscription),
            subscription_tier: String(entry?.subscription_tier || "free").toLowerCase(),
            pending_request_notification_id: Number(
              pendingByUserId.get(userId)?.notification_id || 0
            ) || null,
          });
        }
      }

      return Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code));
    },
    [ev?.host?.code, ev?.participants, normalizeCode, pendingCoHostRequests]
  );

  const getHostTransferPolicyPreview = React.useCallback(
    (candidate) => getEventHostTransferPolicyPreview(ev, candidate),
    [ev]
  );

  const renderHostTransferConfirmation = React.useCallback(
    (candidate) => {
      const preview = getHostTransferPolicyPreview(candidate);
      const tierLabel = (tier) => t(
        `events.details.host_role_modal.tiers.${tier}`,
        { defaultValue: String(tier || "free") }
      );
      const scenarioKey = preview.isDowngrade
        ? "downgrade_explanation"
        : preview.recipientIsHigherTier
          ? "higher_recipient_explanation"
          : "same_tier_explanation";

      return (
        <div className="space-y-4">
          <div className="rounded-[18px] border border-white/15 bg-white/[0.07] px-4 py-4 text-sm leading-6 text-white/85">
            <div className="flex items-center justify-between gap-4">
              <span className="text-white/60">
                {t("events.details.host_role_modal.current_event_tier")}
              </span>
              <strong>{tierLabel(preview.sourceTier)}</strong>
            </div>
            <div className="mt-2 flex items-center justify-between gap-4">
              <span className="text-white/60">
                {t("events.details.host_role_modal.recipient_tier")}
              </span>
              <strong>{tierLabel(preview.targetTier)}</strong>
            </div>
            <div className="mt-2 flex items-center justify-between gap-4">
              <span className="text-white/60">
                {t("events.details.host_role_modal.resulting_event_tier")}
              </span>
              <strong>{tierLabel(preview.effectiveTier)}</strong>
            </div>
          </div>

          <div
            className={`rounded-[18px] border px-4 py-3 text-sm font-medium leading-6 ${
              preview.blocked
                ? "border-red-300/30 bg-red-400/10 text-red-50"
                : "border-amber-300/25 bg-amber-300/10 text-amber-50/90"
            }`}
          >
            {preview.blocked
              ? t("events.details.host_role_modal.capacity_blocked", {
                  occupied: preview.occupiedPlaces,
                  capacity: preview.capacityLimit,
                })
              : t(`events.details.host_role_modal.${scenarioKey}`, {
                  tier: tierLabel(preview.effectiveTier),
                  recipientTier: tierLabel(preview.targetTier),
                  capacity: preview.capacityLimit,
                })}
          </div>
        </div>
      );
    },
    [getHostTransferPolicyPreview, t]
  );

  const isHostTransferConfirmationBlocked = React.useCallback(
    (candidate) => getHostTransferPolicyPreview(candidate).blocked,
    [getHostTransferPolicyPreview]
  );

  const openHostRoleModal = React.useCallback(
    async (kind, options = {}) => {
      const shouldOpenModal = options?.openModal !== false;
      const includeWaitlist = kind !== "co_host_invite";
      const statusData = includeWaitlist
        ? await loadWaitlistStatus({ silent: true, markHostSeen: false })
        : null;
      const candidates = buildHostRoleCandidates(statusData?.entries || [], { includeWaitlist });
      const hasPendingRequests = (
        kind === "host_transfer"
          ? pendingHostTransferRequests.length
          : pendingCoHostRequests.length
      ) > 0;
      if (!candidates.length && !hasPendingRequests) {
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t("events.details.host_role_modal.no_candidates"),
            },
          })
        );
        return false;
      }
      setHostRoleCandidates(candidates);
      if (shouldOpenModal) {
        if (kind === "host_transfer") {
          setIsHostTransferModalOpen(true);
        } else {
          setIsCoHostModalOpen(true);
        }
      }
      return true;
    },
    [
      buildHostRoleCandidates,
      loadWaitlistStatus,
      pendingCoHostRequests.length,
      pendingHostTransferRequests.length,
      t,
    ]
  );

  const handleSendHostRoleRequest = React.useCallback(
    async (kind, selectedUserIds = []) => {
      const isCurrentUserHost =
        Boolean(ev?.is_host) ||
        normalizeCode(ev?.host?.code) === normalizeCode(userStorageCode);
      if (!ev?.id || !isCurrentUserHost) return;

      const targetUserIds = (Array.isArray(selectedUserIds) ? selectedUserIds : [])
        .map((value) => Number(value || 0))
        .filter((value, index, list) => value > 0 && list.indexOf(value) === index);
      if (!targetUserIds.length) return;

      // Hosting transfer is intentionally single-target. Co-host invitations
      // are sent sequentially so every request observes the updated shared
      // slot count and cannot race past the server-side limit.
      const requestTargetIds = kind === "co_host_invite" ? targetUserIds : targetUserIds.slice(0, 1);

      setCoHostSubmitting(true);
      try {
        const responses = [];
        for (const targetUserId of requestTargetIds) {
          const response = await api.post(`/events/${ev.id}/host-role-request/`, {
            kind,
            target_user_id: targetUserId,
          });
          responses.push(response?.data || {});
        }
        setIsCoHostModalOpen(false);
        setIsHostTransferModalOpen(false);
        requestEventToolsClose();
        await refreshDetails();

        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message:
                responses.length === 1
                  ? responses[0]?.message || t("events.details.host_role_modal.request_sent")
                  : t("events.details.host_role_modal.requests_sent", {
                      count: responses.length,
                      defaultValue: `${responses.length} requests sent.`,
                    }),
            },
          })
        );
      } catch (err) {
        const status = err?.response?.status;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          t("events.details.generic_error");
        window.dispatchEvent(
          new CustomEvent("global-error", { detail: { message: msg, status } })
        );
      } finally {
        setCoHostSubmitting(false);
      }
    },
    [ev?.host?.code, ev?.id, ev?.is_host, normalizeCode, refreshDetails, requestEventToolsClose, t, userStorageCode]
  );

  const handleCancelHostRoleRequest = React.useCallback(
    async (requestId, requestType = "notification") => {
      const id = Number(requestId || 0);
      if (!ev?.id || !id) return;

      setPendingCoHostCancelIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      try {
        const { data } = await api.post(
          `/events/${ev.id}/host-role-request/cancel/`,
          requestType === "transfer"
            ? { transfer_request_id: id }
            : { notification_id: id }
        );
        await refreshDetails();
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message:
                data?.status === "cancelled"
                  ? t("events.details.host_role_modal.request_cancelled")
                  : t("events.details.host_role_modal.request_already_processed"),
            },
          })
        );
      } catch (err) {
        const status = err?.response?.status;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          t("events.details.generic_error");
        window.dispatchEvent(
          new CustomEvent("global-error", { detail: { message: msg, status } })
        );
      } finally {
        setPendingCoHostCancelIds((prev) => prev.filter((value) => value !== id));
      }
    },
    [ev?.id, refreshDetails, t]
  );

  const handleRemoveCoHosts = React.useCallback(
    async (selectedUserIds = []) => {
      const isCurrentUserHost =
        Boolean(ev?.is_host) ||
        normalizeCode(ev?.host?.code) === normalizeCode(userStorageCode);
      if (!ev?.id || !isCurrentUserHost) return;

      const selectedSet = new Set(
        (Array.isArray(selectedUserIds) ? selectedUserIds : [])
          .map((value) => Number(value || 0))
          .filter((value) => value > 0)
      );
      if (!selectedSet.size) return;

      const remainingCodes = (Array.isArray(ev?.co_hosts) ? ev.co_hosts : [])
        .filter((coHost) => !selectedSet.has(Number(coHost?.user_id || coHost?.id || 0)))
        .map((coHost) => normalizeCode(coHost?.code))
        .filter(Boolean);

      setCoHostSubmitting(true);
      try {
        await api.post(`/events/${ev.id}/co-hosts/`, { codes: remainingCodes });
        setIsCoHostModalOpen(false);
        requestEventToolsClose();
        await refreshDetails();
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t("events.details.host_role_modal.cohosts_removed"),
            },
          })
        );
      } catch (err) {
        const status = err?.response?.status;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          t("events.details.generic_error");
        window.dispatchEvent(
          new CustomEvent("global-error", { detail: { message: msg, status } })
        );
      } finally {
        setCoHostSubmitting(false);
      }
    },
    [
      ev?.co_hosts,
      ev?.host?.code,
      ev?.id,
      ev?.is_host,
      normalizeCode,
      refreshDetails,
      requestEventToolsClose,
      t,
      userStorageCode,
    ]
  );

  const loadEventHostAnalytics = React.useCallback(async () => {
    if (!ev?.id) return;
    setHostAnalyticsLoading(true);
    setHostAnalyticsError("");
    try {
      const [summaryRes, timeseriesRes] = await Promise.all([
        api.get(`/events/${ev.id}/host-analytics/summary/`, {
          meta: { suppressGlobalError: true },
          suppressUnauthorizedRedirect: true,
        }),
        api.get(`/events/${ev.id}/host-analytics/timeseries/`, {
          params: { days: 120 },
          meta: { suppressGlobalError: true },
          suppressUnauthorizedRedirect: true,
        }),
      ]);
      setHostAnalyticsSummary(summaryRes?.data || null);
      setHostAnalyticsRows(Array.isArray(timeseriesRes?.data?.rows) ? timeseriesRes.data.rows : []);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || t("events.details.generic_error");
      setHostAnalyticsError(message);
    } finally {
      setHostAnalyticsLoading(false);
    }
  }, [ev?.id, t]);

  const isHost = Boolean(ev?.is_host) || !!(ev?.host && ev.host.code === userStorageCode);

  const openConceptDatePollEditor = React.useCallback(() => {
    if (!ev?.id) return;
    requestEventToolsClose();
    setIsConceptDatePollModalOpen(true);
  }, [ev?.id, requestEventToolsClose]);

  const handleHostMoreAction = React.useCallback(
    async (actionId) => {
      if (!actionId) return;

      switch (actionId) {
        case "boost_event": {
          setIsBoostModalOpen(true);
          return;
        }
        case "delete_event": {
          setIsDeleteConfirmOpen(true);
          return;
        }
        case "remove_participant": {
          setIsRemoveParticipantModalOpen(true);
          return;
        }
        case "view_waitlist": {
          const statusData = await loadWaitlistStatus({ silent: true, markHostSeen: true });
          if (!statusData) {
            requestEventToolsOpen();
            return;
          }
          setWaitlistMode("host_readonly");
          setIsWaitlistOpen(true);
          return;
        }
        case "add_co_host": {
          const opened = await openHostRoleModal("co_host_invite");
          if (!opened) requestEventToolsOpen();
          return;
        }
        case "transfer_hosting": {
          const opened = await openHostRoleModal("host_transfer");
          if (!opened) requestEventToolsOpen();
          return;
        }
        case "send_intention_request": {
          setIsHostIntentionModalOpen(true);
          return;
        }
        case "concept_date_poll": {
          openConceptDatePollEditor();
          return;
        }
        case "review_participant_requests": {
          setIsReviewRequestsModalOpen(true);
          return;
        }
        case "open_analytics": {
          setIsHostAnalyticsModalOpen(true);
          void loadEventHostAnalytics();
          return;
        }
        default: {
          window.dispatchEvent(
            new CustomEvent("global-info", {
              detail: {
                message: t("events.details.more_actions.action_coming_soon"),
              },
            })
          );
        }
      }
    },
    [loadEventHostAnalytics, loadWaitlistStatus, openConceptDatePollEditor, openHostRoleModal, requestEventToolsOpen, t]
  );

  useEffect(() => {
    if (!ev?.id || !isHost) return;
    const sp = new URLSearchParams(location.search || "");
    const hostAction = sp.get("host_action");
    if (hostAction !== "requests" && hostAction !== "waitlist") return;

    const openFromQuery = async () => {
      if (hostAction === "requests") {
        setIsReviewRequestsModalOpen(true);
      } else {
        const statusData = await loadWaitlistStatus({ silent: true, markHostSeen: true });
        if (!statusData) return;
        setWaitlistMode("host_readonly");
        setIsWaitlistOpen(true);
      }

      sp.delete("host_action");
      navigate(
        {
          pathname: location.pathname,
          search: sp.toString() ? `?${sp.toString()}` : "",
        },
        { replace: true }
      );
    };

    void openFromQuery();
  }, [ev?.id, isHost, loadWaitlistStatus, location.pathname, location.search, navigate]);

  const isParticipant =
    (Array.isArray(ev?.participants) &&
      ev.participants.some((p) => p.code === userStorageCode)) ||
    Boolean(ev?.is_participant);
  const attendanceWindowOpen = useEventAttendanceWindow(ev?.date_time);

  useEffect(() => {
    if (!ev?.id || !isParticipant) return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/events/intent/confirm/${ev.id}/`);
        if (mounted && data?.intent_status) setIntentStatus(data.intent_status);
      } catch {}
    })();
    return () => { mounted = false; };
  }, [ev?.id, isParticipant]);

  useEffect(() => {
    if (!ev?.id || !isParticipant) {
      setPrimaryAction(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get(
          `/events/${ev.id}/participant-action/`,
          {
            authOptional: true,
            suppressUnauthorizedRedirect: true,
          }
        );
        if (cancelled) return;
        const action = data?.primary_action || data?.action || null;
        setPrimaryAction(action);
      } catch (err) {
        const status = err?.response?.status;
        const code = err?.response?.data?.code;
        if (status === 403 && code === "not_registered") {
          if (!cancelled) setPrimaryAction(null);
          return;
        }

        console.error("[EventDetailsFullscreen] participant-action failed", err);
        if (!cancelled) setPrimaryAction(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ev?.id, isParticipant, intentStatus, primaryActionVersion]);

  const waitlistUnreadByEventId = waitlistUnreadCtx?.byEventId || {};
  const waitlistUnreadKey = ev?.id != null ? String(ev.id) : "";
  const hasWaitlistUnreadInMap = Boolean(
    waitlistUnreadKey &&
      Object.prototype.hasOwnProperty.call(waitlistUnreadByEventId, waitlistUnreadKey)
  );
  const waitlistUnreadCount = isHost
    ? (
        hasWaitlistUnreadInMap
          ? Number(waitlistUnreadByEventId[waitlistUnreadKey] || 0)
          : Number(ev?.waitlist_unread_count || 0)
      ) || 0
    : 0;
  const participantRequestUnreadByEventId = participantRequestUnreadCtx?.byEventId || {};
  const hasParticipantRequestUnreadInMap = Boolean(
    waitlistUnreadKey &&
      Object.prototype.hasOwnProperty.call(participantRequestUnreadByEventId, waitlistUnreadKey)
  );
  const participantRequestsPendingCount = isHost
    ? (
        hasParticipantRequestUnreadInMap
          ? Number(participantRequestUnreadByEventId[waitlistUnreadKey] || 0)
          : Number(ev?.participant_requests_pending_count || 0)
      ) || 0
    : 0;
  const hostActionUnreadCount = waitlistUnreadCount + participantRequestsPendingCount;

  useEffect(() => {
    if (!ev?.id) return;
    const eventIsOpenForQueue =
      ev?.status === "active" || ev?.status === "postponed";

    // Hosts are also participants in many events. Do not clear host waitlist data
    // in that case, otherwise entries flash and then disappear.
    if (isGuest || (!isHost && isParticipant) || !eventIsOpenForQueue) {
      setWaitlistStatus(null);
      return;
    }

    // Host waitlist is opened on-demand from Event tools.
    // Keep loaded host data while modal is open to avoid flash->empty
    // when unread counters refetch and this effect re-runs.
    if (isHost) {
      if (!isWaitlistOpen) {
        setWaitlistStatus(null);
      }
      return;
    }

    void loadWaitlistStatus({ silent: true });
  }, [ev?.id, ev?.status, isGuest, isHost, isParticipant, isWaitlistOpen, loadWaitlistStatus]);

  const hostList = React.useMemo(() => {
    const mainHost = ev?.host ? [ev.host] : [];
    const coHosts = Array.isArray(ev?.co_hosts) ? ev.co_hosts : [];
    const participants = Array.isArray(ev?.participants) ? ev.participants : [];
    const premiumByCode = new Map(
      participants
        .filter((p) => p?.code)
        .map((p) => [String(p.code).trim().toUpperCase(), Boolean(p?.has_premium_subscription)])
    );
    const seen = new Set();
    const userHosts = [];

    for (const host of [...mainHost, ...coHosts].filter(Boolean)) {
      const normalizedCode = String(host?.code || "").trim().toUpperCase();
      if (!normalizedCode || seen.has(normalizedCode)) continue;
      seen.add(normalizedCode);
      userHosts.push({
        id: `user:${normalizedCode}`,
        type: "user",
        ...host,
        label: host?.code || "",
        has_premium_subscription:
          host?.has_premium_subscription ?? premiumByCode.get(normalizedCode) ?? false,
      });
    }

    if (ev?.is_platform_event || ev?.is_professional_meetup) {
      return [{ id: "platform:vaultgrid", type: "platform", label: "VaultGrid Team", name: "VaultGrid Team", imageUrl: "/assets/event_pics/vaultgrid.webp", has_premium_subscription: false }, ...userHosts];
    }
    const club = ev?.club;
    if (club?.id) {
      const clubHost = {
        id: `club:${club.id}`,
        type: "club",
        clubId: club.id,
        name: club.name || "",
        label: club.name || "",
        imageUrl: club.image_url || club.hero_image_url || "",
        publicPath: club.public_path || buildClubPath(club.id, club.name || ""),
        has_premium_subscription: false,
      };

      return [clubHost, ...userHosts];
    }

    return userHosts;
  }, [ev?.club, ev?.co_hosts, ev?.host, ev?.is_platform_event, ev?.is_professional_meetup, ev?.participants]);

  const multipleHosts = hostList.length > 1;

  const hasConfirmedAttendance = isParticipant &&
    ev.participants.some(p => p.code === userStorageCode && p.attended);

  const participantCount = getParticipantCount(ev);
  const isFull = (ev?.max_participants > 0) && (participantCount >= ev.max_participants);
  const isInWaitlist = Boolean(waitlistStatus?.is_in_waitlist);
  const canJoinWaitlist = Boolean(waitlistStatus?.can_join_waitlist);
  const manualJoinAvailable = Boolean(waitlistStatus?.manual_join_available);
  const waitlistCtaVisible = !isParticipant && !isHost && (
    isFull || isInWaitlist || canJoinWaitlist
  );
  const waitlistCtaLabel = manualJoinAvailable && isInWaitlist
    ? t("events.details.waitlist.join_now")
    : isInWaitlist
    ? t(
        waitlistStatus?.prepay_required
          ? "events.details.waitlist.waiting_for_spot"
          : "events.details.waitlist.view_waitlist"
      )
    : t("events.details.waitlist.join_waitlist");

  const reserveUI = React.useMemo(
    () => (ev ? computeReserveUI(ev) : {
      lockActive: false,
      reserved: 0,
      lockedRemaining: 0,
      availableGeneral: 0,
      unlockAt: null,
      userOldOrBlocked: false,
      canJoinNow: false,
    }),
    [ev]
  );
  const joinDisabledByReserve = !reserveUI.canJoinNow;
  const unlockText = reserveUI.unlockAt ? fmtUnlock(reserveUI.unlockAt) : null;
  const unlockBeforeLabel = (typeof ev?.unlock_time_hours === 'number' && ev.unlock_time_hours > 0)
    ? `${ev.unlock_time_hours} ${ev.unlock_time_hours === 1 ? 'hour' : 'hours'} before start`
    : null;

  const [unlockCountdownMs, setUnlockCountdownMs] = useState(null);
  const [unlockCountdownText, setUnlockCountdownText] = useState(null);

  useEffect(() => {
    if (joinDisabledByReserve && reserveUI.lockActive && reserveUI.unlockAt) {
      const calc = () => {
        const ms = Math.max(0, reserveUI.unlockAt.getTime() - Date.now());
        setUnlockCountdownMs(ms);
        setUnlockCountdownText(formatCountdown(ms));
      };
      calc();
      const id = setInterval(calc, 1000);
      return () => clearInterval(id);
    }
    setUnlockCountdownMs(null);
    setUnlockCountdownText(null);
    return undefined;
  }, [joinDisabledByReserve, reserveUI.lockActive, reserveUI.unlockAt?.getTime()]);

  const showIntentButton = (() => {
    if (!isParticipant) return false;
    if (ev.status === 'deleted' || ev.status === 'finished' || ev.status === 'pending') return false;
    if (attendanceWindowOpen) return false;

    if (primaryAction === "activation_date") return true;
    if (primaryAction === "slot") return true;
    if (primaryAction === "intent") return true;
    if (primaryAction === "attendance") return false;

    if (!ev?.date_time) return false;

    const startTs = new Date(ev.date_time).getTime();
    const now = Date.now();

    const t3d = startTs - 3 * 24 * 60 * 60 * 1000;
    const t2h = startTs - EVENT_ATTENDANCE_OPENS_BEFORE_MS;

    if (intentStatus === "confirmed") return false;

    return now >= t3d && now < t2h;
  })();
  const showAttendanceButton = Boolean(
    attendanceWindowOpen &&
      isParticipant &&
      !hasConfirmedAttendance &&
      !isConcept &&
      ev?.event_format !== 0
  );

  const confirmCtaLabel = primaryAction === "slot"
    ? t("required_actions.types.slot_confirmation.confirm_button")
    : primaryAction === "activation_date"
    ? t(
        ev?.activation_date_counts_as_intent
          ? "events.details.confirm_event_date_intent"
          : ev?.last_schedule_change_kind === "time"
          ? "events.details.confirm_event_time"
          : "events.details.confirm_event_date"
      )
    : showIntentButton
      ? t("events.details.confirm_intention")
      : t("events.details.confirm_attendance");

  let ticketPrice = null;
  if (effectiveTicketingPreview?.full_amount != null) ticketPrice = effectiveTicketingPreview.full_amount;
  if (ticketPrice == null && ev?.payment_category_1 != null) ticketPrice = ev.payment_category_1;
  if (ticketPrice == null && ev?.ticket_price != null) ticketPrice = ev.ticket_price;
  if (ticketPrice == null && ev?.price != null) ticketPrice = ev.price;
  if (typeof ticketPrice === 'string') {
    const parsed = parseFloat(ticketPrice);
    ticketPrice = Number.isFinite(parsed) ? parsed : null;
  }

  const isHostPaidCardEvent =
    (ev?.ticketing_flow_kind === "host_stripe") ||
    (ev?.ticketing_flow_kind !== "vaultgrid_revolut" &&
      ev?.pricing_model === "host_paid" &&
      ev?.host_payment_collection_method === "card");
  const joinPaymentPercentRaw = Number(ev?.host_paid_join_payment_percent);
  const joinPaymentPercent =
    Number.isFinite(joinPaymentPercentRaw) ? joinPaymentPercentRaw : 100;
  const joinCheckoutPrice =
    effectiveTicketingPreview?.join_checkout_amount != null
      ? Number(effectiveTicketingPreview.join_checkout_amount)
      : ticketPrice != null && isHostPaidCardEvent
      ? Number(((ticketPrice * Math.max(0, Math.min(100, joinPaymentPercent))) / 100).toFixed(2))
      : ticketPrice;
  const remainingTicketPrice =
    effectiveTicketingPreview?.remaining_scheduled_amount != null
      ? Number(effectiveTicketingPreview.remaining_scheduled_amount)
      : ticketPrice != null && isHostPaidCardEvent
      ? Number((ticketPrice - ((effectiveTicketingPreview?.join_amount != null ? Number(effectiveTicketingPreview.join_amount) : joinCheckoutPrice) || 0)).toFixed(2))
      : 0;

  const handleConfirmSuccess = async (confirmation = null) => {
    const updatedEvent = {
      ...ev,
      viewer_attended: true,
      participants: Array.isArray(ev?.participants)
        ? ev.participants.map(p =>
        p.code === userStorageCode
          ? { ...p, attended: true }
          : p
        )
        : [],
    };
    setLocalEvent(updatedEvent);
    onEventUpdate(updatedEvent);
    window.dispatchEvent(new CustomEvent("vg:event-attendance-confirmed", {
      detail: { eventId: ev?.id },
    }));

    const confirmationSessionPath = String(
      confirmation?.party_session_path || ""
    ).trim();
    const eventSupportsInteractiveSession = Boolean(
      ev?.is_vaultgrid_party ||
      ev?.is_new_joiners_event ||
      ev?.is_sport_session_event
    );
    const directSessionPath = confirmationSessionPath || (
      eventSupportsInteractiveSession
        ? `/events/${ev.id}/party-session`
        : ""
    );

    const scheduleSessionRedirect = (sessionPath) => {
      const normalizedPath = String(sessionPath || "").trim();
      if (
        !normalizedPath ||
        attendanceSessionRedirectEventRef.current === String(ev?.id)
      ) {
        return false;
      }
      attendanceSessionRedirectEventRef.current = String(ev?.id);

      if (attendanceSessionRedirectTimerRef.current) {
        window.clearTimeout(attendanceSessionRedirectTimerRef.current);
      }
      attendanceSessionRedirectTimerRef.current = window.setTimeout(() => {
        attendanceSessionRedirectTimerRef.current = null;
        setIsConfirmOpen(false);
        setAttendanceCashQrMode(false);
        navigate(normalizedPath);
      }, ATTENDANCE_SESSION_AUTO_OPEN_DELAY_MS);
      return true;
    };

    // Geo confirmation already returns the canonical path. Schedule it before
    // refreshing required actions so a cached queue cannot block navigation.
    const redirectScheduled = scheduleSessionRedirect(directSessionPath);

    const nextActions = await refreshRequiredActions({
      silent: true,
      suppressSessionPromptForEventId: ev?.id,
    });
    const joinSessionAction = nextActions.find(
      (action) =>
        action?.type === "join_party_session"
        && Number(action?.event?.id) === Number(ev?.id)
    );
    if (!redirectScheduled && joinSessionAction) {
      scheduleSessionRedirect(
        joinSessionAction?.payload?.session_path
          || `/events/${ev.id}/party-session`
      );
    }
  };

  // When the host confirms attendance via QR scan, the participant receives a
  // real-time WebSocket notification. Update the event card state immediately
  // so the button turns green without requiring a page refresh.
  useEffect(() => {
    if (!ev?.id) return;
    const handler = (e) => {
      const meta = e.detail?.meta || {};
      if (
        String(meta.action) === "attendance_confirmed" &&
        String(meta.event_id) === String(ev.id)
      ) {
        setLocalEvent((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            viewer_attended: true,
            participants: Array.isArray(prev.participants)
              ? prev.participants.map((p) =>
                  p.code === userStorageCode ? { ...p, attended: true } : p
                )
              : prev.participants,
          };
        });
      }
    };
    window.addEventListener("vg:notification", handler);
    return () => window.removeEventListener("vg:notification", handler);
  }, [ev?.id, userStorageCode]);

  const handleIntentConfirmSuccess = (actionType = "intent", payload = null) => {
    if (actionType === "activation_date") {
      setLocalEvent(prev => ({
        ...(prev || {}),
        participants: Array.isArray(prev?.participants)
          ? prev.participants.map(p =>
          p.code === userStorageCode
            ? {
                ...p,
                activation_date_status: "confirmed",
                intent_confirmed:
                  payload?.intent_status === "confirmed"
                    ? true
                    : p.intent_confirmed,
                intent_status:
                  payload?.intent_status === "confirmed"
                    ? "confirmed"
                    : p.intent_status,
              }
            : p
          )
          : [],
        activation_date_counts_as_intent:
          typeof payload?.activation_date_counts_as_intent === "boolean"
            ? payload.activation_date_counts_as_intent
            : prev?.activation_date_counts_as_intent,
      }));
      if (payload?.intent_status === "confirmed") {
        setIntentStatus("confirmed");
      }
      setPrimaryAction("attendance");
      setPrimaryActionVersion((v) => v + 1);
      return;
    }
    if (actionType === "slot") {
      setLocalEvent(prev => ({
        ...(prev || {}),
        participants: Array.isArray(prev?.participants)
          ? prev.participants.map(p =>
              p.code === userStorageCode
                ? { ...p, slot_confirm_status: "confirmed" }
                : p
            )
          : [],
      }));
      setPrimaryAction("attendance");
      setPrimaryActionVersion((v) => v + 1);
      return;
    }
    setIntentStatus('confirmed');
    setLocalEvent(prev => ({
      ...prev,
      participants: Array.isArray(prev?.participants)
        ? prev.participants.map(p =>
            p.code === userStorageCode ? { ...p, intent_confirmed: true } : p
          )
        : [],
    }));
    setPrimaryActionVersion((v) => v + 1);
  };

  const handleIntentDeclineSuccess = (actionType = "intent") => {
    setLocalEvent(prev => ({
      ...prev,
      participants: Array.isArray(prev?.participants)
        ? prev.participants.filter(p => p.code !== userStorageCode)
        : [],
      participants_count: Math.max(0, getParticipantCount(prev) - 1),
    }));
    if (actionType === "activation_date" || actionType === "slot") {
      setPrimaryAction(null);
    }
    setPrimaryActionVersion((v) => v + 1);
  };

  const dt = ev?.date_time ? new Date(ev.date_time) : null;
  const hours = dt ? String(dt.getHours()).padStart(2, '0') : '00';
  const minutes = dt ? String(dt.getMinutes()).padStart(2, '0') : '00';
  const day = dt ? String(dt.getDate()).padStart(2, '0') : '00';
  const month = dt ? String(dt.getMonth() + 1).padStart(2, '0') : '00';
  const year = dt ? String(dt.getFullYear()).slice(-2) : '00';
  const formattedDate = dt ? `${hours}:${minutes} ${day}/${month}/${year}` : '';
  const cardDatetime = isConcept ? null : formattedDate;
  const googleCalendarUrl = ev ? buildGoogleCalendarUrl(ev, t) : null;

  // Payment flags
  // requiresPrepayGate: hard gate (must pay before joining) — stays tied to is_payable_in_advance only
  const requiresPrepayGate = Boolean(
    ev?.is_payable_in_advance || ev?.pay_in_advance || ev?.requires_prepayment
  );

  // paymentFlowEnabled: UI/payment flow can be shown for any paid ticket flow.
  const paymentFlowEnabled = Boolean(requiresPrepayGate || ev?.show_pay_button || isHostPaidCardEvent);

  const eventStartMs = dt ? dt.getTime() : null;
  const nowMs = Date.now();

  // T-3d prepay window should depend ONLY on the hard gate
  const prepayWindowT3d = !!(
    requiresPrepayGate &&
    eventStartMs &&
    nowMs >= eventStartMs - 3 * 24 * 60 * 60 * 1000 &&
    nowMs < eventStartMs
  );

  const ticketPaid = !!ev?.ticket_paid;

  // Show "Pay for ticket" button for participants when payment flow is enabled and ticket isn't paid
  const showPayButton = isParticipant && paymentFlowEnabled && !ticketPaid;

  const canAddToCalendar =
    !isConcept &&
    ev?.status === "active" &&
    !!googleCalendarUrl &&
    eventStartMs &&
    eventStartMs > nowMs &&
    isParticipant;

  const handleOpenCalendarPicker = useCallback((e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!canAddToCalendar) return;
    setIsCalendarPickerOpen(true);
  }, [canAddToCalendar]);

  const handleAddToGoogleCalendar = useCallback(async () => {
    setIsCalendarPickerOpen(false);
    if (googleCalendarUrl) {
      try {
        const opened = await openUrlPreferInstalledApp(googleCalendarUrl);
        if (!opened) await openExternalUrl(googleCalendarUrl);
      } catch {
        await openExternalUrl(googleCalendarUrl);
      }
    }
  }, [googleCalendarUrl]);

  const handleAddToAppleCalendar = useCallback(async () => {
    setIsCalendarPickerOpen(false);
    if (!ev) return;

    if (isNativeIOS()) {
      const eventData = getCalendarEventData(ev, t);
      if (eventData) {
        try {
          const presented = await presentNativeAppleCalendarEvent(eventData);
          if (presented) return;
        } catch {
          // Keep the browser-compatible ICS path as a last-resort fallback.
        }
      }
    }

    downloadAppleCalendarIcs(ev, t);
  }, [ev, t]);

  const openParticipantTicketPayment = useCallback(() => {
    // Legacy shared-cost events may not include participant_ticketing in the
    // details payload. The modal can fetch its pay URL, so it must still open.
    setServerPrepayRequired(true);
    setForceImmediatePayment(Boolean(participantTicketing?.balance_due_now));
    setServerPayUrl(participantTicketing?.pay_now_url || null);
    setServerTicketPaymentState(participantTicketing || null);
    setIntentVariant("payment");
    setPreJoinPaymentFlag(false);
    setTicketMode(true);
    void openIntentModal();
  }, [
    openIntentModal,
    participantTicketing,
    setForceImmediatePayment,
    setIntentVariant,
    setPreJoinPaymentFlag,
    setServerPayUrl,
    setServerPrepayRequired,
    setTicketMode,
  ]);

  const attendanceRequiresTicketPayment = Boolean(
    isParticipant && paymentFlowEnabled && !ticketPaid
  );

  const openAttendanceTicketPayment = useCallback(async () => {
    setIsConfirmOpen(false);
    setAttendanceCashQrMode(false);
    onConfirmClose?.();
    setServerPrepayRequired(true);
    setForceImmediatePayment(true);
    setServerPayUrl(participantTicketing?.pay_now_url || null);
    setServerTicketPaymentState(participantTicketing || null);
    setIntentVariant("payment");
    setPreJoinPaymentFlag(false);
    setTicketMode(true);
    await openIntentModal();
  }, [onConfirmClose, openIntentModal, participantTicketing]);

  const openAttendanceTicketQr = useCallback(() => {
    if (!attendanceWindowOpen) return;
    setServerPayUrl(null);
    setServerTicketPaymentState(null);
    setServerPrepayRequired(false);
    setForceImmediatePayment(false);
    setIsIntentOpen(false);
    setAttendanceCashQrMode(true);
    setIsConfirmOpen(true);
  }, [attendanceWindowOpen]);

  const openAttendanceFlow = useCallback(async ({ allowBeforeWindow = false } = {}) => {
    if (
      !ev?.id ||
      isConcept ||
      (!allowBeforeWindow && !attendanceWindowOpen)
    ) {
      return false;
    }
    if (isGuest) {
      redirectGuestToAuth();
      return false;
    }
    if (attendanceRequiresTicketPayment) {
      await openAttendanceTicketPayment();
      return false;
    }
    setAttendanceCashQrMode(false);
    setIsConfirmOpen(true);
    return true;
  }, [
    attendanceRequiresTicketPayment,
    attendanceWindowOpen,
    ev?.id,
    isConcept,
    isGuest,
    openAttendanceTicketPayment,
    redirectGuestToAuth,
  ]);

  const setIsConfirmOpenGuarded = useCallback(
    (nextOpen) => {
      if (!nextOpen) {
        setIsConfirmOpen(false);
        setAttendanceCashQrMode(false);
        return;
      }
      void openAttendanceFlow();
    },
    [openAttendanceFlow]
  );

  useEffect(() => {
    if (!forceOpenConfirmModal) return;
    setAttendanceOpenRequest((value) => value + 1);
  }, [forceOpenConfirmModal]);

  useEffect(() => {
    if (!attendanceOpenRequest) return;
    if (attendanceOpenHandledRef.current === attendanceOpenRequest) return;
    attendanceOpenHandledRef.current = attendanceOpenRequest;
    void openAttendanceFlow({ allowBeforeWindow: true });
  }, [attendanceOpenRequest, openAttendanceFlow]);

  const loadParticipantTicketCancellation = useCallback(async () => {
    if (!ev?.id) return;
    const loadSeq = ++ticketCancellationLoadSeqRef.current;
    setTicketCancellationLoading(true);
    try {
      const { data } = await api.get(`/events/${ev.id}/ticket-cancellation/quote/`);
      if (loadSeq !== ticketCancellationLoadSeqRef.current) return;
      setTicketCancellationQuote(data?.quote || null);
      if (data?.participant_ticketing) {
        setLocalEvent((prev) =>
          prev ? { ...prev, participant_ticketing: data.participant_ticketing } : prev
        );
      }
    } catch (err) {
      if (loadSeq !== ticketCancellationLoadSeqRef.current) return;
      const errorCode = String(err?.response?.data?.code || "").trim();
      if (
        errorCode === "ticket_cancellation_not_participant" &&
        ticketCancellationResolvedRef.current
      ) {
        setTicketCancellationQuote(null);
        return;
      }
      setTicketCancellationQuote(null);
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message:
              err?.response?.data?.error ||
              t("events.details.ticket_panel.cancellation_modal.load_failed"),
          },
        })
      );
    } finally {
      if (loadSeq === ticketCancellationLoadSeqRef.current) {
        setTicketCancellationLoading(false);
      }
    }
  }, [ev?.id, t]);

  const submitParticipantTicketCancellation = useCallback(
    async ({ phone, note }) => {
      if (!ev?.id) return false;
      ticketCancellationResolvedRef.current = false;
      setTicketCancellationSubmitting(true);
      const quoteSnapshot = ticketCancellationQuote;
      try {
        const { data } = await api.post(`/events/${ev.id}/ticket-cancellation/submit/`, {
          phone,
          note,
        });
        if (data?.participant_ticketing) {
          setLocalEvent((prev) =>
            prev ? { ...prev, participant_ticketing: data.participant_ticketing } : prev
          );
        }
        setTicketCancellationQuote((prev) => ({
          ...(prev || {}),
          existing_request: data?.request || prev?.existing_request || null,
          can_submit: false,
        }));
        const estimatedRefundAmount =
          data?.request?.estimated_refund_amount ??
          quoteSnapshot?.estimated_refund_amount ??
          null;
        setTicketCancellationOutcome({
          resolved: Boolean(data?.resolved),
          refundSubmitted: Boolean(data?.refund_submitted),
          estimatedRefundAmount,
          eventName: ev?.name || "",
          message: data?.message || "",
        });
        if (data?.resolved) {
          ticketCancellationResolvedRef.current = true;
          ticketCancellationLoadSeqRef.current += 1;
          setTicketCancellationLoading(false);
          setTicketCancellationQuote(null);
          removeViewerFromLocalEventAfterCancellation();
        }
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message:
                data?.message ||
                (data?.resolved
                  ? data?.refund_submitted
                    ? t("events.details.ticket_panel.cancellation_modal.processed_refund_message")
                    : t("events.details.ticket_panel.cancellation_modal.processed_no_refund_message")
                  : t("events.details.ticket_panel.cancellation_modal.success_message")),
            },
          })
        );
        void refreshDetails();
        return { ok: true };
      } catch (err) {
        const errorCode = String(err?.response?.data?.code || "").trim();
        const statusCode = Number(err?.response?.status || 0);
        const recoverResolvedCancellation = async () => {
          const fallbackRefundAmount = quoteSnapshot?.estimated_refund_amount ?? null;
          ticketCancellationResolvedRef.current = true;
          ticketCancellationLoadSeqRef.current += 1;
          setTicketCancellationLoading(false);
          setTicketCancellationQuote(null);
          setTicketCancellationOutcome({
            resolved: true,
            refundSubmitted: Number.parseFloat(fallbackRefundAmount || "0") > 0,
            estimatedRefundAmount: fallbackRefundAmount,
            eventName: ev?.name || "",
            message: "",
          });
          removeViewerFromLocalEventAfterCancellation();
          void refreshDetails();
          return { ok: true };
        };

        if (errorCode === "ticket_cancellation_not_participant") {
          return recoverResolvedCancellation();
        }

        if (statusCode >= 500) {
          try {
            await api.get(`/events/${ev.id}/ticket-cancellation/quote/`);
          } catch (quoteErr) {
            const quoteErrorCode = String(quoteErr?.response?.data?.code || "").trim();
            if (quoteErrorCode === "ticket_cancellation_not_participant") {
              return recoverResolvedCancellation();
            }
          }
        }

        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message:
                err?.response?.data?.error ||
                t("events.details.ticket_panel.cancellation_modal.submit_failed"),
            },
          })
        );
        return { ok: false };
      } finally {
        setTicketCancellationSubmitting(false);
      }
    },
    [
      ev?.id,
      ev?.name,
      refreshDetails,
      removeViewerFromLocalEventAfterCancellation,
      t,
      ticketCancellationQuote,
    ]
  );

  const submitParticipantTicketDispute = useCallback(
    async ({ phone, reason }) => {
      if (!ev?.id) return false;
      setTicketDisputeSubmitting(true);
      try {
        const { data } = await api.post(`/events/${ev.id}/ticket-dispute/submit/`, {
          phone,
          reason,
        });
        if (data?.participant_ticketing) {
          setLocalEvent((prev) =>
            prev ? { ...prev, participant_ticketing: data.participant_ticketing } : prev
          );
        }
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message: t("events.details.ticket_panel.dispute_modal.success_message"),
            },
          })
        );
        refreshDetails();
        return true;
      } catch (err) {
        window.dispatchEvent(
          new CustomEvent("global-info", {
            detail: {
              message:
                err?.response?.data?.error ||
                t("events.details.ticket_panel.dispute_modal.submit_failed"),
            },
          })
        );
        return false;
      } finally {
        setTicketDisputeSubmitting(false);
      }
    },
    [ev?.id, refreshDetails, t]
  );

  const vaultReturnTo = React.useMemo(() => {
    if (!ev?.id) return null;

    const pathname = String(location?.pathname || "");
    if (pathname !== "/events/search" && pathname !== "/events/map_search") {
      return null;
    }

    const params = new URLSearchParams();
    params.set("event", String(ev.id));
    params.set("participants", "1");

    return {
      kind: "event_participants",
      pathname,
      search: `?${params.toString()}`,
      state: pathname === "/events/map_search" ? { focusEventId: ev.id } : null,
    };
  }, [ev?.id, location?.pathname]);

  const directEventContext = React.useMemo(() => ev?.id ? {
    id: ev.id,
    name: ev.name || "",
    type: ev.type || "event",
    status: ev.status || "active",
    date_time: ev.date_time || null,
    country: ev.country || "",
    city: ev.city || "",
    event_format: ev.event_format,
    host_format: ev.host_format,
    badge_label: ev.badge_label || null,
    icon_image_url: ev.icon_image_url || null,
    pricing_model: ev.pricing_model || "free",
    join_by_request: Boolean(ev.join_by_request),
    is_verified: Boolean(ev.is_verified),
    is_validated: ev.is_validated !== false,
    public_path: ev.public_path || buildEventPath(ev.id, ev.name || ""),
  } : null, [ev]);

  const goToVault = useCallback((code) => {
    openProfileCard(code, { returnTo: vaultReturnTo });
  }, [openProfileCard, vaultReturnTo]);

  const openParticipantCard = useCallback((participant) => {
    if (isGuest) {
      redirectGuestToAuth();
      return;
    }
    const targetCode = String(participant?.code || "").trim().toUpperCase();
    const viewerCode = String(userStorageCode || "").trim().toUpperCase();
    const mainHostCode = String(ev?.host?.code || "").trim().toUpperCase();
    const isTargetOrganizer = targetCode === mainHostCode ||
      (Array.isArray(ev?.co_hosts) && ev.co_hosts.some(
        (host) => String(host?.code || "").trim().toUpperCase() === targetCode
      ));
    const canContactParticipant = ev?.status === "active" && targetCode && targetCode !== viewerCode && (
      isHost || (isParticipant && isTargetOrganizer)
    );
    openProfileCard(participant, {
      returnTo: vaultReturnTo,
      eventContactContext: canContactParticipant
        ? directEventContext : null,
    });
  }, [isGuest, redirectGuestToAuth, openProfileCard, vaultReturnTo, isHost, isParticipant, ev?.status, ev?.host?.code, ev?.co_hosts, userStorageCode, directEventContext]);

  const openEventContactCard = useCallback((code) => {
    if (!code) return;
    openProfileCard(code, {
      returnTo: vaultReturnTo,
      eventContactContext: isHost && ev?.status === "active" && code !== userStorageCode
        ? directEventContext : null,
    });
  }, [openProfileCard, vaultReturnTo, isHost, ev?.status, userStorageCode, directEventContext]);

  const openRequestCard = useCallback((code, status) => {
    if (status === "pending") openEventContactCard(code);
    else goToVault(code);
  }, [goToVault, openEventContactCard]);

  const goToMap = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (isGuest) return;
      if (!ev?.id) return;

      navigate("/events/map_search", {
        state: buildEventMapState(ev),
      });
    },
    [navigate, ev, isGuest]
  );

    const openHostItem = useCallback(
      (host) => {
        if (!host || host.type === "platform") return;
        if (host.type === "club") {
          navigate(
            host.publicPath || buildClubPath(host.clubId || host.id, host.name || host.label || ""),
          );
          return;
        }
      if (host.code) {
        if (isGuest) {
          redirectGuestToAuth();
          return;
        }
        const targetCode = String(host.code).trim().toUpperCase();
        const viewerCode = String(userStorageCode || "").trim().toUpperCase();
        const mainHostCode = String(ev?.host?.code || "").trim().toUpperCase();
        const canContactHost = targetCode !== viewerCode && (
          targetCode === mainHostCode ||
          (ev?.status === "active" && (isParticipant || isHost))
        );
        openProfileCard(host, {
          returnTo: vaultReturnTo,
          ratingRole: "host",
          eventContactContext: canContactHost ? directEventContext : null,
        });
      }
    },
      [isGuest, redirectGuestToAuth, openProfileCard, vaultReturnTo, navigate, userStorageCode, ev?.host?.code, ev?.status, isParticipant, isHost, directEventContext]
    );

    const openHostTicketSales = useCallback(
      (e) => {
        e?.stopPropagation?.();
        if (!ev?.id) return;
        onClose?.();
        navigate("/events/search", {
          state: {
            audienceMode: "host",
            hostPanelTab: "payments",
            hostPanelFocusEventId: ev.id,
          },
        });
      },
      [ev?.id, ev?.name, navigate, onClose]
    );

    const prepareHostToolAction = React.useCallback(
      async (actionId) => {
        switch (actionId) {
          case "boost_event":
          case "delete_event":
          case "remove_participant":
          case "send_intention_request":
          case "review_participant_requests":
            return true;
          case "concept_date_poll": {
            return true;
          }
          case "view_waitlist": {
            const statusData = await loadWaitlistStatus({ silent: true, markHostSeen: true });
            if (!statusData) return false;
            setWaitlistMode("host_readonly");
            return true;
          }
          case "add_co_host":
            return openHostRoleModal("co_host_invite", { openModal: false });
          case "transfer_hosting":
            return openHostRoleModal("host_transfer", { openModal: false });
          case "open_analytics":
            void loadEventHostAnalytics();
            return true;
          default:
            return false;
        }
      },
      [loadEventHostAnalytics, loadWaitlistStatus, openHostRoleModal]
    );

    const renderHostToolActionView = (actionId, controls = {}) => {
        const closeTools = typeof controls.close === "function" ? controls.close : () => {};
        const backToTools = typeof controls.back === "function" ? controls.back : closeTools;
        const waitlistJoinBlockCode = String(waitlistStatus?.join_block_code || "").trim();
        const waitlistUsesParticipantRatingBlock =
          waitlistJoinBlockCode === "participant_rating_blocked" ||
          waitlistJoinBlockCode === "participant_slots_full";
        const waitlistJoinError = waitlistUsesParticipantRatingBlock
          ? ""
          : waitlistStatus?.join_error || "";
        const participantRequestStatusFilter =
          ev?.join_by_request
            ? "pending"
            : Array.isArray(ev?.request_form_questions) && ev.request_form_questions.length > 0
              ? "all"
              : "pending";
        const hostAnalyticsTotal = hostAnalyticsSummary?.total || {};
        const commonCloseRefresh = async () => {
          await refreshDetails();
        };

        switch (actionId) {
          case "boost_event":
            return {
              title: t(
                isConcept
                  ? "events.details.boost_modal.title_concept"
                  : "events.details.boost_modal.title"
              ),
              maxWidthClassName: "max-w-[640px]",
              desktopMaxHeight: "min(90vh, 760px)",
              minHeight: null,
              bodyClassName: "px-4 pb-4 pt-0",
              body: (
                <BoostEventModal
                  embedded
                  event={ev}
                  isOpen={true}
                  onClose={closeTools}
                  onBoosted={handleBoosted}
                />
              ),
            };
          case "delete_event":
            return {
              title: t(isConcept ? "events.details.delete_modal_concept.title" : "events.details.delete_modal.title"),
              maxWidthClassName: "max-w-[640px]",
              desktopMaxHeight: "min(82vh, 720px)",
              minHeight: null,
              body: (
                <p className="whitespace-pre-line text-center text-[14px] font-medium leading-6 text-white/78">
                  {t(isConcept ? "events.details.delete_modal_concept.message" : "events.details.delete_modal.message")}
                </p>
              ),
              footer: (
                <div className="space-y-3">
                  <AppModalButton
                    variant="danger"
                    onClick={async (e) => {
                      await handleDeleteEvent(e);
                      closeTools();
                    }}
                  >
                    {t("confirm_modal.buttons.confirm", { defaultValue: "Confirm" })}
                  </AppModalButton>
                  <AppModalButton variant="secondary" onClick={backToTools}>
                    {t("confirm_modal.buttons.cancel", { defaultValue: "Cancel" })}
                  </AppModalButton>
                </div>
              ),
            };
          case "view_waitlist":
            return {
              title: t(
                waitlistStatus?.prepay_required
                  ? "events.details.waitlist.prepay_title"
                  : "events.details.waitlist.title"
              ),
              maxWidthClassName: "max-w-[640px]",
              desktopMaxHeight: "min(82vh, 720px)",
              minHeight: "min(54vh, 480px)",
              body: (
                <WaitlistModal
                  embedded
                  visible={true}
                  onClose={closeTools}
                  eventName={ev?.name || ""}
                  entries={waitlistStatus?.entries || []}
                  isInWaitlist={Boolean(waitlistStatus?.is_in_waitlist)}
                  myPosition={waitlistStatus?.my_position || null}
                  waitlistCount={waitlistStatus?.waitlist_count || 0}
                  availabilityUnalertedReason={waitlistStatus?.availability_unalerted_reason}
                  prepayRequired={Boolean(waitlistStatus?.prepay_required)}
                  canJoin={Boolean(waitlistStatus?.can_join_waitlist)}
                  manualJoinAvailable={manualJoinAvailable}
                  manualJoinFreeSlots={Number(waitlistStatus?.manual_join_free_slots || 0)}
                  joinError={waitlistJoinError}
                  readOnly={true}
                  entriesHidden={Boolean(waitlistStatus?.entries_hidden)}
                  canHostPromote={Boolean(waitlistStatus?.can_host_promote)}
                  hostPromotingUserId={waitlistPromotingUserId}
                  loading={waitlistLoading}
                  submitting={waitlistSubmitting}
                  onJoin={handleJoinWaitlist}
                  onManualJoin={handleManualJoinFromWaitlist}
                  onLeave={doLeaveWaitlist}
                  onHostPromote={handleHostPromoteWaitlistUser}
                  onOpenProfile={openEventContactCard}
                />
              ),
            };
          case "add_co_host":
            return {
              title: t("events.details.host_role_modal.cohost_title"),
              maxWidthClassName: "max-w-[680px]",
              desktopMaxHeight: "min(88vh, 760px)",
              minHeight: "min(68vh, 600px)",
              body: (
                <HostRoleRequestModal
                  embedded
                  visible={true}
                  onClose={closeTools}
                  title={t("events.details.host_role_modal.cohost_title")}
                  subtitle={t("events.details.host_role_modal.cohost_subtitle")}
                  currentTitle={t("events.details.host_role_modal.current_cohosts_title")}
                  currentEmpty={t("events.details.host_role_modal.current_cohosts_empty")}
                  pendingTitle={t("events.details.host_role_modal.pending_requests_title")}
                  pendingEmpty={t("events.details.host_role_modal.pending_requests_empty")}
                  chooseTitle={t("events.details.host_role_modal.cohost_choose_candidate_title")}
                  noCandidates={t("events.details.co_host_modal.no_candidates")}
                  submitLabel={t("events.details.host_role_modal.send_request_button")}
                  removeCurrentLabel={t("events.details.host_role_modal.remove_cohosts_button")}
                  removingCurrentLabel={t("events.details.host_role_modal.removing_cohosts")}
                  currentItems={Array.isArray(ev?.co_hosts) ? ev.co_hosts : []}
                  pendingItems={pendingCoHostRequests}
                  candidates={(Array.isArray(hostRoleCandidates) ? hostRoleCandidates : []).filter(
                    (item) => !Boolean(item?.is_co_host)
                  )}
                  submitting={coHostSubmitting}
                  onCancelPending={handleCancelHostRoleRequest}
                  pendingCancelingIds={pendingCoHostCancelIds}
                  maxSlots={maxCoHostSlots}
                  usedSlots={usedCoHostSlots}
                  singleSelect={false}
                  onRemoveCurrent={handleRemoveCoHosts}
                  onSubmit={(selectedUserIds) => handleSendHostRoleRequest("co_host_invite", selectedUserIds)}
                />
              ),
            };
          case "transfer_hosting":
            return {
              title: t("events.details.host_role_modal.transfer_title"),
              maxWidthClassName: "max-w-[680px]",
              desktopMaxHeight: "min(88vh, 760px)",
              minHeight: "min(68vh, 600px)",
              body: (
                <HostRoleRequestModal
                  embedded
                  visible={true}
                  onClose={closeTools}
                  title={t("events.details.host_role_modal.transfer_title")}
                  subtitle={t("events.details.host_role_modal.transfer_subtitle")}
                  currentTitle={t("events.details.host_role_modal.current_host_title")}
                  currentEmpty={t("events.details.host_role_modal.current_host_empty")}
                  pendingTitle={t("events.details.host_role_modal.transfer_pending_title")}
                  chooseTitle={t("events.details.host_role_modal.choose_candidate_title")}
                  noCandidates={t("events.details.host_role_modal.no_candidates")}
                  submitLabel={t("events.details.host_role_modal.continue_button")}
                  confirmLabel={t("events.details.host_role_modal.transfer_submit_button")}
                  confirmationEnabled
                  confirmationTitle={t("events.details.host_role_modal.confirmation_title")}
                  confirmationBackLabel={t("events.details.host_role_modal.back_to_selection")}
                  renderConfirmationContent={renderHostTransferConfirmation}
                  isConfirmationBlocked={isHostTransferConfirmationBlocked}
                  currentItems={ev?.host ? [ev.host] : []}
                  pendingItems={pendingHostTransferRequests}
                  candidates={
                    pendingHostTransferRequests.length > 0
                      ? []
                      : (Array.isArray(hostRoleCandidates) ? hostRoleCandidates : [])
                  }
                  submitting={coHostSubmitting}
                  onCancelPending={(requestId) => handleCancelHostRoleRequest(requestId, "transfer")}
                  pendingCancelingIds={pendingCoHostCancelIds}
                  singleSelect={true}
                  showSlotsSummary={false}
                  onSubmit={(selectedUserIds) => handleSendHostRoleRequest("host_transfer", selectedUserIds)}
                />
              ),
            };
          case "send_intention_request":
            return {
              title: t(usesHostPaymentRequest(ev) ? "events.details.more_actions.payment_modal.title" : "events.details.more_actions.intention_modal.title"),
              maxWidthClassName: "max-w-[640px]",
              desktopMaxHeight: "min(84vh, 720px)",
              minHeight: "min(58vh, 520px)",
              pinnedFooter: true,
              body: (
                <HostIntentionRequestModal
                  paymentRequest={usesHostPaymentRequest(ev)}
                  embedded
                  visible={true}
                  eventId={ev?.id}
                  onClose={closeTools}
                  onSent={commonCloseRefresh}
                />
              ),
            };
          case "remove_participant":
            return {
              title: t("events.details.more_actions.remove_participant_modal.title"),
              maxWidthClassName: "max-w-[640px]",
              desktopMaxHeight: "min(86vh, 740px)",
              minHeight: "min(64vh, 560px)",
              body: (
                <RemoveParticipantModal
                  embedded
                  visible={true}
                  eventId={ev?.id}
                  participants={Array.isArray(ev?.participants) ? ev.participants : []}
                  hostCode={ev?.host?.code || ""}
                  viewerCode={userStorageCode || ""}
                  onClose={closeTools}
                  onRemoved={async (removedUserIds, removedParticipants) => {
                    removeParticipantsFromLocalEvent(removedUserIds, removedParticipants);
                    await refreshDetails();
                  }}
                />
              ),
            };
          case "open_analytics":
            return {
              title: t("events.details.more_actions.analytics_modal.title"),
              maxWidthClassName: "max-w-[940px]",
              desktopMaxHeight: "min(90vh, 760px)",
              minHeight: "min(78vh, 680px)",
              body: (
                <HostAnalyticsModal
                  embedded
                  visible={true}
                  title={t("events.details.more_actions.analytics_modal.title")}
                  loading={hostAnalyticsLoading}
                  error={hostAnalyticsError}
                  cards={[
                    {
                      key: "impressions_total",
                      label: t("events.details.more_actions.analytics_modal.cards.impressions_total"),
                      value: Number(hostAnalyticsTotal?.card_impressions || 0),
                    },
                    {
                      key: "impressions_unique",
                      label: t("events.details.more_actions.analytics_modal.cards.impressions_unique"),
                      value: Number(hostAnalyticsTotal?.unique_viewers_lifetime || 0),
                    },
                    {
                      key: "opens_total",
                      label: t("events.details.more_actions.analytics_modal.cards.opens_total"),
                      value: Number(hostAnalyticsTotal?.card_opens || 0),
                    },
                    {
                      key: "opens_unique",
                      label: t("events.details.more_actions.analytics_modal.cards.opens_unique"),
                      value: Number(hostAnalyticsTotal?.unique_openers_lifetime || 0),
                    },
                  ]}
                  rows={hostAnalyticsRows}
                  charts={[
                    {
                      id: "viewers_daily",
                      title: t("events.details.more_actions.analytics_modal.charts.viewers_title"),
                      totalKey: "unique_viewers",
                      newKey: "new_unique_viewers",
                      totalLabel: t("events.details.more_actions.analytics_modal.charts.total_unique_label"),
                      newLabel: t("events.details.more_actions.analytics_modal.charts.new_unique_label"),
                    },
                    {
                      id: "opens_daily",
                      title: t("events.details.more_actions.analytics_modal.charts.opens_title"),
                      totalKey: "unique_openers",
                      newKey: "new_unique_openers",
                      totalLabel: t("events.details.more_actions.analytics_modal.charts.total_unique_label"),
                      newLabel: t("events.details.more_actions.analytics_modal.charts.new_unique_label"),
                    },
                  ]}
                  closeLabel={t("events.details.more_actions.analytics_modal.close")}
                  emptyLabel={t("events.details.more_actions.analytics_modal.empty")}
                />
              ),
            };
          case "concept_date_poll":
            return {
              title: ev?.concept_date_poll?.exists
                ? t("events.details.more_actions.date_poll_modal.title_edit")
                : t("events.details.more_actions.date_poll_modal.title_create"),
              maxWidthClassName: "max-w-[720px]",
              desktopMaxHeight: "min(90vh, 760px)",
              minHeight: null,
              pinnedFooter: true,
              body: (
                <ConceptDatePollModal
                  embedded
                  visible={true}
                  eventId={ev?.id}
                  durationMinutes={ev?.approximate_duration}
                  onClose={closeTools}
                  onSaved={commonCloseRefresh}
                />
              ),
            };
          case "review_participant_requests":
            return {
              title: t("events.details.more_actions.review_requests_modal.title"),
              maxWidthClassName: "max-w-[760px]",
              desktopMaxHeight: "min(90vh, 760px)",
              minHeight: "min(70vh, 620px)",
              body: (
                <ParticipantRequestsReviewModal
                  embedded
                  visible={true}
                  eventId={ev?.id}
                  hasRequestForm={Boolean(
                    Array.isArray(ev?.request_form_questions) &&
                      ev.request_form_questions.length > 0
                  )}
                  requestStatusFilter={participantRequestStatusFilter}
                  onClose={closeTools}
                  onViewed={async () => {
                    await participantRequestUnreadRefetch?.();
                  }}
                  onUpdated={async () => {
                    await refreshDetails();
                    await participantRequestUnreadRefetch?.();
                  }}
                  onOpenProfile={openRequestCard}
                />
              ),
            };
          default:
            return null;
        }
      };

  const sheetRef = useRef(null);
  const headerRef = useRef(null);
  const startYRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    startYRef.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const endY = e.changedTouches[0].clientY;
    if (endY - startYRef.current > 250) onClose?.();
  }, [onClose]);

  const handleTouchMove = useCallback(
    (e) => {
      const currentY = e.touches[0].clientY;
      const diff = currentY - startYRef.current;
      if (diff > 80) {
        onClose?.();
        startYRef.current = null;
      }
    },
    [onClose]
  );

  useEffect(() => {
    const node = headerRef.current;
    if (!node) return;

    node.addEventListener("touchstart", handleTouchStart, { passive: true });
    node.addEventListener("touchmove", handleTouchMove, { passive: true });
    node.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      node.removeEventListener("touchstart", handleTouchStart);
      node.removeEventListener("touchmove", handleTouchMove);
      node.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const nativeEventSwipeBackEnabled = !Boolean(
    isConfirmOpen ||
      isIntentOpen ||
      checkoutCapacityReservedOpen ||
      isTicketSuccessOpen ||
      isHostConfirmOpen ||
      isHostCancelConfirmOpen ||
      isWaitlistOpen ||
      isCoHostModalOpen ||
      isHostTransferModalOpen ||
      isHostIntentionModalOpen ||
      isReviewRequestsModalOpen ||
      isConceptDatePollModalOpen ||
      isRemoveParticipantModalOpen ||
      isHostAnalyticsModalOpen ||
      isPhotoModalOpen ||
      isJoinRulesIntroOpen ||
      isLeaveConfirmOpen ||
      isDeleteConfirmOpen ||
      isJoinConfirmOpen ||
      joinLimitModal ||
      isCalendarPickerOpen ||
      isHostClaimConfirmOpen ||
      isBoostModalOpen ||
      isReportOpen ||
      ticketCancellationOutcome
  );

  if (!ev) {
    return <EventDetailsLoadingSkeleton desktop={isDesktopPageMode} />;
  }

  const sheetContent = (
    <div
      ref={sheetRef}
      className={
        isDesktopPageMode
          ? "relative z-0 flex h-full w-full justify-center"
          : `${isDesktop ? "absolute" : "fixed"} inset-0 z-40 flex ${
              isDesktop ? "justify-center items-center" : "justify-center"
            } ${shiftRight ? 'lg:pl-[2rem]' : ''}`
      }
    >
      {!isDesktopPageMode ? (
        <div
          onClick={onClose}
          className={`absolute inset-0 z-30 ${isDesktop ? "bg-black/55" : "bg-black/50"}`}
          aria-hidden="true"
        />
      ) : null}
      <div
        onClick={e => e.stopPropagation()}
        className={`relative ${
          isDesktopPageMode
            ? "z-10 h-full w-full max-w-[760px] overflow-y-auto overscroll-none rounded-[30px] bg-[#050812] shadow-[0_24px_80px_rgba(0,0,0,0.34)] custom-scrollbar"
            : isDesktop
            ? "max-h-[90vh] rounded-[28px] border border-white/14 bg-[#050812] p-5 pt-14 overflow-auto z-40 overscroll-none shadow-[0_24px_80px_rgba(0,0,0,0.42)]"
            : "w-full h-full flex flex-col overflow-hidden z-40"
        }`}
        style={
          isDesktopPageMode
            ? {
                overscrollBehavior: "none",
                overscrollBehaviorY: "none",
              }
            : isDesktop
            ? {
                width: `${DETAILS_MODAL_WIDTH_FACTOR * 100}%`,
                minWidth: `${DETAILS_MODAL_MIN_WIDTH}px`,
                overscrollBehavior: "none",
                overscrollBehaviorY: "none",
              }
            : { height: "100dvh", minHeight: "100vh" }
        }
      >
        {isDesktop && !isDesktopPageMode ? (
          <button
            type="button"
            onClick={onClose}
            aria-label={t("event_description_modal.header.close_aria", { defaultValue: "Close" })}
            className="absolute right-4 top-4 z-50 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_10px_24px_rgba(0,0,0,0.28)] transition hover:bg-white/15 active:scale-[0.97]"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
              <path
                d="M5.5 5.5L16.5 16.5M16.5 5.5L5.5 16.5"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : null}
        {isDesktop && !isDesktopPageMode ? (
        <EventDetailsDesktopContent
            // core event data
            ev={ev}
            isConcept={isConcept}
            t={t}
            language={i18n.resolvedLanguage || i18n.language}
            cardDatetime={cardDatetime}
            canAddToCalendar={canAddToCalendar}
            onAddToCalendar={handleOpenCalendarPicker}
            // favourite / meta
            favouriteState={favouriteState}
            toggleFav={toggleFav}
            isParticipant={isParticipant}
            isHost={isHost}
            isFull={isFull}
            hasConfirmedAttendance={hasConfirmedAttendance}
            // hosts
            hostList={hostList}
            multipleHosts={multipleHosts}
            // sharing / navigation
            linkCopied={linkCopied}
            shareEvent={shareEvent}
            goToMap={isGuest ? undefined : goToMap}
            goToVault={goToVault}
            onOpenParticipant={openParticipantCard}
            onOpenHost={openHostItem}
            openEventChat={openEventChat}
            joiningChat={joiningChat}
            openMainHostDirectChat={openMainHostDirectChat}
            participantTicketing={participantTicketing}
            ticketCancellationQuote={ticketCancellationQuote}
            ticketCancellationLoading={ticketCancellationLoading}
            ticketCancellationSubmitting={ticketCancellationSubmitting}
            ticketDisputeSubmitting={ticketDisputeSubmitting}
            onOpenTicketPayment={openParticipantTicketPayment}
            onLoadTicketCancellation={loadParticipantTicketCancellation}
            onSubmitTicketCancellation={submitParticipantTicketCancellation}
            onSubmitTicketDispute={submitParticipantTicketDispute}
            onOpenTicketPanel={openParticipantTicketPanel}
            ticketPanelOpenRequest={ticketPanelOpenRequest}
            ticketPanelOpenView={ticketPanelOpenView}
            onTicketPanelOpenHandled={handleTicketPanelOpenHandled}
            // participants / reserve
            participantsSectionRef={participantsSectionRef}
            loadingCode={loadingCode}
            userStorageCode={userStorageCode}
            intentStatus={intentStatus}
            activationDateBadgesEnabled={!!ev?.activation_date_badges_enabled}
            reserveUI={reserveUI}
            unlockBeforeLabel={unlockBeforeLabel}
            joinDisabledByReserve={joinDisabledByReserve}
            unlockCountdownMs={unlockCountdownMs}
            unlockCountdownText={unlockCountdownText}
            unlockText={unlockText}
            // payment / intent
            showPayButton={showPayButton}
            showIntentButton={showIntentButton}
            showAttendanceButton={showAttendanceButton}
            confirmCtaLabel={confirmCtaLabel}
            prepayWindowT3d={prepayWindowT3d}
            ticketPrice={ticketPrice}
            ticketPaid={ticketPaid}
            requiresPrepay={requiresPrepayGate}
            // role / auth
            isGuest={isGuest}
            waitlistCtaVisible={waitlistCtaVisible}
            waitlistCtaLabel={waitlistCtaLabel}
            isInWaitlist={isInWaitlist}
            waitlistUnreadCount={waitlistUnreadCount}
            moreActionsUnreadCount={hostActionUnreadCount}
            participantRequestsPendingCount={participantRequestsPendingCount}
            eventToolsOpenRequest={eventToolsOpenRequest}
            eventToolsCloseRequest={eventToolsCloseRequest}
            // actions
            handleJoinEvent={handleJoinEvent}
            onJoinPaymentRequired={handleJoinPaymentRequired}
            onCheckoutCapacityReserved={handleCheckoutCapacityReserved}
            handleOpenWaitlist={handleOpenWaitlist}
            handleEditEvent={handleEditEvent}
            handleActivateConcept={handleActivateConcept}
            handleHostConfirmEvent={handleOpenHostConfirmModal}
            handleReportEvent={handleReportEvent}
            handleRepeatEvent={handleRepeatEvent}
            handleOpenTicketSales={openHostTicketSales}
            onMoreActionClick={handleHostMoreAction}
            onPrepareMoreAction={prepareHostToolAction}
            renderMoreActionView={renderHostToolActionView}
            hostConfirmationStatus={ev?.host_confirmation_status || "pending"}
            hostConfirmationRequestedAt={ev?.host_confirmation_requested_at || null}
            primaryActionType={primaryAction}
            newcomerInterestAction={newcomerInterestAction}
            // modal setters
            setIsLeaveConfirmOpen={setIsLeaveConfirmOpen}
            onOpenLeaveConfirm={handleOpenLeaveConfirm}
            setIsConfirmOpen={setIsConfirmOpenGuarded}
            setIsIntentOpen={setIsIntentOpenGuarded}
            setIntentVariant={setIntentVariant}
            setPreJoinPaymentFlag={setPreJoinPaymentFlag}
            setTicketMode={setTicketMode}
            // join-confirm from desktop content (если понадобится)
            setIsJoinConfirmOpen={setIsJoinConfirmOpen}
            // возможность закрыть изнутри
            onClose={onClose}
          />
        ) : (
          <EventDetailsMobileContent
            // core event data
            ev={ev}
            isConcept={isConcept}
            t={t}
            language={i18n.resolvedLanguage || i18n.language}
            cardDatetime={cardDatetime}
            canAddToCalendar={canAddToCalendar}
            onAddToCalendar={handleOpenCalendarPicker}
            // favourite / meta
            favouriteState={favouriteState}
            toggleFav={toggleFav}
            isParticipant={isParticipant}
            isHost={isHost}
            isFull={isFull}
            hasConfirmedAttendance={hasConfirmedAttendance}
            // hosts
            hostList={hostList}
            // refs / layout
            headerRef={headerRef}
            bottomSafeSpaceCss={bottomSafeSpaceCss}
            compactBottomSpacer={isDesktopPageMode}
            // sharing / navigation
            linkCopied={linkCopied}
            shareEvent={shareEvent}
            goToMap={isGuest ? undefined : goToMap}
            goToVault={goToVault}
            onOpenParticipant={openParticipantCard}
            onOpenHost={openHostItem}
            openEventChat={openEventChat}
            openMainHostDirectChat={openMainHostDirectChat}
            joiningChat={joiningChat}
            participantTicketing={participantTicketing}
            ticketCancellationQuote={ticketCancellationQuote}
            ticketCancellationLoading={ticketCancellationLoading}
            ticketCancellationSubmitting={ticketCancellationSubmitting}
            ticketDisputeSubmitting={ticketDisputeSubmitting}
            onOpenTicketPayment={openParticipantTicketPayment}
            onLoadTicketCancellation={loadParticipantTicketCancellation}
            onSubmitTicketCancellation={submitParticipantTicketCancellation}
            onSubmitTicketDispute={submitParticipantTicketDispute}
            ticketPanelOpenRequest={ticketPanelOpenRequest}
            ticketPanelOpenView={ticketPanelOpenView}
            onTicketPanelOpenHandled={handleTicketPanelOpenHandled}
            onOpenTicketPanel={openParticipantTicketPanel}
            // participants / reserve
            participantsSectionRef={participantsSectionRef}
            loadingCode={loadingCode}
            userStorageCode={userStorageCode}
            intentStatus={intentStatus}
            activationDateBadgesEnabled={!!ev?.activation_date_badges_enabled}
            reserveUI={reserveUI}
            unlockBeforeLabel={unlockBeforeLabel}
            joinDisabledByReserve={joinDisabledByReserve}
            unlockCountdownMs={unlockCountdownMs}
            unlockCountdownText={unlockCountdownText}
            unlockText={unlockText}
            // payment / intent
            showPayButton={showPayButton}
            showIntentButton={showIntentButton}
            showAttendanceButton={showAttendanceButton}
            confirmCtaLabel={confirmCtaLabel}
            prepayWindowT3d={prepayWindowT3d}
            ticketPrice={ticketPrice}
            ticketPaid={ticketPaid}
            requiresPrepay={requiresPrepayGate}
            // role / auth
            isGuest={isGuest}
            waitlistCtaVisible={waitlistCtaVisible}
            waitlistCtaLabel={waitlistCtaLabel}
            isInWaitlist={isInWaitlist}
            waitlistUnreadCount={waitlistUnreadCount}
            moreActionsUnreadCount={hostActionUnreadCount}
            participantRequestsPendingCount={participantRequestsPendingCount}
            eventToolsOpenRequest={eventToolsOpenRequest}
            eventToolsCloseRequest={eventToolsCloseRequest}
            nativeSwipeBackEnabled={nativeEventSwipeBackEnabled}
            // actions
            handleJoinEvent={handleJoinEvent}
            onJoinPaymentRequired={handleJoinPaymentRequired}
            onCheckoutCapacityReserved={handleCheckoutCapacityReserved}
            handleOpenWaitlist={handleOpenWaitlist}
            handleEditEvent={handleEditEvent}
            handleActivateConcept={handleActivateConcept}
            handleHostConfirmEvent={handleOpenHostConfirmModal}
            handleReportEvent={handleReportEvent}
            handleRepeatEvent={handleRepeatEvent}
            handleOpenTicketSales={openHostTicketSales}
            onMoreActionClick={handleHostMoreAction}
            onPrepareMoreAction={prepareHostToolAction}
            renderMoreActionView={renderHostToolActionView}
            hostConfirmationStatus={ev?.host_confirmation_status || "pending"}
            hostConfirmationRequestedAt={ev?.host_confirmation_requested_at || null}
            primaryActionType={primaryAction}
            newcomerInterestAction={newcomerInterestAction}
            // modal setters
            setIsLeaveConfirmOpen={setIsLeaveConfirmOpen}
            onOpenLeaveConfirm={handleOpenLeaveConfirm}
            setIsConfirmOpen={setIsConfirmOpenGuarded}
            setIsIntentOpen={setIsIntentOpenGuarded}
            setIntentVariant={setIntentVariant}
            setPreJoinPaymentFlag={setPreJoinPaymentFlag}
            setTicketMode={setTicketMode}
            // 🔹 новые пропсы, которых не хватало
            setIsJoinConfirmOpen={setIsJoinConfirmOpen}
            onClose={onClose}
            onAfterJoinByRequestSuccess={handleAfterJoinByRequestSuccess}
          />
        )}
      </div>
    </div>
  );

  const confirmModal = (!isConcept && isConfirmOpen) && (
    <ConfirmAttendanceModal
      visible={true}
      eventId={ev?.id}
      initialMode={attendanceCashQrMode ? "host" : "geo"}
      forceHostQr={attendanceCashQrMode}
      cashPaymentQrMode={attendanceCashQrMode}
      onClose={() => {
        setIsConfirmOpen(false);
        setAttendanceCashQrMode(false);
        onConfirmClose();
      }}
      onSuccess={() => {
        setAttendanceCashQrMode(false);
        handleConfirmSuccess();
      }}
      onRequirePayment={() => {
        void openAttendanceTicketPayment();
      }}
    />
  );

  // Prevent UI flicker: only render intent/payment modal
  // after we KNOW the user has a custom avatar (hasCustomAvatar === true).
  const canShowIntentModal = isIntentOpen && hasCustomAvatar === true;

  const intentModal = canShowIntentModal && (
    <ConfirmIntentModal
      visible={true}
      eventId={ev?.id}
      eventType={ev?.type}
      variant={intentVariant}
      isPayableInAdvance={serverPrepayRequired || requiresPrepayGate}
      needImmediatePayment={forceImmediatePayment || prepayWindowT3d}
      payUrl={serverPayUrl}
      price={
        ticketMode && participantTicketing?.next_checkout_amount != null
          ? Number(participantTicketing.next_checkout_amount)
          : preJoinPaymentFlag
            ? joinCheckoutPrice
            : null
      }
      paymentContext={{
        isHostPaidCardEvent,
        settlementMode: ev?.settlement_mode || participantTicketing?.settlement_mode,
        currency:
          participantTicketing?.currency ||
          ticketPurchasePreview?.currency ||
          "EUR",
        organizer:
          ev?.host_paid_organizer || {
            name:
              ev?.club?.name ||
              ev?.business_name ||
              ev?.host?.code ||
              null,
            email: null,
            phone: null,
          },
        paymentStep: participantTicketing?.payment_step || "join",
        fullTicketPrice: ticketPrice,
        depositPercent: joinPaymentPercent,
        depositAmount:
          participantTicketing?.join_amount ??
          ticketPurchasePreview?.join_amount ??
          null,
        depositCheckoutAmount:
          participantTicketing?.join_checkout_amount ??
          ticketPurchasePreview?.join_checkout_amount ??
          joinCheckoutPrice,
        fullCheckoutAmount:
          participantTicketing?.full_checkout_amount ??
          ticketPurchasePreview?.full_checkout_amount ??
          null,
        remainingPercent: Math.max(0, 100 - joinPaymentPercent),
        remainingAmount: remainingTicketPrice,
        balanceDueDaysBefore: ev?.host_paid_balance_due_days_before ?? null,
        balanceDueAt: participantTicketing?.balance_due_at || ev?.host_paid_payment_deadline_at || null,
        cancellationPolicy: ev?.host_paid_cancellation_policy ?? null,
        allowFullPaymentOnJoin:
          participantTicketing?.allow_full_payment_on_join ??
          ticketPurchasePreview?.allow_full_payment_on_join ??
          ev?.host_paid_allow_full_ticket_payment_on_join ??
          false,
        fullParticipantServiceFee:
          participantTicketing?.full_participant_service_fee ??
          ticketPurchasePreview?.full_participant_service_fee ??
          null,
        participantServiceFee:
          participantTicketing?.participant_service_fee ??
          ticketPurchasePreview?.participant_service_fee ??
          null,
        nextCheckoutAmount: participantTicketing?.next_checkout_amount ?? null,
        nextTicketBaseAmount: participantTicketing?.next_ticket_base_amount ?? null,
        nextServiceFeeAmount: participantTicketing?.next_service_fee_amount ?? null,
        payFullNowUrl: participantTicketing?.pay_full_now_url ?? null,
        multisportPricing:
          participantTicketing?.multisport_pricing ||
          ticketPurchasePreview?.multisport_pricing ||
          ev?.multisport_pricing ||
          null,
        ticketCategories:
          participantTicketing?.ticket_categories ||
          ticketPurchasePreview?.ticket_categories ||
          (Array.isArray(ev?.ticket_categories) && ev.ticket_categories.length
            ? { enabled: true, options: ev.ticket_categories, selected: null, locked: false }
            : null),
        isPlatformEvent: Boolean(ev?.is_platform_event),
      }}
      preJoinPayment={preJoinPaymentFlag}
      ticketMode={ticketMode}
      onGenderRequired={({ resume }) => {
        setGenderError("");
        setGenderPrompt({ resume });
      }}
      onCheckoutCapacityReserved={handleCheckoutCapacityReserved}
      onShowQr={attendanceRequiresTicketPayment ? openAttendanceTicketQr : undefined}
      onClose={() => {
        setServerPayUrl(null);
        setServerTicketPaymentState(null);
        setServerPrepayRequired(false);
        setForceImmediatePayment(false);
        setIsIntentOpen(false);
      }}
      onConfirmSuccess={handleIntentConfirmSuccess}
      onDeclineSuccess={handleIntentDeclineSuccess}
    />
  );

  const waitlistJoinBlockCode = String(waitlistStatus?.join_block_code || "").trim();
  const waitlistUsesParticipantRatingBlock =
    waitlistJoinBlockCode === "participant_rating_blocked" ||
    waitlistJoinBlockCode === "participant_slots_full";
  const waitlistJoinError = waitlistUsesParticipantRatingBlock
    ? ""
    : waitlistStatus?.join_error || "";

  const waitlistModal = (
    <WaitlistModal
      visible={isWaitlistOpen}
      onClose={() => {
        setIsWaitlistOpen(false);
        setWaitlistMode("participant");
        setWaitlistPromotingUserId(null);
        setWaitlistOpenedFromAvailabilityAlert(false);
        setWaitlistCapacityMessage("");
        setWaitlistLoadError("");
        requestEventToolsClose();
      }}
      onBack={
        waitlistMode === "host_readonly"
          ? () => {
              setIsWaitlistOpen(false);
              setWaitlistMode("participant");
              setWaitlistPromotingUserId(null);
              requestEventToolsOpen();
            }
          : null
      }
      eventName={ev?.name || ""}
      entries={waitlistStatus?.entries || []}
      isInWaitlist={Boolean(waitlistStatus?.is_in_waitlist)}
      myPosition={waitlistStatus?.my_position || null}
      waitlistCount={waitlistStatus?.waitlist_count || 0}
      availabilityUnalertedReason={waitlistStatus?.availability_unalerted_reason}
      prepayRequired={Boolean(waitlistStatus?.prepay_required)}
      canJoin={Boolean(waitlistStatus?.can_join_waitlist)}
      manualJoinAvailable={manualJoinAvailable}
      manualJoinFreeSlots={Number(waitlistStatus?.manual_join_free_slots || 0)}
      joinError={waitlistJoinError}
      entryReason={waitlistCapacityMessage}
      readOnly={waitlistMode === "host_readonly"}
      entriesHidden={Boolean(waitlistStatus?.entries_hidden)}
      canHostPromote={Boolean(waitlistStatus?.can_host_promote)}
      hostPromotingUserId={waitlistPromotingUserId}
      loading={waitlistLoading}
      loadError={waitlistLoadError}
      submitting={waitlistSubmitting}
      openedFromAvailabilityAlert={waitlistOpenedFromAvailabilityAlert}
      onJoin={handleJoinWaitlist}
      onManualJoin={handleManualJoinFromWaitlist}
      onLeave={doLeaveWaitlist}
      onHostPromote={handleHostPromoteWaitlistUser}
      onOpenProfile={openEventContactCard}
      onRetry={() => {
        trackGAEvent("waitlist_status_retry", {
          event_id: String(ev?.id || ""),
          source: waitlistOpenedFromAvailabilityAlert
            ? "availability_notification"
            : "event_details",
        });
        void loadWaitlistStatus({ silent: true });
      }}
    />
  );

  const checkoutCapacityReservedModal = (
    <AppModalShell
      open={checkoutCapacityReservedOpen}
      title={t("events.details.waitlist.checkout_reserved_title")}
      onClose={() => setCheckoutCapacityReservedOpen(false)}
      closeAriaLabel={t("events.details.waitlist.close")}
      zIndexClassName="z-[13000]"
      maxWidthClassName="max-w-[560px]"
      footer={
        <AppModalButton
          onClick={async () => {
            const joined = await doJoinWaitlist({ openModal: false });
            if (joined) {
              setCheckoutCapacityReservedOpen(false);
            }
          }}
          loading={waitlistSubmitting}
          className="text-[#2563EB]"
        >
          {t("events.details.waitlist.join_waitlist")}
        </AppModalButton>
      }
    >
      <p className="text-center text-[15px] font-medium leading-7 text-white/88">
        {t("events.details.waitlist.checkout_reserved_body")}
      </p>
    </AppModalShell>
  );

  const coHostModal = (
    <HostRoleRequestModal
      visible={isCoHostModalOpen}
      onClose={() => {
        if (coHostSubmitting) return;
        setIsCoHostModalOpen(false);
        requestEventToolsClose();
      }}
      onBack={() => {
        if (coHostSubmitting) return;
        setIsCoHostModalOpen(false);
        requestEventToolsOpen();
      }}
      title={t("events.details.host_role_modal.cohost_title")}
      subtitle={t("events.details.host_role_modal.cohost_subtitle")}
      currentTitle={t("events.details.host_role_modal.current_cohosts_title")}
      currentEmpty={t("events.details.host_role_modal.current_cohosts_empty")}
      pendingTitle={t("events.details.host_role_modal.pending_requests_title")}
      pendingEmpty={t("events.details.host_role_modal.pending_requests_empty")}
      chooseTitle={t("events.details.host_role_modal.cohost_choose_candidate_title")}
      noCandidates={t("events.details.co_host_modal.no_candidates")}
      submitLabel={t("events.details.host_role_modal.send_request_button")}
      removeCurrentLabel={t("events.details.host_role_modal.remove_cohosts_button")}
      removingCurrentLabel={t("events.details.host_role_modal.removing_cohosts")}
      currentItems={Array.isArray(ev?.co_hosts) ? ev.co_hosts : []}
      pendingItems={pendingCoHostRequests}
      candidates={(Array.isArray(hostRoleCandidates) ? hostRoleCandidates : []).filter(
        (item) => !Boolean(item?.is_co_host)
      )}
      submitting={coHostSubmitting}
      onCancelPending={handleCancelHostRoleRequest}
      pendingCancelingIds={pendingCoHostCancelIds}
      maxSlots={maxCoHostSlots}
      usedSlots={usedCoHostSlots}
      singleSelect={false}
      onRemoveCurrent={handleRemoveCoHosts}
      onSubmit={(selectedUserIds) => handleSendHostRoleRequest("co_host_invite", selectedUserIds)}
    />
  );

  const hostTransferModal = (
    <HostRoleRequestModal
      visible={isHostTransferModalOpen}
      onClose={() => {
        if (coHostSubmitting) return;
        setIsHostTransferModalOpen(false);
        requestEventToolsClose();
      }}
      onBack={() => {
        if (coHostSubmitting) return;
        setIsHostTransferModalOpen(false);
        requestEventToolsOpen();
      }}
      title={t("events.details.host_role_modal.transfer_title")}
      subtitle={t("events.details.host_role_modal.transfer_subtitle")}
      currentTitle={t("events.details.host_role_modal.current_host_title")}
      currentEmpty={t("events.details.host_role_modal.current_host_empty")}
      pendingTitle={t("events.details.host_role_modal.transfer_pending_title")}
      chooseTitle={t("events.details.host_role_modal.choose_candidate_title")}
      noCandidates={t("events.details.host_role_modal.no_candidates")}
      submitLabel={t("events.details.host_role_modal.continue_button")}
      confirmLabel={t("events.details.host_role_modal.transfer_submit_button")}
      confirmationEnabled
      confirmationTitle={t("events.details.host_role_modal.confirmation_title")}
      confirmationBackLabel={t("events.details.host_role_modal.back_to_selection")}
      renderConfirmationContent={renderHostTransferConfirmation}
      isConfirmationBlocked={isHostTransferConfirmationBlocked}
      currentItems={ev?.host ? [ev.host] : []}
      pendingItems={pendingHostTransferRequests}
      candidates={
        pendingHostTransferRequests.length > 0
          ? []
          : (Array.isArray(hostRoleCandidates) ? hostRoleCandidates : [])
      }
      submitting={coHostSubmitting}
      onCancelPending={(requestId) => handleCancelHostRoleRequest(requestId, "transfer")}
      pendingCancelingIds={pendingCoHostCancelIds}
      singleSelect={true}
      showSlotsSummary={false}
      onSubmit={(selectedUserIds) => handleSendHostRoleRequest("host_transfer", selectedUserIds)}
    />
  );

  const hostIntentionModal = (
    <HostIntentionRequestModal
                  paymentRequest={usesHostPaymentRequest(ev)}
      visible={isHostIntentionModalOpen}
      eventId={ev?.id}
      onClose={() => {
        setIsHostIntentionModalOpen(false);
        requestEventToolsClose();
      }}
      onBack={() => {
        setIsHostIntentionModalOpen(false);
        requestEventToolsOpen();
      }}
      onSent={async () => {
        await refreshDetails();
      }}
    />
  );

  const removeParticipantModal = (
    <RemoveParticipantModal
      visible={isRemoveParticipantModalOpen}
      eventId={ev?.id}
      participants={Array.isArray(ev?.participants) ? ev.participants : []}
      hostCode={ev?.host?.code || ""}
      viewerCode={userStorageCode || ""}
      onClose={() => {
        setIsRemoveParticipantModalOpen(false);
        requestEventToolsClose();
      }}
      onBack={() => {
        setIsRemoveParticipantModalOpen(false);
        requestEventToolsOpen();
      }}
      onRemoved={async (removedUserIds, removedParticipants) => {
        removeParticipantsFromLocalEvent(removedUserIds, removedParticipants);
        await refreshDetails();
      }}
    />
  );

  const hostAnalyticsTotal = hostAnalyticsSummary?.total || {};
  const hostAnalyticsModal = (
    <HostAnalyticsModal
      visible={isHostAnalyticsModalOpen}
      onClose={() => {
        setIsHostAnalyticsModalOpen(false);
        requestEventToolsClose();
      }}
      onBack={() => {
        setIsHostAnalyticsModalOpen(false);
        requestEventToolsOpen();
      }}
      zIndexClassName="z-[1200]"
      title={t("events.details.more_actions.analytics_modal.title")}
      loading={hostAnalyticsLoading}
      error={hostAnalyticsError}
      cards={[
        {
          key: "impressions_total",
          label: t("events.details.more_actions.analytics_modal.cards.impressions_total"),
          value: Number(hostAnalyticsTotal?.card_impressions || 0),
        },
        {
          key: "impressions_unique",
          label: t("events.details.more_actions.analytics_modal.cards.impressions_unique"),
          value: Number(hostAnalyticsTotal?.unique_viewers_lifetime || 0),
        },
        {
          key: "opens_total",
          label: t("events.details.more_actions.analytics_modal.cards.opens_total"),
          value: Number(hostAnalyticsTotal?.card_opens || 0),
        },
        {
          key: "opens_unique",
          label: t("events.details.more_actions.analytics_modal.cards.opens_unique"),
          value: Number(hostAnalyticsTotal?.unique_openers_lifetime || 0),
        },
      ]}
      rows={hostAnalyticsRows}
      charts={[
        {
          id: "viewers_daily",
          title: t("events.details.more_actions.analytics_modal.charts.viewers_title"),
          totalKey: "unique_viewers",
          newKey: "new_unique_viewers",
          totalLabel: t("events.details.more_actions.analytics_modal.charts.total_unique_label"),
          newLabel: t("events.details.more_actions.analytics_modal.charts.new_unique_label"),
        },
        {
          id: "opens_daily",
          title: t("events.details.more_actions.analytics_modal.charts.opens_title"),
          totalKey: "unique_openers",
          newKey: "new_unique_openers",
          totalLabel: t("events.details.more_actions.analytics_modal.charts.total_unique_label"),
          newLabel: t("events.details.more_actions.analytics_modal.charts.new_unique_label"),
        },
      ]}
      closeLabel={t("events.details.more_actions.analytics_modal.close")}
      backLabel={t("events.details.more_actions.back")}
      emptyLabel={t("events.details.more_actions.analytics_modal.empty")}
    />
  );

  const conceptDatePollModal = (
    <ConceptDatePollModal
      visible={isConceptDatePollModalOpen}
      eventId={ev?.id}
      durationMinutes={ev?.approximate_duration}
      onClose={() => {
        setIsConceptDatePollModalOpen(false);
        requestEventToolsClose();
      }}
      onBack={() => {
        setIsConceptDatePollModalOpen(false);
        requestEventToolsOpen();
      }}
      onSaved={async () => {
        await refreshDetails();
      }}
    />
  );

  const reviewRequestsModal = (
    <ParticipantRequestsReviewModal
      visible={isReviewRequestsModalOpen}
      eventId={ev?.id}
      hasRequestForm={Boolean(
        Array.isArray(ev?.request_form_questions) &&
          ev.request_form_questions.length > 0
      )}
      requestStatusFilter={
        ev?.join_by_request
          ? "pending"
          : Array.isArray(ev?.request_form_questions) && ev.request_form_questions.length > 0
            ? "all"
            : "pending"
      }
      onClose={() => {
        setIsReviewRequestsModalOpen(false);
        requestEventToolsClose();
      }}
      onBack={() => {
        setIsReviewRequestsModalOpen(false);
        requestEventToolsOpen();
      }}
      onViewed={async () => {
        await participantRequestUnreadRefetch?.();
      }}
      onUpdated={async () => {
        await refreshDetails();
        await participantRequestUnreadRefetch?.();
      }}
      onOpenProfile={openRequestCard}
    />
  );

  const leaveConfirmMessage = buildLeaveConfirmMessage({
    isConcept,
    leavePreview,
    leavePreviewLoading,
    t,
  });

  const leaveConfirmModal = isLeaveConfirmOpen && (
    <ConfirmModal
      isOpen={true}
      title={t(isConcept ? 'events.details.leave_modal_concept.title' : 'events.details.leave_modal.title')}
      message={leaveConfirmMessage}
      onConfirm={(e) => {
        if (leavePreviewLoading) return;
        setIsLeaveConfirmOpen(false);
        handleLeaveEvent(e);
      }}
      onCancel={() => setIsLeaveConfirmOpen(false)}
      confirmText={t('events.details.leave_modal.confirm_button')}
      cancelText={t('events.details.leave_modal.cancel_button')}
    />
  );

  const deleteConfirmModal = isDeleteConfirmOpen && (
    <ConfirmModal
      isOpen={true}
      title={t(isConcept ? 'events.details.delete_modal_concept.title' : 'events.details.delete_modal.title')}
      message={t(isConcept ? 'events.details.delete_modal_concept.message' : 'events.details.delete_modal.message')}
      onConfirm={(e) => { setIsDeleteConfirmOpen(false); handleDeleteEvent(e); }}
      onCancel={() => setIsDeleteConfirmOpen(false)}
    />
  );

  const hostConfirmModal = isHostConfirmOpen && (
    <ConfirmModal
      isOpen={true}
      title={t("events.details.host_confirm_modal.title")}
      message={t("events.details.host_confirm_modal.message")}
      onConfirm={(e) => {
        void handleConfirmHosting(e);
      }}
      onCancel={() => {
        setIsHostConfirmOpen(false);
      }}
      onSecondary={() => {
        setIsHostConfirmOpen(false);
        setIsHostCancelConfirmOpen(true);
      }}
      confirmText={t("events.details.host_confirm_modal.confirm_button")}
      cancelText={t("events.details.host_confirm_modal.cancel_event_button")}
      confirmButtonVariant="primary"
      cancelButtonVariant="danger"
    />
  );

  const hostCancelConfirmModal = isHostCancelConfirmOpen && (
    <ConfirmModal
      isOpen={true}
      title={t("events.details.host_cancel_modal.title")}
      message={t("events.details.host_cancel_modal.message")}
      onConfirm={(e) => {
        setIsHostCancelConfirmOpen(false);
        void handleDeleteEvent(e);
      }}
      onCancel={() => setIsHostCancelConfirmOpen(false)}
      confirmText={t("events.details.host_cancel_modal.confirm_button")}
      cancelText={t("events.details.host_cancel_modal.back_button")}
      confirmButtonClassName="bg-red-600 text-white"
      cancelButtonClassName="bg-white text-blue-600"
    />
  );

  const hostClaimRewardItems = getHostRewardItems(ev?.host_claim_reward, t);
  const hostClaimMessageNode = (
    <div className="text-center text-white">
      <p className="text-[1.05rem] font-medium leading-relaxed">
        {t("events.details.host_claim_modal.line_intro")}
      </p>
      <p className="mt-4 text-[1.05rem] font-medium leading-relaxed">
        {t("events.details.host_claim_modal.line_flow")}
      </p>
      <p className="mt-4 text-[1.05rem] font-medium leading-relaxed">
        {t("events.details.host_claim_modal.line_activation")}
      </p>
      {hostClaimRewardItems.length > 0 && (
        <>
          <p className="mt-4 text-[1.05rem] font-semibold leading-relaxed">
            {t("events.details.host_claim_modal.reward_label")}
          </p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {hostClaimRewardItems.map((item) => (
              item.kind === "coins" ? (
                <div key={item.key} className="flex items-center gap-1.5 text-white">
                  <span className="text-xl font-extrabold leading-none">
                    {item.amountLabel}
                  </span>
                  <img src={item.icon} alt={item.label} className={item.iconClassName} />
                </div>
              ) : (
                <div key={item.key} className="flex items-center gap-1.5 text-white">
                  <img src={item.icon} alt={item.label} className={item.iconClassName} />
                  <span className="-ml-1 text-xl font-extrabold leading-none">
                    × {item.amountLabel}
                  </span>
                </div>
              )
            ))}
          </div>
        </>
      )}
      <p className="mt-4 text-[1.05rem] font-medium leading-relaxed">
        {t("events.details.host_claim_modal.line_responsibility")}
      </p>
    </div>
  );

  const hostClaimConfirmModal = isHostClaimConfirmOpen && (
    <ConfirmModal
      isOpen={true}
      title={t("events.details.host_claim_modal.title")}
      messageNode={hostClaimMessageNode}
      onConfirm={async () => {
        const ok = await checkProfilePhoto("join");
        if (!ok) return;
        setIsHostClaimConfirmOpen(false);
        const needsHostCreateRulesIntro = await checkHostCreateRulesIntro();
        if (needsHostCreateRulesIntro) {
          navigate("/events/hosting-rules", {
            state: {
              mode: "claim",
              returnTo: `/events/${ev.id}`,
              hostClaimEventId: ev?.id,
            },
          });
          return;
        }
        await doJoinEvent();
      }}
      onCancel={() => setIsHostClaimConfirmOpen(false)}
      confirmText={t("events.details.host_claim_modal.confirm")}
      cancelText={t("events.details.host_claim_modal.cancel")}
      confirmButtonClassName="bg-blue-600 text-white"
      cancelButtonClassName="bg-white text-blue-600"
    />
  );

  const ticketSuccessTitleKey =
    paymentSuccessKind === "deposit"
      ? "host_paid_deposit_success_modal.title"
      : paymentSuccessKind === "remaining"
        ? "host_paid_remaining_success_modal.title"
      : isHostPaidCardEvent
        ? "host_paid_ticket_success_modal.title"
        : "ticket_success_modal.title";

  const ticketSuccessBodyPrefix =
    paymentSuccessKind === "deposit"
      ? "host_paid_deposit_success_modal.body"
      : paymentSuccessKind === "remaining"
        ? "host_paid_remaining_success_modal.body"
      : isHostPaidCardEvent
        ? "host_paid_ticket_success_modal.body"
        : "ticket_success_modal.body";

  const depositSuccessDeadlineLabel = (() => {
    if (paymentSuccessKind !== "deposit") return "";
    const lang = String(i18n?.language || "en").toLowerCase();
    const dueAtRaw = participantTicketing?.balance_due_at || null;
    if (dueAtRaw) {
      try {
        return new Date(dueAtRaw).toLocaleString(undefined, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (_err) {
        // ignore
      }
    }
    const daysBefore = Number(participantTicketing?.balance_due_days_before || 0);
    if (!Number.isFinite(daysBefore) || daysBefore <= 0) return "";
    if (lang.startsWith("ru")) return `за ${daysBefore} дн. до события`;
    if (lang.startsWith("sk")) return `${daysBefore} dni pred podujatim`;
    if (lang.startsWith("cs")) return `${daysBefore} dní před událostí`;
    return `${daysBefore} days before the event`;
  })();

  const depositSuccessRefundSummary = (() => {
    if (paymentSuccessKind !== "deposit") return "";
    const lang = String(i18n?.language || "en").toLowerCase();
    const policy = String(participantTicketing?.cancellation_policy || "").toLowerCase();
    const dueAtRaw = participantTicketing?.balance_due_at || null;
    const eventAtRaw = ev?.date_time || null;
    let refundPercent = 0;
    if (policy === "strict") {
      refundPercent = 0;
    } else if (dueAtRaw && eventAtRaw) {
      try {
        const secondsUntilEvent = Math.max(
          0,
          Math.floor((new Date(eventAtRaw).getTime() - new Date(dueAtRaw).getTime()) / 1000)
        );
        if (policy === "moderate") {
          refundPercent = secondsUntilEvent >= 72 * 60 * 60 ? 100 : 0;
        } else if (policy === "flexible") {
          if (secondsUntilEvent >= 48 * 60 * 60) refundPercent = 100;
          else if (secondsUntilEvent >= 12 * 60 * 60) refundPercent = 50;
          else refundPercent = 0;
        }
      } catch (_err) {
        // ignore
      }
    }
    if (lang.startsWith("ru")) {
      if (refundPercent >= 100) return "По текущей refund policy депозит будет возвращён полностью.";
      if (refundPercent > 0) return `По текущей refund policy депозит будет возвращён частично (${refundPercent}%).`;
      return "По текущей refund policy депозит не подлежит возврату.";
    }
    if (lang.startsWith("sk")) {
      if (refundPercent >= 100) return "Podla aktualnej refund policy bude zaloha vratena v plnej vyske.";
      if (refundPercent > 0) return `Podla aktualnej refund policy bude zaloha vratena ciastocne (${refundPercent} %).`;
      return "Podla aktualnej refund policy je zaloha nevratna.";
    }
    if (lang.startsWith("cs")) {
      if (refundPercent >= 100) return "Podle aktuálních podmínek vrácení peněz bude záloha vrácena v plné výši.";
      if (refundPercent > 0) return `Podle aktuálních podmínek vrácení peněz bude záloha vrácena částečně (${refundPercent} %).`;
      return "Podle aktuálních podmínek vrácení peněz je záloha nevratná.";
    }
    if (refundPercent >= 100) return "Under the current refund policy, the deposit would be refunded in full.";
    if (refundPercent > 0) return `Under the current refund policy, the deposit would be partially refunded (${refundPercent}%).`;
    return "Under the current refund policy, the deposit would be non-refundable.";
  })();

  const ticketSuccessMessageNode = (
    <div className="space-y-3 text-left text-[0.98rem] leading-7 text-white/82">
      {paymentSuccessKind === "deposit" ? (
        <>
          <p>{t(`${ticketSuccessBodyPrefix}.platform_flow`)}</p>
          <p>
            {String(i18n?.language || "en").toLowerCase().startsWith("ru")
              ? `Оставшаяся сумма должна быть оплачена до ${depositSuccessDeadlineLabel || "дедлайна оплаты"}.`
              : String(i18n?.language || "en").toLowerCase().startsWith("cs")
                ? `Zbývající částka musí být uhrazena do ${depositSuccessDeadlineLabel || "termínu platby"}.`
              : String(i18n?.language || "en").toLowerCase().startsWith("sk")
                ? `Zostavajuca suma musi byt uhradena do ${depositSuccessDeadlineLabel || "platobneho terminu"}.`
                : `The remaining balance must be paid by ${depositSuccessDeadlineLabel || "the payment deadline"}.`}
          </p>
          <p>
            {String(i18n?.language || "en").toLowerCase().startsWith("ru")
              ? `Если доплата не поступит вовремя, резервация может быть снята. ${depositSuccessRefundSummary}`
              : String(i18n?.language || "en").toLowerCase().startsWith("cs")
                ? `Pokud doplatek nepřijde včas, rezervace může být zrušena. ${depositSuccessRefundSummary}`
              : String(i18n?.language || "en").toLowerCase().startsWith("sk")
                ? `Ak doplatok nepride vcas, rezervacia moze byt uvolnena. ${depositSuccessRefundSummary}`
                : `If the balance is not paid in time, the reservation may be released. ${depositSuccessRefundSummary}`}
          </p>
        </>
      ) : (
        <>
          <p>{t(`${ticketSuccessBodyPrefix}.platform_flow`)}</p>
          <p>{t(`${ticketSuccessBodyPrefix}.mailbox`)}</p>
          <p>{t(`${ticketSuccessBodyPrefix}.support`)}</p>
        </>
      )}
    </div>
  );

  const ticketSuccessModal = isTicketSuccessOpen && (
    <ConfirmModal
      isOpen={true}
      title={t(ticketSuccessTitleKey)}
      messageNode={ticketSuccessMessageNode}
      onConfirm={() => setIsTicketSuccessOpen(false)}
      onCancel={() => setIsTicketSuccessOpen(false)}
      confirmText={t("ticket_success_modal.button")}
      confirmButtonClassName="bg-blue-600 text-white"
      hideSecondaryButton={true}
      hideCloseButton={true}
    />
  );

  const ticketCancellationOutcomeTitleKey = !ticketCancellationOutcome
    ? null
    : ticketCancellationOutcome.resolved
      ? ticketCancellationOutcome.refundSubmitted
        ? "events.details.ticket_panel.cancellation_modal.confirmation.resolved_refund_title"
        : "events.details.ticket_panel.cancellation_modal.confirmation.resolved_no_refund_title"
      : "events.details.ticket_panel.cancellation_modal.confirmation.review_title";

  const ticketCancellationOutcomeMessageNode = !ticketCancellationOutcome ? null : (
    <div className="space-y-3 text-left text-[0.98rem] leading-7 text-white/82">
      <p>
        {ticketCancellationOutcome.resolved
          ? t("events.details.ticket_panel.cancellation_modal.confirmation.resolved_intro", {
              event: ticketCancellationOutcome.eventName || "",
            })
          : t("events.details.ticket_panel.cancellation_modal.confirmation.review_intro", {
              event: ticketCancellationOutcome.eventName || "",
            })}
      </p>
      {ticketCancellationOutcome.resolved ? (
        ticketCancellationOutcome.refundSubmitted ? (
          <>
            <p>
              {t("events.details.ticket_panel.cancellation_modal.confirmation.refund_amount", {
                amount: ticketCancellationOutcome.estimatedRefundAmount || "0.00",
              })}
            </p>
            <p>{t("events.details.ticket_panel.cancellation_modal.confirmation.refund_timing")}</p>
          </>
        ) : (
          <p>{t("events.details.ticket_panel.cancellation_modal.confirmation.no_refund_body")}</p>
        )
      ) : (
        <p>{ticketCancellationOutcome.message || t("events.details.ticket_panel.cancellation_modal.success_message")}</p>
      )}
      <p>{t("events.details.ticket_panel.cancellation_modal.confirmation.support")}</p>
    </div>
  );

  const ticketCancellationOutcomeModal = ticketCancellationOutcome && (
    <ConfirmModal
      isOpen={true}
      title={t(ticketCancellationOutcomeTitleKey)}
      messageNode={ticketCancellationOutcomeMessageNode}
      onConfirm={() => setTicketCancellationOutcome(null)}
      onCancel={() => setTicketCancellationOutcome(null)}
      confirmText={t("events.details.ticket_panel.modal_close")}
      confirmButtonClassName="bg-blue-600 text-white"
      hideSecondaryButton={true}
    />
  );

  const joinChatTitleKey = isConcept
    ? 'events.details.join_chat_concept.title'
    : 'events.details.join_chat.title';
  const joinChatMessageKey = isConcept
    ? 'events.details.join_chat_concept.message'
    : 'events.details.join_chat.message';
  const joinChatConfirmKey = isConcept
    ? 'events.details.join_chat_concept.confirm'
    : 'events.details.join_chat.confirm';
  const showCalendarButton = canAddToCalendar;

  const calendarPickerModal = isCalendarPickerOpen && (
    <ConfirmModal
      isOpen={true}
      title={t("events.details.calendar_picker.title")}
      message={t("events.details.calendar_picker.message")}
      onConfirm={handleAddToGoogleCalendar}
      onSecondary={handleAddToAppleCalendar}
      onCancel={() => setIsCalendarPickerOpen(false)}
      confirmText={t("events.details.calendar_picker.google")}
      cancelText={t("events.details.calendar_picker.apple")}
      confirmButtonClassName="bg-blue-600 text-white"
      cancelButtonClassName="bg-white text-blue-600"
    />
  );

  const firstJoinRulesCards = [
    {
      icon: "📈",
      imageSrc: "/assets/events/UI/join_intro_6.png",
      title: t("events.details.first_join_rules.cards.1.title"),
      bodyNode: (
        <>
          <a
            href="/events/participant-rating"
            className="inline font-semibold text-blue-200 underline underline-offset-2"
            onClick={openParticipantRatingFromRulesModal}
          >
            {t("events.details.first_join_rules.cards.1.rating_link_label")}
          </a>{" "}
          {t("events.details.first_join_rules.cards.1.body_after_rating_link")}
        </>
      ),
      summary: t("events.details.first_join_rules.cards.1.summary"),
      summaryIcon: "⚠️",
    },
    {
      icon: "🛡️",
      imageSrc: "/assets/events/UI/join_intro_3.png",
      title: t("events.details.first_join_rules.cards.2.title"),
      body: t("events.details.first_join_rules.cards.2.body"),
      summary: t("events.details.first_join_rules.cards.2.summary"),
      summaryIcon: "⚠️",
    },
    {
      icon: "🧭",
      imageSrc: "/assets/events/UI/join_intro_4.png",
      title: t("events.details.first_join_rules.cards.3.title"),
      body: t("events.details.first_join_rules.cards.3.body"),
    },
  ];

  const resumePendingJoinAfterIntro = async () => {
    return await performJoin();
  };

  const firstJoinIntroModal = isJoinRulesIntroOpen && (
    <FirstJoinRulesCarouselModal
      isOpen
      cards={firstJoinRulesCards}
      initialIndex={joinRulesInitialIndex}
      onClose={() => {
        setIsJoinRulesIntroOpen(false);
      }}
      onConfirm={async () => {
        try {
          await acknowledgeJoinRulesIntro();
          const joined = await resumePendingJoinAfterIntro();
          if (joined !== false) {
            setIsJoinRulesIntroOpen(false);
          }
        } catch (err) {
          const msg = err?.response?.data?.error || t("events.details.join_failed");
          window.dispatchEvent(
            new CustomEvent("global-error", { detail: { message: msg, status: 400 } })
          );
        }
      }}
      isSubmitting={false}
    />
  );

  const joinConfirmModal = isJoinConfirmOpen && (
    <ConfirmModal
      isOpen={true}
      title={<span style={{ display: 'block', marginTop: '-8px' }}>{t(joinChatTitleKey)}</span>}
      message={t(joinChatMessageKey)}
      onConfirm={() => {
        openEventChat();
      }}
      onCancel={() => {
        setIsJoinConfirmOpen(false);
      }}
      onSecondary={showCalendarButton
        ? handleOpenCalendarPicker
        : undefined}
      confirmText={t(joinChatConfirmKey)}
      cancelText={showCalendarButton ? t('events.details.join_chat.add_to_calendar') : undefined}
      confirmButtonClassName="bg-blue-600 text-white"
      cancelButtonClassName="bg-white text-blue-600"
    />
  );

  const joinLimitConfirmModal = joinLimitModal && (
    <ConfirmModal
      isOpen={true}
      title={joinLimitModal.title}
      messageNode={joinLimitModal.messageNode}
      onConfirm={getJoinLimitActionHandler(joinLimitModal?.primaryAction || "rating")}
      onSecondary={getJoinLimitActionHandler(joinLimitModal?.secondaryAction)}
      onCancel={() => setJoinLimitModal(null)}
      confirmText={joinLimitModal?.primaryText || joinLimitCopy.openRating}
      cancelText={
        joinLimitResetSubmitting && joinLimitModal?.primaryAction === "reset"
          ? joinLimitCopy.resettingRating
          : (
            joinLimitResetSubmitting && joinLimitModal?.secondaryAction === "reset"
          ? joinLimitCopy.resettingRating
          : (
            joinLimitModal?.secondaryAction
              ? (joinLimitModal?.secondaryText || joinLimitCopy.openRating)
              : undefined
          )
          )
      }
      confirmButtonClassName="bg-blue-600 text-white"
      cancelButtonClassName="bg-white text-blue-600"
    />
  );

  const boostModal = (
    <BoostEventModal
      event={ev}
      isOpen={isBoostModalOpen}
      onClose={() => setIsBoostModalOpen(false)}
      onBoosted={handleBoosted}
    />
  );

  const reportModal = (
    <ReportForm
      isOpen={isReportOpen}
      onClose={() => setIsReportOpen(false)}
      onSubmit={handleReportSubmit}
      mode="event"
    />
  );

  const photoModal = (
    <PhotoRequestModal
      visible={isPhotoModalOpen}
      onClose={() => {
        setIsPhotoModalOpen(false);
        setPhotoGateTarget(null);
      }}
      onUploaded={() => {
        // Photo was successfully uploaded – mark and continue the pending flow
        setHasCustomAvatar(true);
        setIsPhotoModalOpen(false);
        const nextAction = photoGateTarget;
        setPhotoGateTarget(null);
        if (nextAction === "join") {
          void continueJoinFlow();
          return;
        }
        if (nextAction === "waitlist_join") {
          void doJoinWaitlist();
          return;
        }
        if (nextAction === "intent") {
          setIsIntentOpen(true);
        }
      }}
    />
  );

  const genderModal = (
    <EventGenderConfirmationModal
      open={Boolean(genderPrompt)}
      event={ev}
      loading={genderSubmitting}
      error={genderError}
      onClose={() => {
        if (genderSubmitting) return;
        setGenderPrompt(null);
        setGenderError("");
      }}
      onSelect={async (confirmedGender) => {
        if (!genderPrompt || genderSubmitting) return;
        const pendingPrompt = genderPrompt;
        setGenderSubmitting(true);
        setGenderError("");
        try {
          if (typeof pendingPrompt.resume === "function") {
            setGenderPrompt(null);
            await pendingPrompt.resume(confirmedGender);
          } else {
            await doJoinEvent(pendingPrompt.targetEvent || ev, { confirmedGender });
          }
        } catch (error) {
          setGenderError(
            error?.response?.data?.error || t("events.details.join_failed")
          );
        } finally {
          setGenderSubmitting(false);
        }
      }}
    />
  );

  if (isDesktop) {
    const desktopOutput = (
      <>
        {sheetContent}
        {confirmModal}
        {intentModal}
        {checkoutCapacityReservedModal}
        {waitlistModal}
        {coHostModal}
        {hostTransferModal}
        {hostIntentionModal}
        {removeParticipantModal}
        {hostAnalyticsModal}
        {conceptDatePollModal}
        {reviewRequestsModal}
        {leaveConfirmModal}
        {deleteConfirmModal}
        {hostConfirmModal}
        {hostCancelConfirmModal}
        {hostClaimConfirmModal}
        {ticketSuccessModal}
        {ticketCancellationOutcomeModal}
        {calendarPickerModal}
        {firstJoinIntroModal}
        {joinConfirmModal}
        {audienceModal}
        {joinLimitConfirmModal}
        {boostModal}
        {reportModal}
        {photoModal}
        {genderModal}
      </>
    );

    if (asPage) return desktopOutput;

    const modalRoot = document.getElementById("modal-root");
    return modalRoot
      ? createPortal(desktopOutput, modalRoot)
      : desktopOutput;
  }

  return (
    <>
      {sheetContent}
      {confirmModal}
      {intentModal}
      {checkoutCapacityReservedModal}
      {waitlistModal}
      {coHostModal}
      {hostTransferModal}
      {hostIntentionModal}
      {removeParticipantModal}
      {hostAnalyticsModal}
      {conceptDatePollModal}
      {reviewRequestsModal}
      {leaveConfirmModal}
      {deleteConfirmModal}
      {hostConfirmModal}
      {hostCancelConfirmModal}
      {hostClaimConfirmModal}
      {ticketSuccessModal}
      {ticketCancellationOutcomeModal}
      {calendarPickerModal}
      {firstJoinIntroModal}
      {joinConfirmModal}
      {audienceModal}
      {joinLimitConfirmModal}
      {boostModal}
      {reportModal}
      {photoModal}
      {genderModal}
    </>
  );
};

export default EventDetailsFullscreen;
